import Phaser from 'phaser';
import {
  GameSim,
  SCYTHE_END_X,
  SCYTHE_START_X,
  type LevelDef,
  type TransitionDef,
  type GameState,
} from '../../core/game';
import { BALANCE, TICK } from '../../config/balance';
import { spawnLeafWhirl } from '../leafWhirl';
import {
  LEVELS,
  LAST_LEVEL_ID,
  WIN_GATE_LEVEL_IDS,
  hasBoss,
  bossTypeOf,
  BOSS_ENEMY_TYPES,
  chapterOf,
  musicKeyFor,
  expRewardFor,
} from '../../config/levels';
import { findLevel } from '../../config/testArena';
import { DIRT, PLOT_IDLE_ALPHA } from '../theme';
import { ENEMY_NAMES, TOWER_NAMES } from '../../config/displayNames';
import { EnemyView, ENEMY_ALPHA, enemyDisplaySize } from '../EnemyView';
import { TowerView } from '../TowerView';
import {
  effectsToModifiers,
  activeEffects,
  canApply,
  towerEffectIcons,
  type EffectRecord,
} from '../../config/effects';
import {
  isHubLevelId,
  DIAMOND_LEVEL_ID,
  DEBRIS_LEVEL_ID,
  MOONSTONE_LEVEL_ID,
  SHROOM_VILLAGE_HOUSES,
  MUSHY_STATUE,
  SHROOM_GRAVES,
  GOLDEN_STATUES,
  GLOW_CLUSTERS,
  TREE_HOUSES,
  SHROOMBELLION_VINES,
  SHROOMBELLION_LEVEL_ID,
} from '../../config/hubLevels';
import { playShroometronCutscene } from '../shroometronCutscene';
import { levelPickerFor } from '../hubShared';
import {
  PHANTOM_DECOR,
  TEMPLE_GHOST_DECOR,
  RED_PHANTOM_DECOR,
  EVIL_ORB_DECOR,
  DARK_SHARD_LEVEL_ID,
  EVIL_ORB_LEVEL_ID,
  HEAVENS_PEAK_LEVEL_ID,
  LIGHT_SHARD_LEVEL_ID,
  darkCoinRewardFor,
  lightCoinRewardFor,
  HEAVENS_PEAK_RING,
  isOriginLevelId,
  originChapterOf,
} from '../../config/originEvent';
import { PEACEFUL_COIN_PENALTY, peacefulLevel } from '../../config/hub';
import { ProjectileView } from '../ProjectileView';
import { InputController } from '../inputController';
import { menuTowerTexture, towersGrantedOnWin } from '../../config/unlocks';
import { ArenaChallenge, SHREK_CHALLENGE } from '../../core/arenaChallenge';
import type { ArenaWaveEntry, EnemyType, TowerType } from '../../core/types';
import type { ChipState } from '../../core/chip';
import { LocalScoreService } from '../../services/scoreService';
import { setLevelSkipHandler } from '../../account/levelSkip';
import { getAudioService, type AudioService } from '../audio/AudioService';
import { getMusicService } from '../audio/MusicService';
import { ProgressService } from '../../services/progressService';
import { RankService } from '../../services/rankService';
import { StatsService } from '../../services/statsService';
import type { AchievementId } from '../../config/achievements';
import { enqueueAchievementToast, sweepAndToastAchievements } from '../../ui/achievementToast';
import { saveStorage } from '../saveStorage';
import { starsFor, REQUIRED_STARS_TO_ADVANCE } from '../../core/scoring';
import { DARKCOIN_SPIN_FRAMES, LIGHTCOIN_SPIN_FRAMES, spinCoin } from '../coin';
import { installDebugReturn } from '../debugMode';
import { recordStat } from '../quests';
import { playVarietyOrbCutscene } from '../varietyOrbCutscene';
import { playPrinceNachoCutscene } from '../princeNachoCutscene';
import { LIGHT_ORB_LOOK, playEvilOrbCutscene, playOrbCutscene } from '../evilOrbCutscene';
import { chestRandom } from '../chestOpening';
import { FUNGI_ITEMS, fungiItemDrops, type FungiItemKind } from '../../config/fungiItems';
import { CHEST_ART, CHEST_NAMES, rollChestDrop, type ChestKind } from '../../config/originChests';
import { openVarietyOrb } from '../varietyOrbOpening';
import { RARITY_COLORS } from '../../config/restaurant';
import { animationsDisabled } from '../../config/gameSettings';

/** Trail polyline width in pixels. */
const TRAIL_WIDTH = 28;
/**
 * Leafstorm Hambi's leaves only fly to enemies this close to where the tornado
 * landed — his widest range (225) plus slack — so a whirl never streaks
 * across the map to an enemy the sim did not hit.
 */
const LEAF_REACH = 260;

/** Chiply's Dad's chips draw this big — medium, easy to press. */
const CHIP_SIZE = 88;
/** Prince Nacho's shield disc — a touch bigger than a press target. */
const SHIELD_SIZE = 104;
/** The second form's health bar, wide enough to read a six-figure number under. */
// 820, not 900: centred on 640 it must end before the tower shop panel at
// x = 1070, or the bar runs on behind the loadout.
const BOSS_BAR_W = 820;
/**
 * Where Through Space and Time's ice sculptures stand (see
 * `drawIceSculptures`). Pinned by hubLevels.test.ts against the level's own
 * path and plots, so the route and the scenery can never drift apart.
 */
const ICE_SCULPTURE_SPOTS: readonly [number, number][] = [
  [110, 180],
  [360, 90],
  [60, 420],
  [390, 555],
  [200, 680],
  [820, 680],
];

/** How long a level-start notice stays up before it dismisses itself (ms). */
const NOTICE_MS = 8000;

/**
 * Maximum real-time seconds that can be compressed into one Phaser update.
 * Prevents a "spiral of death" after a long tab stall.
 */
const MAX_STEP_WINDOW = 5;

/**
 * Visible playfield bounds — left/right edges of the map area (the tower panel
 * starts past X1) and the underside of the HUD bar. Used to keep transient
 * overlays such as the crit bubble inside the part of the canvas a player can
 * actually see.
 */
const PLAYFIELD_X0 = 16;
const PLAYFIELD_X1 = 1056;
const PLAYFIELD_Y0 = 56;

/** How far a crit/headshot bubble floats up before it is gone. */
const HIT_LABEL_RISE = 34;
/** Explosion palettes: the BBQ Bomber's orange, Demonic Hambi's dark purple. */
const BOMB_BLAST = { flash: 0xffd27f, ring: 0xff7a1a };
const DEMONIC_BLAST = { flash: 0xb07cff, ring: 0x4a1680 };

/**
 * GameScene
 *
 * Owns the GameSim and drives it at a fixed 60 Hz timestep decoupled from the
 * render frame rate. Reconciles EnemyView / TowerView sprite pools against the
 * live simulation state on every frame.
 *
 * Public fields (read by HudScene and the input controller via scene.get()):
 *   sim   — the live GameSim instance
 *   level — the active LevelDef
 *
 * Scene data: { levelId: number } (passed from LevelSelectScene via scene.start)
 */

/**
 * Everything the result screen shows, computed and banked by `handleGameOver`
 * before the screen is drawn.
 *
 * It is passed as one object because the Variety Orb cutscene can sit between
 * the two: the values must survive ten seconds of theatre unchanged, and the
 * screen must report what was actually persisted rather than re-deriving it.
 */
interface ResultOverlayArgs {
  isDemo: boolean;
  won: boolean;
  score: number;
  stars: number;
  isLastLevel: boolean;
  /** This win claimed the Diamond (The Hub) — show its USE / STORE modal. */
  diamondClaimed?: boolean;
  /** The Moon Stone was claimed on this win (the finale's first clear). */
  moonstoneClaimed?: boolean;
  /** Space Debris was paid for this win (Rocket Rage, Normal track). */
  debrisClaimed?: boolean;
  advanced: boolean;
  coinsEarned: number;
  coinsTotal: number;
  grantedNow: TowerType[];
  /** This win claimed the Variety Orb — show the claim notification. */
  orbClaimed: boolean;
  /** Dark Hambi Coins paid by this win (an Origin level) and the new balance. */
  darkCoinsEarned?: number;
  darkCoinsTotal?: number;
  /** A Dark Shard was paid for this win (The Dark Forest, every win). */
  darkShardClaimed?: boolean;
  /** Light Hambi Coins paid by this win (Above the Clouds) and the new balance. */
  lightCoinsEarned?: number;
  lightCoinsTotal?: number;
  /** A Light Shard was paid for this win (The Sky Islands, every win). */
  lightShardClaimed?: boolean;
  /** The Fungi items THIS win dropped onto the Storage shelf (spec 2026-10-24). */
  fungiItemsWon?: FungiItemKind[];
  /** This win put the Evil Orb on the shelf (its quest's first win). */
  evilOrbClaimed?: boolean;
  /** This win put the Token of Shroom on the shelf (The Shroombellion's first win). */
  tokenClaimed?: boolean;
  /** True when THIS win claimed the Light Orb (Heaven's Peak, spec 2026-10-13). */
  lightOrbClaimed?: boolean;
  /** The chest THIS win dropped onto the Storage shelf, if the 25% came up. */
  chestWon?: ChestKind;
}

/**
 * Walking bosses that get the big health bar at the top of the screen (the
 * anchored ones always do): King Shroometron, the Fungi Forest's finale.
 */
const BIG_BAR_BOSSES: readonly EnemyType[] = ['kingshroometron'];

/** Where a falling sky shroom starts and lands (its whole fuse is the fall). */
const SKY_SHROOM_TOP = 70;
const SKY_SHROOM_BOTTOM = 690;

/** How often GameScene re-reads The Hub's effect records from storage. */
const EFFECTS_REFRESH_MS = 500;

export class GameScene extends Phaser.Scene {
  /** Live simulation — public so HudScene / input controller can read state. */
  public sim!: GameSim;
  /** Active level definition — public so HudScene can read path / plot info. */
  public level!: LevelDef;

  /**
   * Interaction state (shared with HudScene and InputController — Task 27).
   *
   * `pendingTowerType` — set by HUD shop when a tower button is tapped (enters
   * placement mode); Task 27 reads it on a plot tap, places the tower, then
   * clears it.
   *
   * `selectedTowerId` — set by Task 27 when the player taps a placed tower;
   * HudScene's selected-tower panel reads it to show Upgrade / Sell controls.
   */
  public pendingTowerType: TowerType | null = null;
  public selectedTowerId: number | null = null;

  /**
   * Plot sprites (Task 27) — one sprite per `level.plots` entry, in the same
   * order.  InputController reads these to apply placement-mode highlights.
   */
  public plotSprites: Phaser.GameObjects.Image[] = [];

  /**
   * Game-speed multiplier (1 = normal, 2 = fast-forward). Toggled by the HUD
   * fast-forward button. Scales how much real time feeds the fixed-step
   * accumulator, so the sim still ticks in deterministic TICK steps.
   */
  public timeScale = 1;

  /**
   * Pause flag, toggled by the HUD pause button. While true the fixed-step
   * loop is skipped entirely so the sim is frozen, but the scene keeps
   * rendering the current frame (and tower selection stays live).
   */
  public paused = false;

  /** Fixed-step time accumulator (seconds). */
  private acc = 0;
  /** Sprite pools keyed by entity id. */
  private enemyViews = new Map<number, EnemyView>();
  private towerViews = new Map<number, TowerView>();
  /**
   * The Hub's timed effects (potions, the Diamond), re-read from storage
   * every EFFECTS_REFRESH_MS and folded into the sim's modifier table on the
   * wall clock — so a potion used in a menu is live here and a Diamond
   * expiring mid-level drops the shield exactly on time.
   */
  private effects: EffectRecord[] = [];
  private effectsReadAt = 0;
  private shieldOn = false;
  private projectileViews = new Map<number, ProjectileView>();
  /**
   * King Ember's fireballs in flight, keyed by sim id. `last` is the most
   * recently drawn position, so the impact burst can be spawned where the ball
   * actually was on the frame it disappeared from the sim.
   */
  /** Thrown-thing fireballs (leaves, nugget pieces) drawn as sprites, by sim id. */
  private thrownViews = new Map<number, Phaser.GameObjects.Image>();
  /** Press targets on the field (chips, whistles), by sim id (see core/chip.ts). */
  private chipViews = new Map<
    number,
    { img: Phaser.GameObjects.Image; label: Phaser.GameObjects.Text }
  >();
  /**
   * The red pulse over the playfield while one of The Lifeguard's whistles is
   * waiting. Never interactive, so it can never eat the tap meant for the
   * whistle under it.
   */
  private whistleFlash: Phaser.GameObjects.Rectangle | null = null;
  /** Enemy type of the most recent leak — a Chiply loss gets its own notice. */
  private lastLeak: EnemyType | null = null;
  /** Tears down the level-start notice early (the run ended under it). */
  private dismissNotice: (() => void) | null = null;
  private fireballViews = new Map<
    number,
    { gfx: Phaser.GameObjects.Graphics; last: { x: number; y: number } }
  >();
  /**
   * The Laser Nacho's beams, by sim id: one Graphics each, redrawn every frame
   * as a straight line from the shooter to the tower, fading out over the
   * shot's `travel`. The sim treats a beam exactly like a thrown thing, so the
   * damage still lands on the tick the fade ends.
   */
  private beamViews = new Map<number, Phaser.GameObjects.Graphics>();
  /**
   * Fireball ids already seen, so a thrower's swing plays once per shot rather
   * than every frame the shot is in the air. Pruned with the views.
   */
  private seenFireballIds = new Set<number>();
  /** Prince Nacho's shields on the playfield, by shield id. */
  private shieldViews = new Map<
    number,
    {
      img: Phaser.GameObjects.Image;
      ring: Phaser.GameObjects.Graphics;
      label: Phaser.GameObjects.Text;
    }
  >();
  /** The second form's health bar, while he is on the field. */
  private bossBar: {
    bg: Phaser.GameObjects.Rectangle;
    fill: Phaser.GameObjects.Rectangle;
    label: Phaser.GameObjects.Text;
  } | null = null;
  /**
   * The background image, kept so Rocket Rage's transition can swap its
   * texture. Every other level never touches it.
   */
  private bgImage: Phaser.GameObjects.Image | null = null;
  /** The trail Graphics, kept so the transition can redraw it in a new style. */
  private trailGfx: Phaser.GameObjects.Graphics | null = null;
  /** The rocket standing on Rocket Rage's pad, kept so it can leave. */
  private rocketSprite: Phaser.GameObjects.Image | null = null;
  /** Rock chunks drawn under the plots after the transition. */
  private plotBases: Phaser.GameObjects.Image[] = [];
  /**
   * Cursor into `LevelDef.transitions` — the next one still to play. Each
   * fires once: a wave started early (concurrently) must not re-trigger one.
   */
  private nextTransition = 0;
  /** Wave number seen on the previous frame — how the transition is detected. */
  private lastWaveSeen = 0;
  /**
   * The trail style in force RIGHT NOW. Starts as the level's own and is
   * replaced by `transition.trackStyle` once the cutscene has run, which is
   * why the drawing code reads this rather than `level.trackStyle`.
   */
  private trackStyle: LevelDef['trackStyle'];
  /**
   * Tower views mid-fade after leaving the sim. Held only so a scene restart
   * can tear them down; they remove themselves when the tween completes.
   */
  private fadingTowerViews = new Set<TowerView>();
  /** Guards the game-over overlay so it is only shown once. */
  private gameOverShown = false;
  /** Enemy hover tooltip (bubble bg + text), created hidden in create(). */
  private hoverBg!: Phaser.GameObjects.Rectangle;
  private hoverText!: Phaser.GameObjects.Text;
  /** Input controller — handles placement, selection, and range ring. */
  private inputController!: InputController;
  /** Shared audio service — plays SFX for drained sim events. */
  private audio!: AudioService;

  /**
   * The Moon's Peaceful toggle was on when this level was started (scene
   * data). Carried through RETRY and into the HUD; the level the sim runs is
   * `peacefulLevel(config)` and the win records to the Peaceful track.
   */
  private _peaceful = false;
  /** Holds the levelId received from scene data via init(). */
  private _pendingLevelId: number = LEVELS[0].id;

  /**
   * Demo/review outcome (set via scene data by DemoScene). When non-null the run
   * is forced to the matching end overlay immediately, NOTHING is persisted
   * (no score, stars, unlocks, or coins), and the result buttons return to the
   * demo screen. Null during normal play.
   */
  private demoOutcome: 'win' | 'loss' | null = null;

  /**
   * The Test Arena's Shrekbook challenge (see core/arenaChallenge.ts). Armed by
   * HudScene when it sends a wave, fed the sim's kill events below, and judged
   * on waveCleared. Inert outside the arena: no campaign wave ever arms it.
   */
  private readonly arenaChallenge = new ArenaChallenge();

  /**
   * Achievements/rank over the saved storage (sandboxed under `?debug`, so a
   * demo run's awards never reach the real save). `award` is idempotent, so
   * hooks below call it freely; the toast only shows on a NEW award.
   */
  private readonly rankSvc = new RankService(saveStorage());

  /** Lifetime counters (losses, waves) feeding Waver / I Trusted you!. */
  private readonly statsSvc = new StatsService(saveStorage());

  /**
   * Phantom special cinematics waiting to play (same-tick specials queue).
   * While one runs the game is fully paused and the frame visually frozen
   * (`syncViews` skipped), auto-unpausing when the queue drains.
   */
  private cinematicQueue: { from: { x: number; y: number }; to: { x: number; y: number } }[] = [];
  private cinematicActive = false;
  private pausedBeforeCinematic = false;
  /**
   * Settings → Disable Animation (spec 2026-10-27 §3), read once per level:
   * tower cutscenes are skipped and their attack's own visuals play at once.
   * The sim is never touched — the damage was always timed in sim seconds
   * from the cast, and the cutscenes only paused it.
   */
  private animationsOff = false;
  /** SHROOM FRENZY casts waiting for their cutscene (the K–O's queue, its own). */
  private frenzyQueue: { tier: number; pos: { x: number; y: number }; delay: number }[] = [];
  /**
   * King Hambi K–Os waiting for their ceremony (spec 2026-09-22). Several kings
   * can cast on one tick; they play one after another on the same pause
   * machinery as the Phantom's special.
   */
  private kingQueue: { tier: 1 | 2 | 3 | 4; pos: { x: number; y: number }; delay: number }[] = [];
  /**
   * Rains whose ceremony has finished but must not fall yet: with two kings
   * queued, the second ceremony pauses the sim, so the first king's shards
   * are held until the LAST ceremony ends and the game is running again.
   */
  private heldRains: number[] = [];
  /**
   * Ultra Hammy END THEMs waiting for their moment (spec 2026-09-29), on the
   * same pause machinery as the K–O. The boost, the minions and the 5000 are
   * all sim-side; this is theatre.
   */
  private endThemQueue: { pos: { x: number; y: number }; delay: number }[] = [];
  /** END THEM's minions, drawn from `state.minions` (keyed by minion id). */
  private minionViews = new Map<number, Phaser.GameObjects.Image>();
  /**
   * Ends the Variety Orb cutscene early. Held so a scene shutdown mid-cutscene
   * (RETRY, EPISODES, a restart) tears its tweens and timers down instead of
   * leaving them to fire against destroyed objects.
   */
  private cancelOrbCutscene: (() => void) | null = null;
  /** The orb in the dead tree (`decor: 'evilorb'`) — the cutscene lifts it out. */
  private evilOrbDecor: Phaser.GameObjects.Image | null = null;
  /** The orb in the sakura ring (`decor: 'heavenspeak'`) — the Light Orb's cutscene lifts it. */
  private heavenOrbDecor: Phaser.GameObjects.Image | null = null;
  /** The Reaper's swings already drawn: owner id → scene-clock ms of the last. */
  private lastScytheAt = new Map<number, number>();
  /** Swing hits in flight: the tower's spot and how far through its travel. */
  private swingHits = new Map<number, { x: number; y: number; done: number }>();

  /** Award an achievement (idempotent) and toast it when newly earned. */
  private awardAch(id: AchievementId): void {
    try {
      const a = this.rankSvc.award(id);
      if (a) enqueueAchievementToast({ title: a.name, body: a.how, iconKey: a.icon });
    } catch (err) {
      console.warn('[GameScene] achievement award failed', err);
    }
  }

  /**
   * Whether the CHALLENGE COMPLETE panel has already been shown in this scene.
   * Reset in create(), so re-entering the arena re-arms it — but a single visit
   * can never stack two celebrations.
   */
  private challengeOverlayShown = false;

  constructor() {
    super({ key: 'GameScene' });
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Capture scene-start data before create() runs so TypeScript can type it
   * correctly without fighting the base-class signature.
   */
  init(data: { levelId?: number; demo?: 'win' | 'loss'; peaceful?: boolean }): void {
    this._pendingLevelId = data?.levelId ?? LEVELS[0].id;
    this.demoOutcome = data?.demo ?? null;
    this._peaceful = data?.peaceful === true;
  }

  create(): void {
    this.challengeOverlayShown = false;
    // ── Resolve level ────────────────────────────────────────────────────────
    // findLevel, not LEVELS.find — the Test Arena lives outside the campaign
    // array on purpose (see config/testArena.ts).
    const configLevel = findLevel(this._pendingLevelId) ?? LEVELS[0];
    // Peaceful applies to hub levels only; anything else ignores the flag.
    if (!isHubLevelId(configLevel.id)) this._peaceful = false;
    this.level = this._peaceful ? peacefulLevel(configLevel) : configLevel;

    // ── Build the sim ────────────────────────────────────────────────────────
    // Date.now() at the render-layer edge is intentionally impure — we are NOT
    // in the deterministic sim core here.
    // Purchased Evolutions raise the tier cap to 4 — in every mode (episodes,
    // arena, and the debug/demo screens alike).
    const evolved = new ProgressService(
      saveStorage(),
      LAST_LEVEL_ID,
      WIN_GATE_LEVEL_IDS,
    ).evolvedTowers();
    this.sim = new GameSim(this.level, BALANCE, Date.now(), evolved);

    // ── Reset per-run state ──────────────────────────────────────────────────
    this.acc = 0;
    this.enemyViews = new Map();
    this.shieldViews = new Map();
    this.seenFireballIds = new Set();
    // The Reaper's swings: a RETRY must not KO towers at last run's spots.
    this.lastScytheAt = new Map();
    this.swingHits = new Map();
    this.evilOrbDecor = null;
    this.heavenOrbDecor = null;
    this.bossBar = null;
    this.towerViews = new Map();
    this.projectileViews = new Map();
    this.fireballViews = new Map();
    this.thrownViews = new Map();
    this.chipViews = new Map();
    this.whistleFlash = null;
    this.lastLeak = null;
    this.dismissNotice = null;
    this.fadingTowerViews = new Set();
    this.gameOverShown = false;
    this.pendingTowerType = null;
    this.selectedTowerId = null;
    this.plotSprites = [];
    this.cinematicQueue = [];
    this.kingQueue = [];
    this.heldRains = [];
    this.endThemQueue = [];
    this.frenzyQueue = [];
    this.minionViews = new Map();
    this.cinematicActive = false;
    this.animationsOff = animationsDisabled();
    this.beamViews = new Map();
    this.plotBases = [];
    this.rocketSprite = null;
    this.nextTransition = 0;
    this.lastWaveSeen = 0;
    this.trackStyle = this.level.trackStyle;
    this.pausedBeforeCinematic = false;
    // scoreSubmitted is reset implicitly by gameOverShown (see handleGameOver guard)

    // ── Draw: background ─────────────────────────────────────────────────────
    // Per-level background; fall back to bg-forest until the themed art exists.
    const bgKey =
      this.level.background && this.textures.exists(this.level.background)
        ? this.level.background
        : 'bg-forest';
    this.bgImage = this.add.image(640, 360, bgKey).setDepth(0);

    // ── Draw: trail (polyline through path waypoints) ────────────────────────
    // Default is the dirt trail; The Mineshafts asks for a railway instead.
    const trail = this.add.graphics().setDepth(1);
    this.trailGfx = trail;
    const waypoints = this.level.path;
    this.drawTrailStyle(trail, waypoints);

    // A level with more than one enemy base draws each lead-in in the same
    // style as the trunk, so the two lanes read as one track that forks.
    for (const lead of this.level.entrances ?? []) {
      this.drawTrailStyle(trail, lead);
    }

    // ── Draw: crystal decor (Amethyst Core) ──────────────────────────────────
    // Piles of amethyst crystals lining both sides of the track and ringing
    // every plot. Sits above the trail, below the plot markers.
    if (this.level.decor === 'crystals') {
      this.drawCrystalDecor(this.add.graphics().setDepth(1.4));
    }
    if (this.level.decor === 'hambibar') this.drawHambiBarDecor();
    if (this.level.decor === 'exhibition') this.drawExhibitionDecor();
    if (this.level.decor === 'station') this.drawStationDecor();
    if (this.level.decor === 'beach') this.drawBeachDecor();
    if (this.level.decor === 'phantoms') this.drawPhantomDecor();
    if (this.level.decor === 'templeghosts') this.drawTempleGhostDecor();
    if (this.level.decor === 'redphantoms') this.drawRedPhantomDecor();
    if (this.level.decor === 'evilorb') this.drawEvilOrbDecor();
    if (this.level.decor === 'heavenspeak') this.drawHeavensPeakDecor();
    if (this.level.decor === 'shroomvillage') this.drawShroomVillageDecor();
    if (this.level.decor === 'mushystatue') this.drawMushyStatue();
    if (this.level.decor === 'shroomgraves') this.drawShroomGraves();
    if (this.level.decor === 'goldstatues') this.drawGoldenStatues();
    if (this.level.decor === 'glowclusters') this.drawGlowClusters();
    if (this.level.decor === 'treehouses') this.drawTreeHouses();
    if (this.level.decor === 'nachyo') this.drawNachyoDecor();
    if (this.level.decor === 'rocketpad') this.drawRocketPadDecor();
    if (this.level.decor === 'spaceplatform') this.drawSpacePlatformDecor();
    // Letting go of a shield is a SCENE event, not a disc event: dragging the
    // pointer off the disc mid-hold has to count as a release, and a
    // `pointerout` on the sprite alone would miss a release that happened
    // anywhere else. Releasing with no shield held is a harmless no-op.
    this.input.on('pointerup', () => {
      for (const id of this.shieldViews.keys()) {
        this.sim.dispatch({ type: 'holdShield', shieldId: id, down: false });
      }
    });
    if (this.level.decor === 'icesculptures') this.drawIceSculptures();

    // ── Draw: start / end markers ────────────────────────────────────────────
    // Make it obvious where the horde enters (green) and where it breaks through
    // to the picnic (red). Sit above the trail (1.5), below the plots (2).
    // One green marker per enemy BASE — two of them on Nach'yo Business, so
    // both mouths are as obvious as the single one is everywhere else.
    const mouths = this.level.entrances?.length
      ? this.level.entrances.map((lead) => lead[0])
      : [waypoints[0]];
    const end = waypoints[waypoints.length - 1];
    const markers = this.add.graphics().setDepth(1.5);
    for (const start of mouths) {
      markers.fillStyle(0x2ecc40, 1); // entry — green
      markers.fillCircle(start.x, start.y, 18);
      markers.lineStyle(3, 0xffffff, 0.9);
      markers.strokeCircle(start.x, start.y, 18);
    }
    markers.fillStyle(0xe74c3c, 1); // goal — red
    markers.fillCircle(end.x, end.y, 18);
    markers.lineStyle(3, 0xffffff, 0.9);
    markers.strokeCircle(end.x, end.y, 18);

    // ── Draw: plot markers ───────────────────────────────────────────────────
    // Sprites are stored in plotSprites (parallel to level.plots) so the
    // InputController can apply placement-mode highlights each frame.
    this.plotSprites = this.level.plots.map((plot) =>
      this.add.image(plot.pos.x, plot.pos.y, 'plot').setDepth(2).setAlpha(PLOT_IDLE_ALPHA),
    );

    // ── Enemy hover tooltip (hidden until pointer is over an enemy) ───────────
    // Depth 40: above gameplay, below the game-over overlay (50).
    this.hoverBg = this.add
      .rectangle(0, 0, 150, 78, 0x10141c, 0.92)
      .setStrokeStyle(2, 0xffffff)
      .setOrigin(0.5, 1)
      .setDepth(40)
      .setVisible(false);
    this.hoverText = this.add
      .text(0, 0, '', { fontSize: '13px', color: '#ffffff', align: 'left', lineSpacing: 3 })
      .setOrigin(0.5, 1)
      .setDepth(41)
      .setVisible(false);

    // ── Input controller (Task 27) ───────────────────────────────────────────
    this.inputController = new InputController(this);
    this.audio = getAudioService(this);

    // ── Background music ─────────────────────────────────────────────────────
    // Each episode plays the loop mapped to its chapter/episode (see
    // musicKeyFor); neighbouring episodes share a track. Stops on scene
    // shutdown, which covers win, loss, and leaving the level early.
    const music = getMusicService(this);
    music.playTrack(musicKeyFor(this.level));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      music.stop();
      // A cutscene still running when the scene goes away must take its tweens
      // and timers with it, or they fire against destroyed game objects.
      this.cancelOrbCutscene?.();
      this.cancelOrbCutscene = null;
    });

    // ── /skip (spec 2026-10-04) ──────────────────────────────────────────────
    // A dev's /skip wins the level being played: the run is forced to 'won' and
    // the next update() draws Level Complete through handleGameOver, so the
    // coins, stars, grants and prizes are exactly the ones a real win pays. The
    // arenas (sandboxes — "winning" one means nothing) and the debug page's
    // demo runs are left alone, as is a run that has already ended.
    const offSkip = setLevelSkipHandler(() => {
      if (this.level.sandbox === true || this.demoOutcome !== null) return false;
      if (this.gameOverShown || this.sim.isOver) return false;
      // A perfect 3★ win: the score is this level's flawless clear, not the
      // 750 that ending at wave 0 with full lives used to bank.
      this.sim.forcePerfectWin();
      return true;
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, offSkip);

    // ── Debug bridge for E2E tests (Task 30) ────────────────────────────────
    // Only compiled-in when VITE_E2E is set; absent in normal production builds.
    if (import.meta.env.VITE_E2E) {
      (window as unknown as { __hambi?: unknown }).__hambi = { sim: this.sim, scene: this };
    }

    // ── Level-start notices (The Moon) ───────────────────────────────────────
    // The level's own `notice` (Wizard Hambi on Chiply and the chips, Wise
    // Hambi on the whistles). In-canvas, so fullscreen shows them.
    if (!this.demoOutcome) {
      if (this._peaceful) {
        // Peaceful replaces the level's own warning: the things it warns of
        // cannot hurt you here, and the penalty is what the player must know.
        this.showLevelNotice(
          'tower-wise-1',
          'Wise Hambi',
          `Peaceful: nothing here can hurt you, and this level pays ${PEACEFUL_COIN_PENALTY} fewer coins.`,
        );
      } else if (this.level.notice) {
        const n = this.level.notice;
        this.showLevelNotice(n.art, n.sender, n.text);
      }
    }

    // ── Demo/review mode ─────────────────────────────────────────────────────
    // Force the requested end state so the next update() surfaces the overlay
    // without playing. handleGameOver() persists nothing while demoOutcome is set.
    if (this.demoOutcome) {
      this.sim.forceEnd(this.demoOutcome === 'win');
    }

    installDebugReturn(this); // Esc → DemoScene when launched via ?debug
  }

  // ---------------------------------------------------------------------------
  // Game loop
  // ---------------------------------------------------------------------------

  override update(_time: number, deltaMs: number): void {
    if (this.sim.isOver) {
      this.handleGameOver();
      return;
    }

    if (this.paused) {
      // Frozen: bank no real time and run no sim ticks, but keep rendering the
      // current frame below so the map, HUD and tower selection stay live.
      this.acc = 0;
    } else {
      this.refreshEffects();
      // Fixed-step accumulator — decouples sim from render FPS
      this.acc += (deltaMs / 1000) * this.timeScale;
      const MAX = MAX_STEP_WINDOW;
      let steps = 0;
      while (this.acc >= TICK && steps < Math.ceil(MAX / TICK)) {
        this.sim.step(TICK);
        this.acc -= TICK;
        steps++;
      }

      for (const e of this.sim.drainEvents()) {
        this.audio.playEvent(e);
        // A shot that rolled above normal AND connected. The sim ships the
        // impact point with the event because the projectile's view has already
        // been advanced past it by the time the shot leaves the list.
        // Horror Hambi's ball poisons on EVERY hit, so it skips the instant
        // POISON! bubble; "poisoned!" rises over the enemy one second later
        // (spec 2026-10-12).
        // The Mushy's dodge: "DODGE" rises from over his head.
        if (e.type === 'enemyDodged') {
          this.spawnDodgeLabel(e.pos.x, e.pos.y - enemyDisplaySize(e.enemyType) / 2);
        }
        // The Fungi items (spec 2026-10-24): the Shroom's tower dodge, the
        // Snowshroom's mark, and the shroomnitized enemy's strike.
        if (e.type === 'towerDodged') {
          this.spawnDodgeLabel(e.pos.x, e.pos.y - 44, 'DODGE', 'tower-dodge-label', '#7a1a12');
        }
        if (e.type === 'enemyShroomnitized') {
          this.spawnDodgeLabel(
            e.pos.x,
            e.pos.y - enemyDisplaySize(e.enemyType) / 2,
            'SHROOMNITIZED!',
            'shroomnitize-label',
            '#1a4a6a',
          );
        }
        if (e.type === 'enemyStruckEnemy') this.flyStrike(e.from, e.to);
        // A Hyno-Fungi's hypnosis: a blue swirl over the tower and what it did.
        if (e.type === 'towerHypnotized') this.showHypnosis(e.pos, e.outcome);
        // King Shroometron calls his Hyno-Fungi at a quarter health.
        if (e.type === 'bossSummoned') this.cameras.main.shake(300, 0.007);
        if (e.type === 'critHit' && e.towerType === 'horror') this.queuePoisonedLabel(e.pos);
        else if (e.type === 'critHit') this.spawnHitLabel(e.pos.x, e.pos.y, e.kind, e.towerType);
        // The Ham Reaper's scythe crosses the screen (the sim cuts as it goes).
        if (e.type === 'scytheCast') this.playHamScythe(e.duration);
        // The Light Shop (spec 2026-10-13): Healer Hambi's sparkles over every
        // tower he healed, and the Banking Burger's "+$" where the enemy fell.
        if (e.type === 'towerHealed') this.showTowerHeals(e.healed);
        if (e.type === 'bankerPaid') this.showBankerCash(e.pos, e.cash);
        // Poisoned luck: Bow Hambi loosed a poison arrow (campaign and Hub
        // runs — the arena's free build would make it a free click).
        // Bow Hambi's only: Horror Hambi poisons on every ball, so his would be
        // a free award, not luck (spec 2026-10-12).
        if (
          e.type === 'projectileFired' &&
          e.poison &&
          e.towerType === 'bow' &&
          this.level.sandbox !== true
        ) {
          this.awardAch('poisonedluck');
        }
        // A Chiply leak ends the run; the loss screen names him.
        if (e.type === 'enemyLeaked') this.lastLeak = e.enemyType;
        // "when this happens the screen shakes" — Prince Nacho's second form
        // drops in front of the track, and the board feels it.
        if (e.type === 'bossTransformed') this.cameras.main.shake(600, 0.014);
        // A missed shield costs two lives: the same red flash a missed chip
        // gets, so the two "you didn't press it" failures read alike.
        if (e.type === 'shieldMissed') this.flashKo();
        // An unpressed target: the lives are already gone sim-side. A chip
        // flashes K.O (and pays the achievement); a whistle is a STRIKE.
        if (e.type === 'chipExpired') {
          if (e.kind === 'skyshroom') {
            // A sky shroom landed: the tower it hit already shows the damage.
            this.cameras.main.shake(180, 0.005);
          } else if (e.kind === 'whistle') {
            this.flashStrike();
          } else {
            this.flashKo();
            if (this.level.sandbox !== true) this.awardAch('ko');
          }
        }
        // The arena's Shrekbook challenge rides the kill/clear events the sim
        // already emits — the sim itself knows nothing about tower unlocks.
        if (e.type === 'enemyKilled') this.arenaChallenge.onKill(e.enemyType);
        if (e.type === 'waveCleared' && this.arenaChallenge.completedOnClear()) {
          this.awardChallengeTower();
        }
        // Achievements. Tester is the ONLY one earnable in the sandbox arena
        // (the Shrekbook persistence precedent); the rest are campaign-only —
        // the arena's freeBuild would make First Kill / Max Tier free clicks.
        if (e.type === 'enemyKilled' && this.level.sandbox !== true) this.awardAch('firstkill');
        // Quests: lifetime counters. Campaign and Moon runs bank kills, boss
        // kills and sells; the arenas (sandbox) bank only their cleared waves.
        // Demo runs bank nothing.
        const banks = this.level.sandbox !== true && this.demoOutcome === null;
        if (e.type === 'enemyKilled' && banks) {
          recordStat('kills');
          if ((BOSS_ENEMY_TYPES as readonly string[]).includes(e.enemyType)) {
            recordStat('bossKills');
          }
        }
        if (e.type === 'waveCleared' && this.level.sandbox === true && this.demoOutcome === null) {
          recordStat('arenaWaves');
        }
        if (e.type === 'towerSold' && banks) recordStat('sells');
        if (e.type === 'waveCleared' && this.level.sandbox === true) this.awardAch('tester');
        if (e.type === 'towerUpgraded' && e.tier === 3 && this.level.sandbox !== true) {
          this.awardAch('maxtier');
        }
        // Waver: every campaign wave cleared counts toward the lifetime total
        // (arena waves and demo runs bank nothing, like every other counter).
        if (e.type === 'waveCleared' && this.level.sandbox !== true && this.demoOutcome === null) {
          try {
            if (this.statsSvc.incWaves() >= 100) this.awardAch('waver');
          } catch (err) {
            console.warn('[GameScene] wave counter failed', err);
          }
        }
        // Phantom special: queue the paused power-up cinematic. The 1500 has
        // already landed sim-side (hitscan) — this is pure theatre, which is
        // why gating it costs no damage. TIER 3 ONLY: a tier-1/2 Phantom fires
        // the same special and kills just as hard, it simply doesn't stop the
        // game to show off. Freezing the board every 30 seconds from the very
        // first Phantom was the problem; at tier 3 it reads as the reward for
        // maxing the tower.
        if (
          e.type === 'specialFired' &&
          e.towerType === 'phantom' &&
          e.tier === 3 &&
          !this.animationsOff
        ) {
          this.cinematicQueue.push({ from: { ...e.from }, to: { ...e.to } });
        }
        // King Hambi's K–O: the ceremony, and a Golden Stone for every cast.
        // The stone is banked NOW, before the theatre, so a player who leaves
        // mid-ceremony keeps it. Campaign and Normal-track Moon runs only: the
        // arena's free build would make it a free farm, and Peaceful pays no
        // prizes.
        if (e.type === 'shardRainCast') {
          if (this.animationsOff) this.rainShards(e.delay);
          else this.kingQueue.push({ tier: e.tier, pos: { ...e.pos }, delay: e.delay });
          if (banks && !this._peaceful) this.awardGoldstone();
        }
        if (e.type === 'shardRainLanded') this.landShardRain();
        // Ultra Hammy's END THEM: its own moment, and NO Golden Stone — the
        // stone is King Hambi's alone.
        if (e.type === 'endThemCast') {
          if (this.animationsOff) this.rainDynamite(e.delay);
          else this.endThemQueue.push({ pos: { ...e.pos }, delay: e.delay });
        }
        if (e.type === 'endThemLanded') this.landEndThem(e.at);
        // The Golden Shroom's SHROOM FRENZY: its own 5 s scene, then the rain.
        if (e.type === 'shroomFrenzyCast') {
          if (this.animationsOff) this.rainGoldenShrooms(e.delay);
          else this.frenzyQueue.push({ tier: e.tier, pos: { ...e.pos }, delay: e.delay });
        }
        if (e.type === 'shroomFrenzyLanded') this.landShardRain();
      }
      if (this.cinematicQueue.length > 0 && !this.cinematicActive) {
        this.playPhantomCinematic();
      }
      if (this.kingQueue.length > 0 && !this.cinematicActive) {
        this.playKingCeremony();
      }
      if (this.endThemQueue.length > 0 && !this.cinematicActive) {
        this.playEndThem();
      }
      if (this.frenzyQueue.length > 0 && !this.cinematicActive) {
        this.playShroomFrenzy();
      }
    }

    // Rocket Rage: the frame its wave begins, the rocket leaves. Checked here
    // rather than off an event because the wave can be started by the player,
    // by the auto-start, or early (concurrently) — the wave NUMBER moving past
    // the mark catches every one of them, and the cursor makes each once.
    const list = this.level.transitions ?? [];
    const tr = list[this.nextTransition];
    if (tr && !this.cinematicActive) {
      // `waveIndex` is 0-based; a transition's `wave` is the 1-based number.
      const wave = this.sim.state.waveIndex + 1;
      if (this.lastWaveSeen < tr.wave && wave >= tr.wave) {
        this.nextTransition++;
        this.playRocketCutscene(tr);
      }
      this.lastWaveSeen = Math.max(this.lastWaveSeen, wave);
    }

    if (this.cinematicActive) {
      // Frozen frame: the target's death (already decided sim-side) must not
      // show until the red bullet lands, so views stay exactly as they were.
      return;
    }

    this.syncViews();
    this.inputController.update();
    this.updateHoverBubble();
  }

  /**
   * The rocket leaves, and the level lands somewhere else (Rocket Rage, wave
   * 10). Three seconds, on the same pause machinery the Phantom's special
   * uses: the sim stops stepping, tweens keep running, and the whole board is
   * hidden behind an opaque cover for the duration — the brief is explicit
   * that the track and the plots must not be visible while it plays.
   *
   * Nothing about the SIMULATION changes. The path, the plots and every tower
   * standing on them are exactly where they were; only the ground under them
   * is swapped, which is why the towers appear to have made the trip.
   */
  private playRocketCutscene(tr: TransitionDef): void {
    const D = 46; // above the Phantom's cinematic layer, below the game-over overlay
    this.cinematicActive = true;
    this.pausedBeforeCinematic = this.paused;
    this.paused = true;
    const objs: Phaser.GameObjects.GameObject[] = [];

    // An OPAQUE cover: the board must be gone, not dimmed. Interactive, so a
    // stray tap during the launch cannot reach a plot underneath.
    objs.push(this.add.rectangle(640, 360, 1280, 720, 0x05030c, 1).setDepth(D).setInteractive());
    // A starfield to launch through, so the cover is a scene and not a hole.
    const stars = this.add.graphics().setDepth(D + 1);
    for (let i = 0; i < 90; i++) {
      const x = (i * 977) % 1280;
      const y = (i * 613) % 720;
      stars.fillStyle(0xffffff, 0.25 + ((i * 37) % 60) / 100);
      stars.fillCircle(x, y, 1 + ((i * 17) % 3) * 0.6);
    }
    objs.push(stars);

    // The rocket flies UP and out of frame over the three seconds, trailing
    // flame. It is the level's own sprite where there is one, so the thing
    // that leaves is the thing that was standing on the pad.
    const hasArt = this.textures.exists('decor-rocket');
    const rocket = hasArt
      ? this.add
          .image(640, 420, 'decor-rocket')
          .setDisplaySize(190, 290)
          .setDepth(D + 3)
      : null;
    if (rocket) objs.push(rocket);
    const flame = this.add.graphics().setDepth(D + 2);
    objs.push(flame);

    const CUTSCENE_MS = 3000;
    const started = this.time.now;
    const burn = this.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        const t = Math.min(1, (this.time.now - started) / CUTSCENE_MS);
        // Ease in: it sits, shakes, then goes.
        const lift = t * t * 1250;
        const shake = t < 0.25 ? Math.sin(this.time.now / 18) * 3 : 0;
        if (rocket) rocket.setPosition(640 + shake, 420 - lift);
        // +128, not half the 290px display height: decor-rocket has transparent
        // padding under the engine bell, so +145 leaves a visible gap.
        const ty = (rocket?.y ?? 420) + 128;
        flame.clear();
        if (t > 0.15) {
          for (let i = 0; i < 5; i++) {
            const len = (60 + i * 34) * (0.6 + Math.random() * 0.6);
            flame.fillStyle(i < 2 ? 0xfff0b0 : i < 4 ? 0xffa227 : 0xd4431f, 0.75 - i * 0.12);
            flame.fillEllipse(640 + shake, ty + len / 2, 46 - i * 6, len);
          }
        }
      },
    });

    // The caption, so the beat reads as a story point rather than a glitch.
    const cap = this.add
      .text(640, 620, tr.caption ?? 'The rocket is leaving...', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '30px',
        fontStyle: 'bold',
        color: '#ffd23f',
      })
      .setOrigin(0.5)
      .setDepth(D + 4)
      .setName('rocket-cutscene-caption');
    objs.push(cap);

    this.time.delayedCall(CUTSCENE_MS, () => {
      burn.remove();
      this.applyTransition(tr);
      cap.setText(tr.captionEnd ?? '...and so are you.');
      // A short beat on the new sky before the cover lifts, so the change of
      // scene is noticed rather than merely happening.
      this.tweens.add({
        targets: objs,
        alpha: 0,
        duration: 550,
        onComplete: () => {
          objs.forEach((o) => o.destroy());
          this.cinematicActive = false;
          this.paused = this.pausedBeforeCinematic;
        },
      });
    });
  }

  /**
   * Swap the ground under a running level: new background, new trail style,
   * new decor, and a rock chunk under every plot. The path and the plots
   * themselves are untouched — see `LevelDef.transitions`.
   */
  private applyTransition(tr: TransitionDef): void {
    if (this.bgImage && this.textures.exists(tr.background)) {
      this.bgImage.setTexture(tr.background);
    }
    if (tr.trackStyle !== undefined) this.trackStyle = tr.trackStyle;
    if (this.trailGfx) {
      this.trailGfx.clear();
      this.drawTrailStyle(this.trailGfx, this.level.path);
      for (const lead of this.level.entrances ?? []) this.drawTrailStyle(this.trailGfx, lead);
    }
    // The pad is gone with the rocket.
    this.rocketSprite?.destroy();
    this.rocketSprite = null;
    // The previous leg's bases go with it: a level with TWO transitions would
    // otherwise stack ice on top of rock, since this pass only ever appended.
    this.plotBases.forEach((b) => b.destroy());
    this.plotBases = [];
    // Floating rock chunks: one under each plot, below the plot marker (2) and
    // above the trail (1), so a tower still reads as standing ON its plot.
    if (tr.plotBase && this.textures.exists(tr.plotBase)) {
      for (const plot of this.level.plots) {
        this.plotBases.push(
          this.add
            .image(plot.pos.x, plot.pos.y + 6, tr.plotBase)
            .setDisplaySize(104, 104)
            .setDepth(1.8),
        );
      }
    }
    // Scenery that belongs to the place you have just arrived at.
    if (tr.decor === 'icesculptures') this.drawIceSculptures();
  }

  /** Bank one Golden Stone (a K–O was cast) and say so. */
  private awardGoldstone(): void {
    try {
      new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS).awardGoldstone();
      recordStat('obtains');
    } catch (err) {
      console.warn('[GameScene] Golden Stone award failed', err);
    }
  }

  /**
   * King Hambi's K–O (spec 2026-09-22). The game stops; he swells up to fill
   * the middle of the screen with a giant K on his left and O on his right;
   * then he floats off the top, the game unpauses, and a rain of yellow shards
   * falls from the top of the screen. The 3000 lands SIM-SIDE when the rain
   * does (`shardRainLanded`, a fixed delay after the cast) — this is theatre
   * timed to meet it.
   */
  private playKingCeremony(): void {
    const next = this.kingQueue.shift();
    if (!next) return;
    this.cinematicActive = true;
    this.pausedBeforeCinematic = this.paused;
    this.paused = true;
    const D = 45; // the Phantom's cinematic layer
    const objs: Phaser.GameObjects.GameObject[] = [];
    const dim = this.add
      .rectangle(640, 360, 1280, 720, 0x0b0700, 0.6)
      .setDepth(D)
      .setInteractive()
      .setName('king-ceremony');
    objs.push(dim);

    const key = `tower-kinghambi-${Math.min(next.tier, 3)}`;
    const king = this.add
      .image(next.pos.x, next.pos.y, this.textures.exists(key) ? key : 'tower-kinghambi-1')
      .setDisplaySize(72, 72)
      .setDepth(D + 2)
      .setName('king-ceremony-king');
    objs.push(king);
    const big = 330 / Math.max(1, king.width);

    const letter = (x: number, ch: string) => {
      const t = this.add
        .text(x, 360, ch, {
          fontFamily: 'Georgia, serif',
          fontSize: '260px',
          fontStyle: 'bold',
          color: '#ffd21f',
          stroke: '#5a3a00',
          strokeThickness: 14,
        })
        .setOrigin(0.5)
        .setDepth(D + 1)
        .setScale(0)
        .setName(`king-ceremony-${ch}`);
      objs.push(t);
      return t;
    };
    const k = letter(640 - 330, 'K');
    const o = letter(640 + 330, 'O');

    const done = () => {
      objs.forEach((obj) => obj.destroy());
      this.paused = this.pausedBeforeCinematic;
      this.cinematicActive = false;
      // The shards fall with the game running again, landing as the sim does
      // — so while another king is still queued, they wait for him.
      this.heldRains.push(next.delay);
      if (this.kingQueue.length > 0) {
        this.playKingCeremony();
        return;
      }
      for (const d of this.heldRains) this.rainShards(d);
      this.heldRains = [];
    };

    // 1. He swells into the middle of the screen (~0.6s).
    this.tweens.add({
      targets: king,
      x: 640,
      y: 360,
      scale: big,
      duration: 600,
      ease: 'Back.easeOut',
      onComplete: () => {
        // 2. K and O slam in either side of him and hold (~1.1s).
        this.tweens.add({ targets: [k, o], scale: 1, duration: 320, ease: 'Back.easeOut' });
        this.cameras.main.shake(260, 0.006);
        this.time.delayedCall(1100, () => {
          // 3. He floats up and out of the top; the letters fade (~0.8s).
          this.tweens.add({ targets: [k, o, dim], alpha: 0, duration: 600 });
          this.tweens.add({
            targets: king,
            y: -260,
            duration: 800,
            ease: 'Cubic.easeIn',
            onComplete: done,
          });
        });
      },
    });
  }

  /**
   * The falling shards: timed to reach the ground in `delay` seconds of SIM
   * time, so at 2x speed they fall twice as fast and still land with the
   * damage. The landing itself is `landShardRain`.
   */
  private rainShards(delay: number): void {
    const ms = (delay * 1000) / Math.max(0.25, this.timeScale);
    for (let i = 0; i < 46; i++) {
      const x = 20 + ((i * 331) % 1240);
      const y = 70 + ((i * 197) % 600);
      const size = 30 + ((i * 17) % 22);
      const shard = this.add
        .image(x + 60, -40 - ((i * 53) % 200), 'proj-kinghambi')
        .setDisplaySize(size, size)
        .setRotation(2.4)
        .setDepth(9)
        .setName('king-shard');
      this.tweens.add({
        targets: shard,
        x,
        y,
        duration: ms,
        ease: 'Quad.easeIn',
        onComplete: () => {
          this.tweens.add({
            targets: shard,
            alpha: 0,
            scale: shard.scale * 1.6,
            duration: 260,
            onComplete: () => shard.destroy(),
          });
        },
      });
    }
  }

  /**
   * Ultra Hammy's END THEM (spec 2026-09-29), on the K–O's pause machinery:
   * the game stops, he swells into the middle of the screen with END THEM
   * slammed in beneath him, holds, and shrinks back onto his plot. Then the
   * dynamite falls with the game running again, timed to meet the sim's
   * `endThemLanded` — the 5000, the boost and the minions are all sim-side.
   */
  private playEndThem(): void {
    const next = this.endThemQueue.shift();
    if (!next) return;
    this.cinematicActive = true;
    this.pausedBeforeCinematic = this.paused;
    this.paused = true;
    const D = 45; // the Phantom's cinematic layer
    const objs: Phaser.GameObjects.GameObject[] = [];
    const dim = this.add
      .rectangle(640, 360, 1280, 720, 0x12001f, 0.6)
      .setDepth(D)
      .setInteractive()
      .setName('endthem-ceremony');
    objs.push(dim);

    const hammy = this.add
      .image(next.pos.x, next.pos.y, 'tower-ultrahammy-4')
      .setDisplaySize(72, 72)
      .setDepth(D + 2)
      .setName('endthem-hammy');
    objs.push(hammy);
    const small = hammy.scale;
    const big = 300 / Math.max(1, hammy.width);

    const words = this.add
      .text(640, 360 + 200, 'END THEM', {
        fontFamily: 'Georgia, serif',
        fontSize: '96px',
        fontStyle: 'bold',
        color: '#ff2a2a',
        stroke: '#2a0000',
        strokeThickness: 12,
      })
      .setOrigin(0.5)
      .setDepth(D + 1)
      .setScale(0)
      .setName('endthem-text');
    objs.push(words);

    const done = () => {
      objs.forEach((obj) => obj.destroy());
      this.paused = this.pausedBeforeCinematic;
      this.cinematicActive = false;
      this.rainDynamite(next.delay);
    };

    // 1. He swells into the middle of the screen (~0.6s).
    this.tweens.add({
      targets: hammy,
      x: 640,
      y: 330,
      scale: big,
      duration: 600,
      ease: 'Back.easeOut',
      onComplete: () => {
        // 2. END THEM slams in under him and holds (~1.1s).
        this.tweens.add({ targets: words, scale: 1, duration: 320, ease: 'Back.easeOut' });
        this.cameras.main.shake(300, 0.008);
        this.time.delayedCall(1100, () => {
          // 3. He shrinks back onto his plot; the words and the dim fade (~0.6s).
          this.tweens.add({ targets: [words, dim], alpha: 0, duration: 500 });
          this.tweens.add({
            targets: hammy,
            x: next.pos.x,
            y: next.pos.y,
            scale: small,
            duration: 600,
            ease: 'Cubic.easeIn',
            onComplete: done,
          });
        });
      },
    });
  }

  /**
   * The Golden Shroom's SHROOM FRENZY (spec 2026-10-27): five seconds with the
   * game stopped — he falls out of the sky into the middle of the screen, a
   * storm of golden mushrooms comes down, black SHROOM FRENZY text slams in
   * and a stream of shrooms flies across from the right — then the game runs
   * again and the 900 lands with the rain (`shroomFrenzyLanded`, a fixed sim
   * delay after the cast). Skipped by Settings → Disable Animation.
   */
  private playShroomFrenzy(): void {
    const next = this.frenzyQueue.shift();
    if (!next) return;
    this.cinematicActive = true;
    this.pausedBeforeCinematic = this.paused;
    this.paused = true;
    const D = 45;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      return o;
    };
    const timers: Phaser.Time.TimerEvent[] = [];
    add(
      this.add
        .rectangle(640, 360, 1280, 720, 0x1a1200, 0.55)
        .setDepth(D)
        .setInteractive()
        .setName('frenzy-ceremony'),
    );
    const tierKey = `tower-goldenshroom-${Math.min(3, Math.max(1, next.tier))}`;
    const hero = add(
      this.add
        .image(640, -160, this.textures.exists(tierKey) ? tierKey : 'tower-patty-1')
        .setDisplaySize(240, 240)
        .setDepth(D + 2)
        .setName('frenzy-hero'),
    );
    // 1. He falls from the sky into the middle (~0.8s).
    this.tweens.add({ targets: hero, y: 330, duration: 800, ease: 'Bounce.easeOut' });
    this.time.delayedCall(800, () => this.cameras.main.shake(300, 0.008));
    // 2. Thousands of golden mushrooms come down (0.6–3.4s).
    const shroomKey = this.textures.exists('proj-goldenshroom') ? 'proj-goldenshroom' : 'coin';
    timers.push(
      this.time.addEvent({
        delay: 30,
        repeat: 90,
        startAt: 0,
        callback: () => {
          const i = objs.length;
          const s = add(
            this.add
              .image((i * 337) % 1280, -30, shroomKey)
              .setDisplaySize(26 + (i % 4) * 6, 26 + (i % 4) * 6)
              .setDepth(D + 1)
              .setName('frenzy-rain'),
          );
          this.tweens.add({
            targets: s,
            y: 760,
            angle: (i % 2 ? 1 : -1) * 240,
            duration: 900 + (i % 5) * 120,
            ease: 'Quad.easeIn',
          });
        },
      }),
    );
    // 3. SHROOM FRENZY, in black, slams in (1.6s) and holds.
    const words = add(
      this.add
        .text(640, 560, 'Shroom Frenzy', {
          fontFamily: 'Georgia, serif',
          fontSize: '104px',
          fontStyle: 'bold',
          color: '#000000',
          stroke: '#ffd84a',
          strokeThickness: 10,
        })
        .setOrigin(0.5)
        .setDepth(D + 3)
        .setScale(0)
        .setName('frenzy-text'),
    );
    this.time.delayedCall(1600, () =>
      this.tweens.add({ targets: words, scale: 1, duration: 320, ease: 'Back.easeOut' }),
    );
    // 4. Shrooms fly across from the right of the screen to the left (2.4–4.6s).
    this.time.delayedCall(2400, () => {
      for (let i = 0; i < 28; i++) {
        const y = 90 + ((i * 197) % 560);
        const s = add(
          this.add
            .image(1360 + (i % 7) * 70, y, shroomKey)
            .setDisplaySize(44, 44)
            .setDepth(D + 2.5)
            .setName('frenzy-fly'),
        );
        this.tweens.add({
          targets: s,
          x: -120,
          angle: -360,
          duration: 1100 + (i % 4) * 160,
          delay: (i % 6) * 120,
          ease: 'Linear',
        });
      }
    });
    // 5. Five seconds in, the scene clears and the game runs again.
    this.time.delayedCall(4400, () => this.tweens.add({ targets: objs, alpha: 0, duration: 550 }));
    this.time.delayedCall(5000, () => {
      timers.forEach((t) => t.remove());
      objs.forEach((o) => {
        this.tweens.killTweensOf(o);
        o.destroy();
      });
      this.paused = this.pausedBeforeCinematic;
      this.cinematicActive = false;
      this.rainGoldenShrooms(next.delay);
    });
  }

  /**
   * The frenzy's landing rain: golden shrooms falling to meet the sim's
   * `shroomFrenzyLanded` `delay` seconds of SIM time after the cast, like the
   * K–O's shards. The only frenzy visual when animations are off.
   */
  private rainGoldenShrooms(delay: number): void {
    const ms = (delay * 1000) / Math.max(0.25, this.timeScale);
    const key = this.textures.exists('proj-goldenshroom') ? 'proj-goldenshroom' : 'coin';
    for (let i = 0; i < 40; i++) {
      const x = 20 + ((i * 331) % 1240);
      const y = 80 + ((i * 197) % 590);
      const size = 26 + ((i * 17) % 18);
      const s = this.add
        .image(x, -40 - ((i * 53) % 160), key)
        .setDisplaySize(size, size)
        .setDepth(9)
        .setName('frenzy-landing');
      this.tweens.add({
        targets: s,
        y,
        duration: ms,
        ease: 'Quad.easeIn',
        onComplete: () => {
          this.tweens.add({
            targets: s,
            alpha: 0,
            scale: s.scale * 1.6,
            duration: 260,
            onComplete: () => s.destroy(),
          });
        },
      });
    }
  }

  /**
   * END THEM's dynamite shower: sticks fall from the top of the screen and
   * reach the ground in `delay` seconds of SIM time, like the K–O's shards.
   * The explosions go off where the enemies actually were (`landEndThem`).
   */
  private rainDynamite(delay: number): void {
    const ms = (delay * 1000) / Math.max(0.25, this.timeScale);
    for (let i = 0; i < 36; i++) {
      const x = 30 + ((i * 331) % 1220);
      const y = 90 + ((i * 197) % 580);
      const size = 30 + ((i * 17) % 16);
      const stick = this.add
        .image(x - 40, -40 - ((i * 53) % 200), 'proj-ultrahammy')
        .setDisplaySize(size, size)
        .setRotation(-0.6)
        .setDepth(9)
        .setName('endthem-dynamite');
      this.tweens.add({
        targets: stick,
        x,
        y,
        rotation: 1.2,
        duration: ms,
        ease: 'Quad.easeIn',
        onComplete: () => stick.destroy(),
      });
    }
  }

  /** The dynamite hit the ground: a BBQ Bomber explosion on every enemy it hit. */
  private landEndThem(at: readonly { x: number; y: number }[]): void {
    this.cameras.main.shake(360, 0.012);
    for (const p of at) this.spawnExplosion(p.x, p.y, 110);
  }

  /**
   * END THEM's minions: three tier-2 Ultra Hammies at the top of the screen.
   * They pop in when the sim puts them there and fade out when it takes them
   * away. They are not towers, so nothing about them is clickable.
   */
  private syncMinions(minions: readonly { id: number; pos: { x: number; y: number } }[]): void {
    const live = new Set<number>();
    for (const m of minions) {
      live.add(m.id);
      if (this.minionViews.has(m.id)) continue;
      const img = this.add
        .image(m.pos.x, m.pos.y, 'tower-ultrahammy-2')
        .setDepth(3)
        .setName('endthem-minion');
      img.setDisplaySize(64, 64);
      const scale = img.scale;
      // Disable Animation: the clones are simply there, no pop-in.
      if (!this.animationsOff) {
        img.setScale(0);
        this.tweens.add({ targets: img, scale, duration: 300, ease: 'Back.easeOut' });
      }
      this.tweens.add({
        targets: img,
        y: m.pos.y - 6,
        duration: 500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: 300,
      });
      this.minionViews.set(m.id, img);
    }
    for (const [id, img] of this.minionViews) {
      if (live.has(id)) continue;
      this.minionViews.delete(id);
      this.tweens.killTweensOf(img);
      this.tweens.add({
        targets: img,
        alpha: 0,
        scale: 0,
        duration: 300,
        onComplete: () => img.destroy(),
      });
    }
  }

  /**
   * The Shadow Reaper's scythe (spec 2026-10-07): one huge silver crescent
   * sweeps from his side across the whole board and fades — the swing the
   * hits on every tower come from.
   */
  private playScytheSweep(from: { x: number; y: number }): void {
    if (!this.textures.exists('fx-scytheslash')) return;
    const slash = this.add
      .image(from.x - 260, from.y + 40, 'fx-scytheslash')
      .setDisplaySize(380, 380)
      .setAngle(-50)
      .setAlpha(0)
      .setDepth(4.7)
      .setName('reaper-scythe-sweep');
    this.tweens.add({
      targets: slash,
      x: from.x + 260,
      y: from.y + 220,
      angle: 40,
      alpha: { from: 0.95, to: 0 },
      displayWidth: 900,
      displayHeight: 900,
      duration: 520,
      ease: 'Cubic.easeOut',
      onComplete: () => slash.destroy(),
    });
    this.cameras.main.shake(220, 0.006);
  }

  /**
   * The Ham Reaper's scythe (spec 2026-10-12): the huge scythe flies in from
   * the right edge, blade first, and out past the left in the sweep's sim
   * duration. Pure theatre — the sim cuts each enemy as the blade's x reaches
   * it, and this sprite rides the same line.
   */
  private playHamScythe(duration: number): void {
    if (!this.textures.exists('fx-hamscythe')) return;
    const blade = this.add
      .image(SCYTHE_START_X + 220, 380, 'fx-hamscythe')
      .setDisplaySize(560, 280)
      .setDepth(4.7)
      .setAlpha(0.95)
      .setName('hamreaper-scythe');
    this.tweens.add({
      targets: blade,
      x: SCYTHE_END_X - 220,
      angle: { from: 6, to: -6 },
      duration: duration * 1000,
      ease: 'Linear',
      onComplete: () => blade.destroy(),
    });
    this.cameras.main.shake(260, 0.004);
  }

  /**
   * Horror Hambi: "poisoned!" over the enemy his ball hit, one second after the
   * hit. The event carries the impact point, so the enemy is the living one
   * nearest it now, and the label goes wherever that enemy has walked to.
   */
  private queuePoisonedLabel(at: { x: number; y: number }): void {
    let target: (typeof this.sim.state.enemies)[number] | null = null;
    let best = Infinity;
    for (const en of this.sim.state.enemies) {
      if (!en.alive) continue;
      const d = Math.hypot(en.pos.x - at.x, en.pos.y - at.y);
      if (d < best) {
        best = d;
        target = en;
      }
    }
    const id = target?.id;
    this.time.delayedCall(1000, () => {
      const now = this.sim.state.enemies.find((en) => en.id === id && en.alive);
      const pos = now?.pos ?? at;
      this.spawnPoisonedLabel(pos.x, pos.y);
    });
  }

  private spawnPoisonedLabel(x: number, y: number): void {
    const label = this.add
      .text(x, y - 22, 'poisoned!', {
        fontFamily: 'monospace',
        fontSize: '18px',
        fontStyle: 'bold',
        color: '#d11f2e',
        stroke: '#1a0306',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setDepth(5)
      .setName('horror-poisoned');
    const halfW = label.width / 2;
    label.x = Phaser.Math.Clamp(x, PLAYFIELD_X0 + halfW, PLAYFIELD_X1 - halfW);
    label.y = Math.max(label.y, PLAYFIELD_Y0 + label.height / 2 + HIT_LABEL_RISE);
    this.tweens.add({
      targets: label,
      y: label.y - HIT_LABEL_RISE,
      alpha: 0,
      duration: 800,
      ease: 'Quad.easeOut',
      onComplete: () => label.destroy(),
    });
  }

  /** "KO" over a tower the scythe just hit — the brief's words, per tower. */
  private flashSwingKo(x: number, y: number): void {
    const ko = this.add
      .text(x, y - 46, 'KO', {
        fontSize: '30px',
        fontStyle: 'bold',
        color: '#ff3b3b',
        stroke: '#1a0000',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setDepth(8)
      .setScale(0.4)
      .setName('reaper-ko');
    this.tweens.add({ targets: ko, scale: 1, duration: 160, ease: 'Back.easeOut' });
    this.tweens.add({
      targets: ko,
      y: y - 74,
      alpha: 0,
      delay: 650,
      duration: 450,
      onComplete: () => ko.destroy(),
    });
  }

  /** The rain hit the ground: a golden flash and a shake as the 3000 lands. */
  private landShardRain(): void {
    this.cameras.main.shake(320, 0.01);
    const flash = this.add.rectangle(640, 360, 1280, 720, 0xffe066, 0.35).setDepth(9.5);
    this.tweens.add({ targets: flash, alpha: 0, duration: 450, onComplete: () => flash.destroy() });
  }

  /**
   * "King Hambi has joined your towers!" — The Ember and Flame's crown, shown
   * on the Level Complete screen after the cutscene. Built ABOVE the Variety
   * Orb's modal (which the same first win claims), so the player meets him
   * first and the orb is waiting underneath when OK is pressed.
   */
  private showKingHambiNotification(): void {
    const W = 1280;
    const H = 720;
    const D = 130; // above the orb's claim modal (120)
    const band = RARITY_COLORS.Ketchuptastic;
    const bandHex = `#${band.toString(16).padStart(6, '0')}`;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      return o;
    };
    add(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x05030c, 0.85)
        .setDepth(D)
        .setInteractive(),
    );
    add(
      this.add
        .rectangle(W / 2, H / 2, 720, 500, 0x1a1726, 0.98)
        .setStrokeStyle(4, band)
        .setDepth(D + 1),
    );
    add(
      this.add
        // Below y=180 so the achievement toasts that land with this win
        // (Stageness, King Down) never sit on top of it.
        .text(W / 2, H / 2 - 150, 'KETCHUPTASTIC', {
          fontSize: '46px',
          color: bandHex,
          fontStyle: 'bold',
          stroke: '#fff4d6',
          strokeThickness: 6,
        })
        .setOrigin(0.5)
        .setDepth(D + 2)
        .setName('kinghambi-claim-rarity'),
    );
    const king = add(
      this.add
        .image(W / 2, H / 2 - 30, 'tower-kinghambi-1')
        .setDisplaySize(160, 160)
        .setDepth(D + 2)
        .setName('kinghambi-claim-art'),
    );
    const bob = this.tweens.add({
      targets: king,
      y: H / 2 - 42,
      duration: 1400,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
    add(
      this.add
        .text(W / 2, H / 2 + 88, 'You have unlocked King Hambi!', {
          fontSize: '32px',
          color: '#f5c518',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    add(
      this.add
        .text(W / 2, H / 2 + 130, 'Splitting shards, and a K–O every 30 seconds.', {
          fontSize: '19px',
          color: '#9fd8ff',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    const ok = add(
      this.add
        .rectangle(W / 2, H / 2 + 200, 230, 60, 0x1a6b1a)
        .setStrokeStyle(2, 0x4caf50)
        .setInteractive({ useHandCursor: true })
        .setDepth(D + 2)
        .setName('kinghambi-claim-ok'),
    );
    add(
      this.add
        .text(W / 2, H / 2 + 200, 'OK', { fontSize: '24px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    ok.on('pointerup', () => {
      bob.stop();
      objs.forEach((o) => o.destroy());
      objs.length = 0;
    });
  }

  /**
   * Phantom Hambi's ~2s special cinematic (spec: the game pauses, he powers
   * up, the red bullet flies, the explosion lands, then auto-unpause). Runs
   * one queue entry at a time; the pause is held until the queue drains.
   * Tweens keep animating while `this.paused` is true — only sim stepping and
   * view syncing stop.
   */
  private playPhantomCinematic(): void {
    const next = this.cinematicQueue.shift();
    if (!next) return;
    if (!this.cinematicActive) {
      this.cinematicActive = true;
      this.pausedBeforeCinematic = this.paused;
      this.paused = true;
    }
    const D = 45; // above gameplay/tooltips (41), below the game-over overlay (50)
    const objs: Phaser.GameObjects.GameObject[] = [];
    const finish = (): void => {
      objs.forEach((o) => o.destroy());
      if (this.cinematicQueue.length > 0) {
        this.playPhantomCinematic();
      } else {
        this.cinematicActive = false;
        this.paused = this.pausedBeforeCinematic;
      }
    };

    // Dim the field and swallow clicks for the duration.
    objs.push(this.add.rectangle(640, 360, 1280, 720, 0x000000, 0.45).setDepth(D).setInteractive());

    // Power-up (~0.9s): a red ring pulses over the phantom.
    const ring = this.add
      .circle(next.from.x, next.from.y, 26, 0xff2222, 0.3)
      .setStrokeStyle(3, 0xff4444)
      .setDepth(D + 1);
    objs.push(ring);
    this.tweens.add({
      targets: ring,
      radius: 62,
      alpha: 0.9,
      duration: 300,
      yoyo: true,
      repeat: 1,
      onComplete: () => {
        // The red bullet (~0.5s flight).
        const bullet = this.add
          .circle(next.from.x, next.from.y, 10, 0xff2020, 1)
          .setStrokeStyle(2, 0xffaaaa)
          .setDepth(D + 2);
        objs.push(bullet);
        this.tweens.add({
          targets: bullet,
          x: next.to.x,
          y: next.to.y,
          duration: 500,
          ease: 'Cubic.easeIn',
          onComplete: () => {
            // Explosion (~0.6s): an expanding burst where it struck.
            bullet.destroy();
            const boom = this.add.circle(next.to.x, next.to.y, 8, 0xff5500, 0.9).setDepth(D + 2);
            const flash = this.add.circle(next.to.x, next.to.y, 4, 0xffdd66, 1).setDepth(D + 3);
            objs.push(boom, flash);
            this.tweens.add({ targets: flash, radius: 34, alpha: 0, duration: 350 });
            this.tweens.add({
              targets: boom,
              radius: 72,
              alpha: 0,
              duration: 600,
              ease: 'Quad.easeOut',
              onComplete: finish,
            });
          },
        });
      },
    });
  }

  /**
   * Show a stat tooltip for the enemy under the pointer (nearest within ~26px),
   * or hide it when the pointer isn't over an enemy.
   */
  private updateHoverBubble(): void {
    const p = this.input.activePointer;
    const wx = p.worldX;
    const wy = p.worldY;

    let near: (typeof this.sim.state.enemies)[number] | null = null;
    let best = 26;
    for (const e of this.sim.state.enemies) {
      const d = Math.hypot(e.pos.x - wx, e.pos.y - wy);
      if (d <= best) {
        best = d;
        near = e;
      }
    }

    if (!near) {
      this.hoverBg.setVisible(false);
      this.hoverText.setVisible(false);
      return;
    }

    const label = ENEMY_NAMES[near.type];
    this.hoverText.setText(
      `${label}\nHP   ${Math.ceil(near.hp)}/${near.maxHp}\nSpeed ${Math.round(near.baseSpeed)}\nBounty $${near.bounty}`,
    );
    // Anchor the bubble just above the enemy; nudge down if it would clip the top.
    const bx = Phaser.Math.Clamp(near.pos.x, 80, 990);
    const by = Math.max(96, near.pos.y - 24);
    this.hoverBg.setSize(this.hoverText.width + 20, this.hoverText.height + 14);
    this.hoverBg.setPosition(bx, by).setVisible(true);
    this.hoverText.setPosition(bx, by - 7).setVisible(true);
  }

  // ---------------------------------------------------------------------------
  // View reconciliation
  // ---------------------------------------------------------------------------

  /**
   * Reconcile sprite pools against the current sim state.
   * - New entity id → create view
   * - Existing id  → update view (position, HP bar, tint, tier)
   * - Vanished id  → destroy view + remove from map
   */
  private syncViews(): void {
    const state = this.sim.state;

    // -- Enemies ---------------------------------------------------------------
    const liveEnemyIds = new Set<number>();
    for (const enemy of state.enemies) {
      liveEnemyIds.add(enemy.id);
      const view = this.enemyViews.get(enemy.id);
      if (view) {
        view.update(enemy, state.frozen);
      } else {
        this.enemyViews.set(enemy.id, new EnemyView(this, enemy));
      }
    }
    for (const [id, view] of this.enemyViews) {
      if (!liveEnemyIds.has(id)) {
        view.destroy();
        this.enemyViews.delete(id);
      }
    }

    // -- Towers ----------------------------------------------------------------
    const liveTowerIds = new Set<number>();
    for (const tower of state.towers) {
      liveTowerIds.add(tower.id);
      const view = this.towerViews.get(tower.id);
      const fx = {
        icons: towerEffectIcons(this.effects, tower.type, this.effectsReadAt),
        shield: this.shieldOn,
      };
      if (view) {
        view.update(tower, tower.id === this.selectedTowerId, state.frozen, fx);
      } else {
        const v = new TowerView(this, tower);
        v.update(tower, tower.id === this.selectedTowerId, state.frozen, fx);
        this.towerViews.set(tower.id, v);
      }
    }
    for (const [id, view] of this.towerViews) {
      // A tower that left the sim — burnt down by King Ember, or sold — fades
      // off its plot instead of blinking out. It is dropped from towerViews
      // straight away so this sweep never sees it twice; the fading objects
      // clean themselves up when the tween finishes.
      if (!liveTowerIds.has(id)) {
        this.towerViews.delete(id);
        this.fadingTowerViews.add(view);
        view.fadeOutAndDestroy(() => this.fadingTowerViews.delete(view));
      }
    }

    this.syncMinions(state.minions);

    // -- Projectiles ----------------------------------------------------------
    const liveProjIds = new Set<number>();
    for (const proj of state.projectiles) {
      liveProjIds.add(proj.id);
      const view = this.projectileViews.get(proj.id);
      if (view) {
        view.update(proj);
      } else {
        const v = new ProjectileView(this, proj);
        v.update(proj);
        this.projectileViews.set(proj.id, v);
      }
    }
    for (const [id, view] of this.projectileViews) {
      if (!liveProjIds.has(id)) {
        if (view.towerType === 'bomb') {
          this.spawnExplosion(view.lastPos.x, view.lastPos.y, view.tier >= 3 ? 200 : 110);
        } else if (view.towerType === 'demonic') {
          // Demonic Hambi's skull: the bomb's blast, in dark purple.
          this.spawnExplosion(view.lastPos.x, view.lastPos.y, 110, DEMONIC_BLAST);
        } else if (view.towerType === 'ultrahammy' && (view.tier === 2 || view.tier === 3)) {
          // Ultra Hammy's dynamite (and his minions') goes off like a bomb.
          this.spawnExplosion(view.lastPos.x, view.lastPos.y, 80);
        } else if (view.towerType === 'bacon' || view.towerType === 'sauce') {
          this.spawnDecal(`decal-${view.towerType}`, view.lastPos.x, view.lastPos.y);
        } else if (view.towerType === 'leafstorm') {
          // The tornado landed: leaves whirl out to the enemies it caught —
          // the four nearest the landing spot, which is the sim's own pick.
          const at = { x: view.lastPos.x, y: view.lastPos.y };
          const caught = state.enemies
            .filter((e) => e.alive)
            .sort(
              (a, b) =>
                Math.hypot(a.pos.x - at.x, a.pos.y - at.y) -
                Math.hypot(b.pos.x - at.x, b.pos.y - at.y),
            )
            .slice(0, BALANCE.towers.leafstorm.tier1.tornado?.targets ?? 4)
            .filter((e) => Math.hypot(e.pos.x - at.x, e.pos.y - at.y) <= LEAF_REACH)
            .map((e) => ({ x: e.pos.x, y: e.pos.y }));
          spawnLeafWhirl(this, at, caught);
        }
        view.destroy();
        this.projectileViews.delete(id);
      }
    }

    // -- Press targets (Chiply's Dad's chips, The Lifeguard's whistles) --------
    // A medium sprite labelled "Press", tinted from white toward red as its
    // fuse burns. Pressing dispatches to the sim; the view goes when the sim's
    // target does, whether pressed, expired, or cleared with its carrier.
    const liveChipIds = new Set<number>();
    let whistlePending = false;
    for (const c of state.chips) {
      liveChipIds.add(c.id);
      if (c.kind === 'whistle') whistlePending = true;
      let view = this.chipViews.get(c.id);
      if (!view) {
        view = this.spawnChipView(c);
        this.chipViews.set(c.id, view);
      }
      const burnt = 1 - c.remaining / c.fuse;
      // King Shroometron's sky shrooms FALL: from the top of the screen to
      // the bottom over their fuse, and the sprite (its own hit area) with them.
      if (c.kind === 'skyshroom') {
        const y = SKY_SHROOM_TOP + burnt * (SKY_SHROOM_BOTTOM - SKY_SHROOM_TOP);
        view.img.setPosition(c.pos.x, y);
        view.label.setPosition(c.pos.x, y + CHIP_SIZE / 2 + 4);
      }
      const warm = Phaser.Math.Clamp((burnt - 0.35) / 0.65, 0, 1);
      const g = Math.round(255 * (1 - warm * 0.8));
      const b = Math.round(255 * (1 - warm));
      view.img.setTint(Phaser.Display.Color.GetColor(255, g, b));
      view.label.setColor(warm > 0.5 ? '#ffffff' : '#1a1a1a');
    }
    for (const [id, view] of this.chipViews) {
      if (!liveChipIds.has(id)) {
        view.img.destroy();
        view.label.destroy();
        this.chipViews.delete(id);
      }
    }
    // The screen pulses red for as long as a whistle is waiting.
    if (whistlePending && !this.whistleFlash) {
      this.whistleFlash = this.add
        .rectangle(640, 360, 1280, 720, 0xff2222, 0)
        .setDepth(44)
        .setName('whistle-flash');
      this.tweens.add({
        targets: this.whistleFlash,
        fillAlpha: 0.28,
        duration: 500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    } else if (!whistlePending && this.whistleFlash) {
      this.tweens.killTweensOf(this.whistleFlash);
      this.whistleFlash.destroy();
      this.whistleFlash = null;
    }

    // -- Fireballs (King Ember's Ember Barrage) --------------------------------
    // Drawn as Graphics rather than sprites: a blue-white flaming core with a
    // hot outer halo, redrawn each frame at the sim's arc position so the ball
    // the player sees is exactly the one that lands.
    const liveFireballIds = new Set<number>();
    for (const fb of state.fireballs) {
      liveFireballIds.add(fb.id);
      // A shot the render layer has not seen before: swing its thrower's staff
      // once, on the frame it launches. Free for every future carrier, and it
      // needs no event of its own — the new fireball IS the event.
      if (!this.seenFireballIds.has(fb.id)) {
        this.seenFireballIds.add(fb.id);
        if (fb.ownerId !== undefined) this.enemyViews.get(fb.ownerId)?.swing();
        // The Reaper's scythe: ONE sweep across the board per swing, however
        // many towers it hits (one sim shot per tower, all in the same tick).
        if (fb.swing && fb.ownerId !== undefined) {
          const last = this.lastScytheAt.get(fb.ownerId) ?? -Infinity;
          if (this.time.now - last > 500) {
            this.lastScytheAt.set(fb.ownerId, this.time.now);
            this.playScytheSweep(fb.from);
          }
        }
      }
      // A swing has nothing in flight: the hit lands where the tower stands,
      // with a KO over it once it resolves (see the sweep below the loop).
      if (fb.swing) {
        this.swingHits.set(fb.id, {
          x: fb.to.x,
          y: fb.to.y,
          done: fb.duration > 0 ? fb.elapsed / fb.duration : 1,
        });
        continue;
      }
      // A BEAM (the Laser Nacho): a straight line snapped from the shooter to
      // the tower the instant it fires, fading out over the shot's travel. It
      // does not move — the sim's `pos` is ignored — because a beam is already
      // everywhere along its length; the damage still lands when the fade ends.
      if (fb.beam) {
        let g = this.beamViews.get(fb.id);
        if (!g) {
          g = this.add.graphics().setDepth(4.6);
          this.beamViews.set(fb.id, g);
        }
        const t = fb.duration > 0 ? Math.min(1, fb.elapsed / fb.duration) : 1;
        const a = 1 - t;
        g.clear();
        g.lineStyle(10, 0xf2c14a, 0.22 * a);
        g.lineBetween(fb.from.x, fb.from.y, fb.to.x, fb.to.y);
        g.lineStyle(5, 0xff5a3c, 0.75 * a);
        g.lineBetween(fb.from.x, fb.from.y, fb.to.x, fb.to.y);
        g.lineStyle(2, 0xfff3d0, a);
        g.lineBetween(fb.from.x, fb.from.y, fb.to.x, fb.to.y);
        continue;
      }
      // A thrown thing (the Corroder's leaves, the Nuisance Nugget's nugget
      // piece): a tumbling sprite along the same arc, no ember tail.
      if (fb.projectile && this.textures.exists(fb.projectile)) {
        let img = this.thrownViews.get(fb.id);
        if (!img) {
          const k = fb.scale ?? 1;
          img = this.add
            .image(fb.pos.x, fb.pos.y, fb.projectile)
            // A FALLING thing is a spear, not a tumbling scrap: it is drawn
            // tall and narrow, point down, so the drop reads as a drop.
            .setDisplaySize(fb.fromSky ? 26 * k : 30, fb.fromSky ? 78 * k : 30)
            .setDepth(4.5);
          // A possessed tower's spirit (EnemySpec.ghosting) flies purple; a
          // shroomnitized tower's glowing spore keeps its own orange.
          if (fb.ghosted && fb.projectile === 'proj-ghostwisp') img.setTint(0xb46bff);
          this.thrownViews.set(fb.id, img);
        }
        img.setPosition(fb.pos.x, fb.pos.y);
        // Falling spears keep their heading; everything else tumbles.
        if (!fb.fromSky) img.setRotation(img.rotation + 0.18);
        this.thrownViews.set(fb.id, img);
        continue;
      }
      let view = this.fireballViews.get(fb.id);
      if (!view) {
        view = { gfx: this.add.graphics().setDepth(4.5), last: { ...fb.pos } };
        this.fireballViews.set(fb.id, view);
      }
      // A tail of shrinking, fading embers strung back along the arc the ball
      // has already flown, so a fast-moving ball still reads as a fireball and
      // its direction of travel is obvious.
      const prev = view.last;
      const dx = fb.pos.x - prev.x;
      const dy = fb.pos.y - prev.y;
      view.last = { x: fb.pos.x, y: fb.pos.y };
      const g = view.gfx;
      g.clear();
      for (let t = 5; t >= 1; t--) {
        g.fillStyle(0x2f8fe6, 0.1 * (1 - t / 6));
        g.fillCircle(fb.pos.x - dx * t * 1.6, fb.pos.y - dy * t * 1.6, 14 - t * 1.6);
      }
      g.fillStyle(0x2f8fe6, 0.3);
      g.fillCircle(fb.pos.x, fb.pos.y, 26);
      g.fillStyle(0x4fb8ff, 0.75);
      g.fillCircle(fb.pos.x, fb.pos.y, 16);
      g.fillStyle(0xa8e4ff, 0.95);
      g.fillCircle(fb.pos.x, fb.pos.y, 10);
      g.fillStyle(0xffffff, 1);
      g.fillCircle(fb.pos.x, fb.pos.y, 5);
    }
    for (const [id, img] of this.thrownViews) {
      if (!liveFireballIds.has(id)) {
        img.destroy();
        this.thrownViews.delete(id);
      }
    }
    for (const [id, g] of this.beamViews) {
      if (!liveFireballIds.has(id)) {
        g.destroy();
        this.beamViews.delete(id);
      }
    }
    for (const [id, view] of this.fireballViews) {
      if (!liveFireballIds.has(id)) {
        this.spawnFireballBurst(view.last.x, view.last.y);
        view.gfx.destroy();
        this.fireballViews.delete(id);
      }
    }
    for (const id of this.seenFireballIds) {
      if (!liveFireballIds.has(id)) this.seenFireballIds.delete(id);
    }
    // A swing hit that left the sim having run (nearly) its whole travel
    // LANDED: a KO over that tower. One cut short (the boss died, the wave
    // ended) did not, and shows nothing.
    for (const [id, hit] of this.swingHits) {
      if (liveFireballIds.has(id)) continue;
      this.swingHits.delete(id);
      if (hit.done > 0.85) this.flashSwingKo(hit.x, hit.y);
    }

    this.syncShields(state);
    this.syncBossBar(state);
  }

  /**
   * Railway trail (`trackStyle: 'railway'`): a gravel bed, wooden sleepers
   * perpendicular to the track, and two steel rails offset either side of the
   * centreline. Corner offsets are mitred, which is exact for the axis-aligned
   * 90° paths every level uses. Pure Graphics — overlap-e2e safe.
   */
  private drawRailway(
    gfx: Phaser.GameObjects.Graphics,
    waypoints: { x: number; y: number }[],
  ): void {
    const BED_W = TRAIL_WIDTH; // gravel bed matches the dirt trail's footprint
    const TIE_W = 24; // sleeper length (across the track)
    const TIE_T = 6; // sleeper thickness (along the track)
    const TIE_SPACING = 24;
    const RAIL_OFFSET = 7; // rail distance from the centreline
    const RAIL_T = 3;

    // Gravel bed under everything.
    gfx.lineStyle(BED_W, 0x4a4136, 1);
    gfx.beginPath();
    gfx.moveTo(waypoints[0].x, waypoints[0].y);
    for (let i = 1; i < waypoints.length; i++) gfx.lineTo(waypoints[i].x, waypoints[i].y);
    gfx.strokePath();

    // Per-segment unit direction + perpendicular.
    const dirs = [];
    for (let i = 1; i < waypoints.length; i++) {
      const a = waypoints[i - 1];
      const b = waypoints[i];
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      const ux = (b.x - a.x) / len;
      const uy = (b.y - a.y) / len;
      dirs.push({ a, b, len, ux, uy, px: -uy, py: ux });
    }

    // Wooden sleepers, evenly spaced along each segment.
    gfx.lineStyle(TIE_T, 0x6a4a26, 1);
    for (const d of dirs) {
      for (let t = TIE_SPACING / 2; t <= d.len - TIE_SPACING / 4; t += TIE_SPACING) {
        const cx = d.a.x + d.ux * t;
        const cy = d.a.y + d.uy * t;
        gfx.lineBetween(
          cx - (d.px * TIE_W) / 2,
          cy - (d.py * TIE_W) / 2,
          cx + (d.px * TIE_W) / 2,
          cy + (d.py * TIE_W) / 2,
        );
      }
    }

    // Twin steel rails: the centreline offset ±RAIL_OFFSET, with mitred
    // corners (perpendiculars of both adjoining segments summed at a vertex).
    gfx.lineStyle(RAIL_T, 0x9aa2ad, 1);
    for (const side of [-1, 1]) {
      gfx.beginPath();
      for (let i = 0; i < waypoints.length; i++) {
        const prev = dirs[i - 1];
        const next = dirs[i];
        // Corner vertices sum two unit perpendiculars — for 90° axis-aligned
        // turns that is the exact mitre; endpoints use their one segment's.
        const ox = ((prev?.px ?? 0) + (next?.px ?? 0)) * side * RAIL_OFFSET;
        const oy = ((prev?.py ?? 0) + (next?.py ?? 0)) * side * RAIL_OFFSET;
        if (i === 0) gfx.moveTo(waypoints[i].x + ox, waypoints[i].y + oy);
        else gfx.lineTo(waypoints[i].x + ox, waypoints[i].y + oy);
      }
      gfx.strokePath();
    }
  }

  /**
   * Rock trail (`trackStyle: 'rocks'`, Fiery Lava): big cooled-basalt boulders
   * laid across the lava with a worn TRACK trodden along the top of them, so
   * the crossing reads as a rock causeway that the horde actually walks —
   * boulders first, then the path over them. Stone size and jitter come from
   * the stone's index (deterministic — no RNG, so it is stable across reloads
   * and screenshot diffs). Pure Graphics — overlap-e2e safe.
   */
  private drawRockTrail(
    gfx: Phaser.GameObjects.Graphics,
    waypoints: { x: number; y: number }[],
  ): void {
    const BED_W = TRAIL_WIDTH + 18; // basalt causeway, wider than the boulders
    const STEP = 54; // centre-to-centre spacing — sparse, chunky stepping stones
    const R = 29; // base boulder radius — must clearly overhang the track
    const TRACK_W = 13; // the worn path trodden over the boulder tops

    // Deterministic pseudo-random in [0,1) from two integers — no RNG, so the
    // causeway is identical across reloads and screenshot diffs.
    const rand = (n: number, k: number): number => {
      const v = Math.sin(n * 127.1 + k * 311.7) * 43758.5453;
      return v - Math.floor(v);
    };

    /**
     * One irregular boulder: a closed polygon of `verts` points whose radii and
     * angles are individually jittered, so no two stones share a silhouette.
     */
    const boulder = (cx: number, cy: number, r: number, n: number): void => {
      const verts = 6 + Math.floor(rand(n, 1) * 4); // 6–9 sides
      const spin = rand(n, 2) * Math.PI * 2;
      const pts: Phaser.Geom.Point[] = [];
      for (let v = 0; v < verts; v++) {
        // Angle wobbles around the even division so sides differ in width.
        const ang = spin + ((v + (rand(n, 10 + v) - 0.5) * 0.55) / verts) * Math.PI * 2;
        const rad = r * (0.68 + rand(n, 40 + v) * 0.5);
        pts.push(new Phaser.Geom.Point(cx + Math.cos(ang) * rad, cy + Math.sin(ang) * rad));
      }
      // Shadowed underside first, offset down-right to seat the rock.
      gfx.fillStyle(0x241f1c, 1);
      gfx.fillPoints(
        pts.map((p) => new Phaser.Geom.Point(p.x + 3, p.y + 4)),
        true,
      );
      gfx.fillStyle(0x4c443c, 1); // body
      gfx.fillPoints(pts, true);
      // Lit top-left face: the same silhouette shrunk toward an offset centre,
      // so the facet follows the rock's own shape instead of being a disc.
      const fx = cx - r * 0.2;
      const fy = cy - r * 0.22;
      gfx.fillStyle(0x6b6157, 1);
      gfx.fillPoints(
        pts.map((p) => new Phaser.Geom.Point(fx + (p.x - cx) * 0.58, fy + (p.y - cy) * 0.58)),
        true,
      );
      gfx.fillStyle(0x837a6f, 1); // highlight catching the lava glow
      gfx.fillPoints(
        pts.map((p) => new Phaser.Geom.Point(fx + (p.x - cx) * 0.26, fy + (p.y - cy) * 0.26)),
        true,
      );
    };

    // Dark basalt bed, so gaps between the sparse stones never show raw lava.
    gfx.lineStyle(BED_W, 0x2b2622, 1);
    gfx.beginPath();
    gfx.moveTo(waypoints[0].x, waypoints[0].y);
    for (let i = 1; i < waypoints.length; i++) gfx.lineTo(waypoints[i].x, waypoints[i].y);
    gfx.strokePath();

    // Walk the whole polyline at a fixed stride, dropping one boulder per step.
    let n = 0;
    for (let i = 1; i < waypoints.length; i++) {
      const a = waypoints[i - 1];
      const b = waypoints[i];
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      const ux = (b.x - a.x) / len;
      const uy = (b.y - a.y) / len;
      const px = -uy;
      const py = ux;
      for (let t = 0; t <= len; t += STEP, n++) {
        // Offset across and along the trail so the chain never looks stamped.
        const cx = a.x + ux * (t + (rand(n, 3) - 0.5) * 14) + px * (rand(n, 4) - 0.5) * 22;
        const cy = a.y + uy * (t + (rand(n, 3) - 0.5) * 14) + py * (rand(n, 4) - 0.5) * 22;
        boulder(cx, cy, R * (0.72 + rand(n, 5) * 0.8), n); // sizes vary ~0.7–1.5×
      }
    }

    // The worn track over the boulder tops — laid last so it reads as a path
    // trodden ALONG the rocks rather than a road beside them.
    gfx.lineStyle(TRACK_W, DIRT, 1);
    gfx.beginPath();
    gfx.moveTo(waypoints[0].x, waypoints[0].y);
    for (let i = 1; i < waypoints.length; i++) gfx.lineTo(waypoints[i].x, waypoints[i].y);
    gfx.strokePath();
  }

  /**
   * A seated hambi (the Hambi Tower's own sprite, small) with a wine glass
   * it sips from now and then: the glass tilts up to its face and back on a
   * slow, offset loop so the room never sips in unison. Render-only.
   */
  private seatHambi(
    x: number,
    y: number,
    offset: number,
    drinking = true,
    art = 'tower-patty-1',
    size = 52,
  ): void {
    const key = this.textures.exists(art) ? art : 'tower-patty-1';
    this.add.image(x, y, key).setDisplaySize(size, size).setDepth(1.45);
    if (!drinking || !this.textures.exists('decor-wineglass')) return;
    const glass = this.add
      .image(x + 20, y + 8, 'decor-wineglass')
      .setDisplaySize(22, 22)
      .setOrigin(0.5, 0.9)
      .setDepth(1.46);
    this.tweens.add({
      targets: glass,
      y: y - 6,
      x: x + 12,
      angle: -35,
      duration: 700,
      ease: 'Sine.easeInOut',
      yoyo: true,
      hold: 500,
      repeat: -1,
      repeatDelay: 1400 + offset,
      delay: offset,
    });
  }

  /**
   * Hambi Bar decor: the regulars on the counter stools and at the side
   * tables, all drinking, and a disco ball hanging from the top of the
   * room, turning slowly. Positions follow bg-hambibar's painted chairs.
   */
  private drawHambiBarDecor(): void {
    // Seated ON the counter stools (seat centres at y≈128) — the two middle
    // stools stay empty because the disco ball hangs over them.
    const stools = [272, 438, 762, 927, 1010];
    stools.forEach((x, i) => this.seatHambi(x, 128, i * 530));
    // On the side-table chairs down the left wall (seat centres); the
    // right-hand tables sit under the HUD panel.
    this.seatHambi(87, 203, 900);
    this.seatHambi(87, 410, 1700);
    this.seatHambi(140, 583, 300);
    this.seatHambi(38, 663, 1200);
    // The disco ball: big, still, hanging from the ceiling on a chain at the
    // top centre of the room — over the counter, clear of the trail (y ≥ 230).
    if (this.textures.exists('decor-discoball')) {
      const chain = this.add.graphics().setDepth(1.47);
      chain.lineStyle(3, 0x2b2b2b, 1);
      chain.lineBetween(640, 0, 640, 60);
      this.add.image(640, 122, 'decor-discoball').setDisplaySize(136, 136).setDepth(1.47);
    }
  }

  /**
   * HTD Exhibition decor: three hambis guarding the empty case at the bottom
   * of the hall, shoulder to shoulder — no drinks on duty.
   */
  private drawExhibitionDecor(): void {
    for (const x of [584, 640, 696]) this.seatHambi(x, 488, 0, false, 'tower-gunner-1', 56);
  }

  /**
   * Station decor (`decor: 'station'`, The Space Station): the satellite is
   * under way, so planets and stars drift past in the strips of open space
   * above and below the hull. Everything is masked to those strips — the
   * hull (and the game on it) is never crossed. Render-only.
   */
  private drawStationDecor(): void {
    // Visible only OUTSIDE the hull and its solar wings: an inverted geometry
    // mask over those rects. The top strip is under the HUD bar, so the
    // traffic runs down the left margin and along the bottom edge.
    const hull = this.make.graphics();
    hull.fillStyle(0xffffff);
    hull.fillRect(100, 72, 1080, 606);
    hull.fillRect(0, 130, 80, 140);
    hull.fillRect(0, 450, 80, 120);
    const mask = hull.createGeometryMask();
    mask.invertAlpha = true;
    type Drifter = Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle;
    const across = (obj: Drifter, y: number, duration: number, delay: number) => {
      obj.setMask(mask).setDepth(0.5).setPosition(1330, y);
      this.tweens.add({ targets: obj, x: -60, duration, delay, repeat: -1, repeatDelay: 900 });
    };
    const down = (obj: Drifter, x: number, duration: number, delay: number) => {
      obj.setMask(mask).setDepth(0.5).setPosition(x, -60);
      this.tweens.add({ targets: obj, y: 780, duration, delay, repeat: -1, repeatDelay: 900 });
    };
    // Planets: one passes every few seconds — down the left margin, then
    // along the bottom edge.
    const planet = (key: string, size: number) =>
      this.textures.exists(key) ? this.add.image(0, 0, key).setDisplaySize(size, size) : null;
    const a = planet('decor-planet-a', 60);
    const b = planet('decor-planet-b', 60);
    const c = planet('decor-planet-c', 44);
    const d = planet('decor-planet-a', 44);
    if (a) down(a, 48, 9000, 0);
    if (b) down(b, 44, 11000, 4500);
    if (c) across(c, 700, 9000, 2500);
    if (d) across(d, 698, 12000, 8000);
    // Star streaks: faster, so the drift reads as the station moving.
    for (let i = 0; i < 8; i++) {
      const streak = this.add.rectangle(0, 0, 18, 2, 0xffffff, 0.7);
      if (i % 2 === 0) across(streak, 690 + ((i * 23) % 26), 2600 + i * 350, i * 420);
      else down(streak.setSize(2, 18), 20 + ((i * 29) % 60), 2600 + i * 350, i * 420);
    }
  }

  /**
   * Panel trail (`trackStyle: 'panel'`, The Space Station): a walkway of
   * darker hull plating with a seam either side and rivets along both edges,
   * so the route blends into the satellite instead of being painted dirt.
   */
  private drawPanelTrail(
    gfx: Phaser.GameObjects.Graphics,
    waypoints: { x: number; y: number }[],
  ): void {
    const stroke = (width: number, color: number, alpha: number) => {
      gfx.lineStyle(width, color, alpha);
      gfx.beginPath();
      gfx.moveTo(waypoints[0].x, waypoints[0].y);
      for (let i = 1; i < waypoints.length; i++) gfx.lineTo(waypoints[i].x, waypoints[i].y);
      gfx.strokePath();
    };
    stroke(TRAIL_WIDTH + 6, 0x5f6670, 1); // outer seam
    stroke(TRAIL_WIDTH, 0x8f979f, 1); // the walkway plate
    stroke(TRAIL_WIDTH - 12, 0x9aa2aa, 1); // a lighter centre strip
    // Rivets every 40px along each segment, on both shoulders.
    gfx.fillStyle(0x4b525a, 1);
    for (let i = 1; i < waypoints.length; i++) {
      const a = waypoints[i - 1];
      const b = waypoints[i];
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      const ux = (b.x - a.x) / len;
      const uy = (b.y - a.y) / len;
      const nx = -uy * (TRAIL_WIDTH / 2 - 3);
      const ny = ux * (TRAIL_WIDTH / 2 - 3);
      for (let d = 20; d < len - 10; d += 40) {
        const px = a.x + ux * d;
        const py = a.y + uy * d;
        gfx.fillCircle(px + nx, py + ny, 2);
        gfx.fillCircle(px - nx, py - ny, 2);
      }
    }
  }

  /** The Hyno-Fungi's swirl over a hypnotised tower, and what the hypnosis did. */
  private showHypnosis(
    pos: { x: number; y: number },
    outcome: 'stun' | 'shroomnitize' | 'range' | 'stunseal',
  ): void {
    if (this.textures.exists('fx-hypnoswirl')) {
      const swirl = this.add
        .image(pos.x, pos.y - 6, 'fx-hypnoswirl')
        .setDisplaySize(56, 56)
        .setAlpha(0.9)
        .setDepth(4.8)
        .setName('hypno-swirl');
      this.tweens.add({
        targets: swirl,
        angle: 720,
        alpha: 0,
        duration: 1400,
        ease: 'Cubic.easeIn',
        onComplete: () => swirl.destroy(),
      });
    }
    const text = {
      stun: 'HYPNOTIZED!',
      shroomnitize: 'SHROOMNITIZED!',
      range: 'RANGE −40%',
      stunseal: 'HYPNOTIZED + SEALED!',
    }[outcome];
    this.spawnDodgeLabel(pos.x, pos.y - 46, text, 'hypno-label', '#1a3a8a');
  }

  /** A press target (chip or whistle): sprite, "Press" label, and the press handler. */
  private spawnChipView(c: ChipState): {
    img: Phaser.GameObjects.Image;
    label: Phaser.GameObjects.Text;
  } {
    const want =
      c.kind === 'whistle'
        ? 'pickup-whistle'
        : c.kind === 'skyshroom'
          ? 'proj-mushyshroom'
          : 'pickup-chip';
    const key = this.textures.exists(want) ? want : 'plot';
    const img = this.add
      .image(c.pos.x, c.pos.y, key)
      .setDisplaySize(CHIP_SIZE, CHIP_SIZE)
      .setDepth(30)
      .setName(`${c.kind}-${c.id}`)
      .setInteractive({ useHandCursor: true });
    const label = this.add
      .text(c.pos.x, c.pos.y, c.kind === 'skyshroom' ? 'Tap!' : 'Press', {
        fontSize: '18px',
        color: '#1a1a1a',
        fontStyle: 'bold',
        stroke: '#ffffff',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(31);
    // Pop in, so a fresh chip catches the eye.
    const s = img.scaleX;
    img.setScale(s * 0.2);
    this.tweens.add({ targets: img, scale: s, duration: 220, ease: 'Back.easeOut' });
    // The press. stopPropagation keeps the scene-level pointerdown (the
    // InputController's placement / selection) from seeing the same tap.
    img.on(
      'pointerdown',
      (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
        ev.stopPropagation();
        this.sim.dispatch({ type: 'pressChip', chipId: c.id });
      },
    );
    return { img, label };
  }

  /**
   * Prince Nacho's shields: press and HOLD. The ring round each one fills as
   * the hold accumulates and the disc reddens as the fuse burns, so the two
   * clocks the player is racing are both on screen at once.
   *
   * The pointer is released on the SCENE, not the sprite: dragging off the
   * disc mid-hold must count as letting go, and a `pointerout` alone would
   * miss a release that happened elsewhere.
   */
  private syncShields(state: GameState): void {
    const live = new Set<number>();
    for (const sh of state.shields) {
      live.add(sh.id);
      let view = this.shieldViews.get(sh.id);
      if (!view) {
        const key = this.textures.exists('decor-shield') ? 'decor-shield' : 'plot';
        const img = this.add
          .image(sh.pos.x, sh.pos.y, key)
          .setDisplaySize(SHIELD_SIZE, SHIELD_SIZE)
          .setDepth(32)
          .setName(`shield-${sh.id}`)
          .setInteractive({ useHandCursor: true });
        const ring = this.add.graphics().setDepth(33);
        const label = this.add
          .text(sh.pos.x, sh.pos.y + SHIELD_SIZE / 2 + 26, 'HOLD', {
            fontSize: '20px',
            color: '#ffffff',
            fontStyle: 'bold',
            stroke: '#1a1a1a',
            strokeThickness: 4,
          })
          .setOrigin(0.5)
          .setDepth(33);
        const sc = img.scaleX;
        img.setScale(sc * 0.2);
        this.tweens.add({ targets: img, scale: sc, duration: 220, ease: 'Back.easeOut' });
        img.on(
          'pointerdown',
          (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
            // Swallow it: the scene-level pointerdown places towers.
            ev.stopPropagation();
            this.sim.dispatch({ type: 'holdShield', shieldId: sh.id, down: true });
          },
        );
        view = { img, ring, label };
        this.shieldViews.set(sh.id, view);
      }
      const heldFrac = Math.min(1, sh.held / sh.holdNeeded);
      const burnt = 1 - sh.remaining / sh.fuse;
      const warm = Phaser.Math.Clamp((burnt - 0.3) / 0.7, 0, 1);
      const g = Math.round(255 * (1 - warm * 0.75));
      const b = Math.round(255 * (1 - warm * 0.9));
      view.img.setTint(Phaser.Display.Color.GetColor(255, g, b));
      view.label.setText(heldFrac > 0 ? `HOLD ${Math.round(heldFrac * 100)}%` : 'HOLD');
      const ring = view.ring;
      ring.clear();
      ring.lineStyle(7, 0x2b2b3d, 0.85);
      ring.strokeCircle(sh.pos.x, sh.pos.y, SHIELD_SIZE / 2 + 9);
      if (heldFrac > 0) {
        ring.lineStyle(7, 0x4fe36b, 1);
        ring.beginPath();
        ring.arc(
          sh.pos.x,
          sh.pos.y,
          SHIELD_SIZE / 2 + 9,
          -Math.PI / 2,
          -Math.PI / 2 + heldFrac * Math.PI * 2,
        );
        ring.strokePath();
      }
    }
    for (const [id, view] of this.shieldViews) {
      if (live.has(id)) continue;
      view.img.destroy();
      view.ring.destroy();
      view.label.destroy();
      this.shieldViews.delete(id);
    }
  }

  /**
   * The second form's health bar: a wide bar under the top bar with
   * "75 000/100 000 · Prince Nacho (2nd form)" beneath it, per the brief. It
   * appears with him and goes with him.
   */
  private syncBossBar(state: GameState): void {
    const boss = state.enemies.find(
      (e) => e.alive && (e.anchored || BIG_BAR_BOSSES.includes(e.type)),
    );
    if (!boss) {
      if (this.bossBar) {
        this.bossBar.bg.destroy();
        this.bossBar.fill.destroy();
        this.bossBar.label.destroy();
        this.bossBar = null;
      }
      return;
    }
    if (!this.bossBar) {
      const bg = this.add
        .rectangle(640, 82, BOSS_BAR_W, 26, 0x2a1030, 0.95)
        .setStrokeStyle(3, 0xffc53d)
        .setDepth(43)
        .setName('boss-bar');
      const fill = this.add
        .rectangle(640 - BOSS_BAR_W / 2 + 3, 82, BOSS_BAR_W - 6, 20, 0xd93b3b)
        .setOrigin(0, 0.5)
        .setDepth(43.1);
      const label = this.add
        .text(640, 110, '', {
          fontSize: '18px',
          color: '#ffd98a',
          fontStyle: 'bold',
          stroke: '#1a1020',
          strokeThickness: 4,
        })
        .setOrigin(0.5)
        .setDepth(43.2)
        .setName('boss-bar-label');
      this.bossBar = { bg, fill, label };
    }
    const ratio = Math.max(0, boss.hp / boss.maxHp);
    this.bossBar.fill.width = (BOSS_BAR_W - 6) * ratio;
    const n = (v: number) => Math.max(0, Math.round(v)).toLocaleString('en-US');
    this.bossBar.label.setText(`${n(boss.hp)}/${n(boss.maxHp)}   ${ENEMY_NAMES[boss.type]}`);
  }

  /** Big red K.O over the playfield when a chip's fuse runs out. */
  private flashKo(): void {
    const t = this.add
      .text(535, 360, 'K.O', {
        fontSize: '160px',
        color: '#ff2222',
        fontStyle: 'bold',
        stroke: '#3a0000',
        strokeThickness: 10,
      })
      .setOrigin(0.5)
      .setDepth(45)
      .setName('ko-flash')
      .setScale(0.6);
    this.tweens.add({ targets: t, scale: 1, duration: 200, ease: 'Back.easeOut' });
    this.tweens.add({
      targets: t,
      alpha: 0,
      delay: 900,
      duration: 500,
      onComplete: () => t.destroy(),
    });
  }

  /**
   * STRIKE: a missed whistle. The Lifeguard looms, enlarged, beside the word,
   * over the playfield for a moment (the two lives are already gone sim-side).
   */
  private flashStrike(): void {
    const D = 45;
    const objs: Phaser.GameObjects.GameObject[] = [];
    if (this.textures.exists('enemy-lifeguard')) {
      const him = this.add
        .image(380, 360, 'enemy-lifeguard')
        .setDisplaySize(280, 280)
        .setDepth(D)
        .setName('strike-lifeguard');
      // Pop in at his enlarged size — setDisplaySize is a scale, so the
      // tween must end on THAT scale, not 1 (his native 64px).
      const big = him.scaleX;
      him.setScale(big * 0.6);
      this.tweens.add({ targets: him, scale: big, duration: 200, ease: 'Back.easeOut' });
      objs.push(him);
    }
    const t = this.add
      .text(760, 360, 'STRIKE', {
        fontSize: '120px',
        color: '#ff2222',
        fontStyle: 'bold',
        stroke: '#3a0000',
        strokeThickness: 10,
      })
      .setOrigin(0.5)
      .setDepth(D)
      .setName('strike-flash')
      .setScale(0.6);
    objs.push(t);
    this.tweens.add({ targets: t, scale: 1, duration: 200, ease: 'Back.easeOut' });
    this.tweens.add({
      targets: objs,
      alpha: 0,
      delay: 1200,
      duration: 500,
      onComplete: () => objs.forEach((o) => o.destroy()),
    });
  }

  /**
   * Draw one polyline in the level's current trail style. Pulled out of
   * `create` so the entrance lead-ins can be drawn the same way as the trunk,
   * and so Rocket Rage's transition can redraw the whole track in a new style
   * without duplicating the switch.
   */
  private drawTrailStyle(
    g: Phaser.GameObjects.Graphics,
    pts: { x: number; y: number }[],
    style = this.trackStyle,
  ): void {
    if (style === 'railway') {
      this.drawRailway(g, pts);
    } else if (style === 'rocks') {
      this.drawRockTrail(g, pts);
    } else if (style === 'panel') {
      this.drawPanelTrail(g, pts);
    } else {
      g.lineStyle(TRAIL_WIDTH, DIRT, 1);
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
      g.strokePath();
    }
  }

  /**
   * Nach'yo Business decor: the NACHO ONLY signpost planted between the two
   * bases, and cheese blobs pooled around the crater floor. The sign's board
   * is art; its LETTERING is drawn here, because the generator cannot spell.
   * Render-only.
   */
  private drawNachyoDecor(): void {
    if (this.textures.exists('decor-nachosign')) {
      this.add.image(180, 250, 'decor-nachosign').setDisplaySize(96, 96).setDepth(1.45);
    }
    // y = 240, not the sprite's own 250: the board occupies the TOP of
    // decor-nachosign's frame (the post fills the rest), so text centred on
    // the sprite would hang off the bottom edge of the plank.
    this.add
      .text(180, 240, 'NACHO\nONLY', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        fontStyle: 'bold',
        color: '#2b1a06',
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(1.46);
    // No procedural cheese here: bg-nachyo already carries the splatters, on
    // the crater rim where BG_RULES wants them. Drawing more with Graphics
    // read as pale smudges over the playfield and is the hand-faked art
    // CLAUDE.md rules out.
  }

  /**
   * Rocket Rage decor: the giant rocket standing in the middle of the pad.
   * A SPRITE, not part of the background, because on wave 10 it has to leave
   * (see `playRocketCutscene`). Its footprint is x 545–735, y 255–545, which
   * the level's path and plots are laid out around.
   */
  private drawRocketPadDecor(): void {
    if (!this.textures.exists('decor-rocket')) return;
    this.rocketSprite = this.add
      .image(640, 400, 'decor-rocket')
      .setDisplaySize(190, 290)
      .setDepth(1.45);
  }

  /**
   * Through Space and Time's opening decor: the rocket standing on the grey
   * platform. It sits in the pocket the route deliberately leaves empty
   * (x 500..780, y 160..400) — the same pocket Prince Nacho's second form
   * lands in on wave 30, once the rocket is long gone.
   *
   * A SPRITE, not part of the background, because it leaves twice.
   */
  private drawSpacePlatformDecor(): void {
    if (!this.textures.exists('decor-rocket')) return;
    this.rocketSprite = this.add
      .image(640, 288, 'decor-rocket')
      .setDisplaySize(140, 195)
      .setDepth(1.45);
  }

  /**
   * The ice world's sculptures (`decor: 'icesculptures'`), drawn after the
   * wave-20 launch. Six pinned coordinates the GAME owns, not background
   * scenery: an image model has never seen the route, so it cannot put
   * anything "on the ice" without burying the track — the lesson Bun Beach's
   * buns taught. Every spot is ≥45px off any lane, ≥70px from any plot, and
   * clear of the boss pocket. Pinned by hubLevels.test.ts.
   */
  private drawIceSculptures(): void {
    if (!this.textures.exists('decor-icesculpture')) return;
    for (const [x, y] of ICE_SCULPTURE_SPOTS) {
      this.plotBases.push(
        this.add.image(x, y, 'decor-icesculpture').setDisplaySize(78, 78).setDepth(1.4),
      );
    }
  }

  /**
   * Phantom decor (`decor: 'phantoms'`, The Shadowing Meadow): Phantom Hambis
   * "lurking around the forest" — the tower's own art, half-faded, drifting
   * a few pixels up and down at the clearing's edges. Positions are config
   * (`PHANTOM_DECOR`) so a test can pin them clear of the trail and plots.
   * Render-only.
   */
  private drawPhantomDecor(): void {
    if (!this.textures.exists('tower-phantom-1')) return;
    PHANTOM_DECOR.forEach(({ x, y }, i) => {
      const ghost = this.add
        .image(x, y, 'tower-phantom-1')
        .setDisplaySize(58, 58)
        .setAlpha(0.6)
        .setDepth(1.4)
        .setFlipX(x > 640)
        .setName(`decor-phantom-${i}`);
      this.tweens.add({
        targets: ghost,
        y: y - 6,
        alpha: 0.4,
        duration: 1600 + i * 230,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    });
  }

  /** One haunting decor sprite: faded, bobbing, never interactive. */
  private haunt(
    key: string,
    x: number,
    y: number,
    size: number,
    alpha: number,
    i: number,
    name: string,
  ): void {
    if (!this.textures.exists(key)) return;
    const img = this.add
      .image(x, y, key)
      .setDisplaySize(size, size)
      .setAlpha(alpha)
      .setDepth(1.4)
      .setFlipX(x > 640)
      .setName(name);
    this.tweens.add({
      targets: img,
      y: y - 7,
      alpha: alpha * 0.7,
      duration: 1500 + i * 210,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * The Shadowing Temple (`decor: 'templeghosts'`): "lots of ghosts around the
   * map" — the event's Ghosts and Dark Ghosts on the walls and in the corners,
   * as see-through as on the track. Positions are `TEMPLE_GHOST_DECOR`.
   */
  private drawTempleGhostDecor(): void {
    TEMPLE_GHOST_DECOR.forEach(({ x, y, dark }, i) => {
      const type = dark ? 'darkghost' : 'ghost';
      this.haunt(`enemy-${type}`, x, y, 48, ENEMY_ALPHA[type] ?? 0.6, i, `decor-templeghost-${i}`);
    });
  }

  /** The Dark Forest (`decor: 'redphantoms'`): red Phantom Hambis lurking. */
  private drawRedPhantomDecor(): void {
    RED_PHANTOM_DECOR.forEach(({ x, y }, i) =>
      this.haunt('decor-redphantom', x, y, 60, 0.75, i, `decor-redphantom-${i}`),
    );
  }

  /**
   * The Quest for the Evil Orb (`decor: 'evilorb'`): the small dead tree in the
   * middle with the orb set in its fork — a sprite of its own, drawn OVER the
   * tree so no branch covers it, glowing — and the haunts round the edges.
   */
  private drawEvilOrbDecor(): void {
    const { tree, orb, haunts } = EVIL_ORB_DECOR;
    if (this.textures.exists('decor-orbtree')) {
      this.add
        .image(tree.x, tree.y, 'decor-orbtree')
        .setDisplaySize(tree.size, tree.size)
        .setDepth(1.4)
        .setName('decor-orbtree');
    }
    if (this.textures.exists('item-evilorb')) {
      const glow = this.add
        .circle(orb.x, orb.y, orb.size * 0.75, 0xff5a1f, 0.25)
        .setDepth(1.44)
        .setName('decor-evilorb-glow');
      this.tweens.add({
        targets: glow,
        scale: 1.25,
        alpha: 0.1,
        duration: 1100,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.evilOrbDecor = this.add
        .image(orb.x, orb.y, 'item-evilorb')
        .setDisplaySize(orb.size, orb.size)
        .setDepth(1.45)
        .setName('decor-evilorb');
      this.evilOrbDecor.setData('glow', glow);
    }
    haunts.forEach(({ x, y, kind }, i) => {
      if (kind === 'phantom') this.haunt('tower-phantom-1', x, y, 56, 0.6, i, `decor-haunt-${i}`);
      else this.haunt(`enemy-${kind}`, x, y, 46, ENEMY_ALPHA[kind] ?? 0.6, i, `decor-haunt-${i}`);
    });
  }

  /**
   * Beach decor (`decor: 'beach'`, Bun Beach): four hambis lounging on the
   * sand at the edges — Hambi Towers and Wise Hambis — clear of the trail and
   * the plots. Render-only.
   */
  /**
   * Heaven's Peak (spec 2026-10-12): the light blue-white orb sitting on the
   * clouds and the ring of sakura trees around it, inside the trail that
   * circles them (HEAVENS_PEAK_RING, pinned by originEvent.test).
   */
  private drawHeavensPeakDecor(): void {
    const { centre, treeRadius, trees } = HEAVENS_PEAK_RING;
    if (this.textures.exists('decor-sakura')) {
      // Back row first, so a lower tree overlaps the one behind it.
      const spots = Array.from({ length: trees }, (_, i) => {
        const a = (i / trees) * Math.PI * 2 - Math.PI / 2;
        return { x: centre.x + Math.cos(a) * treeRadius, y: centre.y + Math.sin(a) * treeRadius };
      }).sort((p, q) => p.y - q.y);
      for (const p of spots) {
        this.add
          .image(p.x, p.y - 14, 'decor-sakura')
          .setDisplaySize(72, 72)
          .setDepth(1.45 + p.y / 10000)
          .setName('decor-sakura');
      }
    }
    if (this.textures.exists('decor-heavenorb')) {
      const orb = this.add
        .image(centre.x, centre.y - 6, 'decor-heavenorb')
        .setDisplaySize(84, 84)
        .setDepth(1.46)
        .setName('decor-heavenorb');
      this.heavenOrbDecor = orb;
      // A slow glow: the orb breathes.
      this.tweens.add({
        targets: orb,
        alpha: { from: 1, to: 0.78 },
        duration: 1800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.inOut',
      });
    }
  }

  /**
   * Village of Veg's mushroom-roofed houses, in the middle of the loop the
   * route walks round. Drawn by the game, not baked into bg-villageofveg, so
   * they sit exactly where hubLevels.test checks they clear trail and plots.
   */
  private drawShroomVillageDecor(): void {
    if (!this.textures.exists('decor-shroomhouse')) return;
    for (const [x, y, size] of SHROOM_VILLAGE_HOUSES) {
      this.add.image(x, y, 'decor-shroomhouse').setDisplaySize(size, size).setDepth(1.45);
    }
  }

  /** Grave of the Rotten's mushroom graves beside the track. */
  private drawShroomGraves(): void {
    if (!this.textures.exists('decor-shroomgrave')) return;
    for (const [x, y, size] of SHROOM_GRAVES) {
      this.add.image(x, y, 'decor-shroomgrave').setDisplaySize(size, size).setDepth(1.45);
    }
  }

  /** Pathway of the Fungi's mossy golden statues, glowing golden mushrooms at their feet. */
  private drawGoldenStatues(): void {
    for (const [x, y, size] of GOLDEN_STATUES) {
      if (this.textures.exists('decor-goldstatue')) {
        this.add.image(x, y, 'decor-goldstatue').setDisplaySize(size, size).setDepth(1.45);
      }
      if (!this.textures.exists('decor-goldenmushroom')) continue;
      for (const dx of [-40, 40]) {
        const glow = this.add
          .image(x + dx, y + size / 2 - 10, 'decor-goldenmushroom')
          .setDisplaySize(34, 34)
          .setDepth(1.46);
        this.tweens.add({
          targets: glow,
          alpha: { from: 1, to: 0.7 },
          duration: 1400,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.inOut',
        });
      }
    }
  }

  /** Magical Mayhem's blue and orange glowing mushroom clusters. */
  private drawGlowClusters(): void {
    for (const [x, y, size, blue] of GLOW_CLUSTERS) {
      const key = blue ? 'decor-glowmushroom-blue' : 'decor-glowmushroom-orange';
      if (!this.textures.exists(key)) continue;
      const img = this.add.image(x, y, key).setDisplaySize(size, size).setDepth(1.45);
      this.tweens.add({
        targets: img,
        alpha: { from: 1, to: 0.75 },
        duration: 1600 + (x % 5) * 140,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.inOut',
      });
    }
  }

  /**
   * The Shroombellion's tree houses: rope bridges first (stretched and turned
   * to run from the giant house to each small one), then the houses on top.
   */
  private drawTreeHouses(): void {
    this.drawVines();
    const [big, ...small] = TREE_HOUSES;
    if (this.textures.exists('decor-ropebridge')) {
      for (const h of small) {
        const dx = h[0] - big[0];
        const dy = h[1] - big[1];
        const len = Math.hypot(dx, dy) - big[2] / 2 - h[2] / 2 + 20;
        const mid = {
          x: big[0] + (dx / Math.hypot(dx, dy)) * (big[2] / 2 - 10 + len / 2),
          y: big[1] + (dy / Math.hypot(dx, dy)) * (big[2] / 2 - 10 + len / 2),
        };
        this.add
          .image(mid.x, mid.y, 'decor-ropebridge')
          .setDisplaySize(len, 26)
          .setRotation(Math.atan2(dy, dx))
          .setDepth(1.44)
          .setName('decor-ropebridge');
      }
    }
    for (const [x, y, size] of TREE_HOUSES) {
      const key = size === big[2] ? 'decor-treehouse-big' : 'decor-treehouse-small';
      if (!this.textures.exists(key)) continue;
      this.add.image(x, y, key).setDisplaySize(size, size).setDepth(1.45).setName(key);
    }
  }

  /**
   * The Shroombellion's vines, hung from the top edge (spec 2026-10-29). Every
   * vine is drawn at the SAME scale and a short one is CROPPED, never squashed,
   * so they all read as the same plant. Under the plots, towers and enemies.
   */
  private drawVines(): void {
    if (!this.textures.exists('decor-vine')) return;
    const W = 120;
    const H = 400;
    for (const [x, len] of SHROOMBELLION_VINES) {
      const vine = this.add
        .image(x, 0, 'decor-vine')
        .setOrigin(0.5, 0)
        .setDisplaySize(W, H)
        .setDepth(1.44)
        .setName('decor-vine');
      if (len < H) vine.setCrop(0, 0, vine.frame.width, (vine.frame.height * len) / H);
      // A gentle sway from the top, out of step with its neighbours.
      vine.setRotation(-0.02);
      this.tweens.add({
        targets: vine,
        rotation: 0.02,
        duration: 2200 + (x % 7) * 180,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.inOut',
      });
    }
  }

  /** Mushy Mayhem's stone mushroom at the top of the clearing. */
  private drawMushyStatue(): void {
    if (!this.textures.exists('decor-mushystatue')) return;
    const [x, y, size] = MUSHY_STATUE;
    this.add.image(x, y, 'decor-mushystatue').setDisplaySize(size, size).setDepth(1.45);
  }

  private drawBeachDecor(): void {
    this.seatHambi(70, 250, 0, false, 'tower-patty-1', 56);
    this.seatHambi(70, 470, 0, false, 'tower-wise-1', 56);
    this.seatHambi(1010, 150, 0, false, 'tower-patty-1', 56);
    this.seatHambi(1040, 250, 0, false, 'tower-wise-1', 56);

    // THE BEACH BAR, drawn here rather than baked into bg-bunbeach. In the
    // background art it sat in the top-RIGHT corner, which the tower shop
    // panel (x >= 1070) covers completely — nobody ever saw it. Top-CENTRE at
    // y = 132 clears the top bar (y < 50) above it and the trail's y = 190 leg
    // below it, so it is visible for the whole run.
    if (this.textures.exists('decor-beachbar')) {
      // y 64..164: below the 50px top bar, above the trail's y = 190 leg
      // (whose top edge is 176). Both clearances are pinned by hubLevels.test.
      this.add.image(640, 114, 'decor-beachbar').setDisplaySize(140, 100).setDepth(1.45);
    }

    // BUNS spread across the sand beside the track and the plots, instead of
    // scattered round the outer rim where the art used to put them. Every one
    // is at least 40px off a trail leg and 60px off a plot, so nothing the
    // player has to click is sitting under a bun. None goes below y = 500
    // either: bg-bunbeach's sea starts around y = 595 and a bun bobbing in it
    // reads as a mistake. Nor may one land on a lounging hambi (the four
    // `seatHambi` calls above) — two of them did, and the pair read as one
    // muddled blob until the coordinates moved.
    const buns: [number, number, number][] = [
      [90, 120, 52],
      [330, 90, 48],
      [620, 90, 50],
      [900, 100, 50],
      [1040, 85, 46],
      [200, 240, 50],
      [475, 240, 52],
      [665, 245, 48],
      [950, 250, 50],
      [60, 350, 50],
      [475, 470, 46],
      [1040, 470, 46],
    ];
    for (const [x, y, size] of buns) {
      if (!this.textures.exists('decor-bun')) break;
      this.add
        .image(x, y, 'decor-bun')
        .setDisplaySize(size, size)
        .setDepth(1.4)
        .setRotation(((x * 7 + y * 3) % 360) * (Math.PI / 180));
    }
  }

  /**
   * A level-start notice in the dev-message banner's dress — dark panel,
   * gold border, gold sender, pale text, the banner's own font — drawn in
   * the canvas with a portrait on the left. Under the top bar, clear of the
   * HUD panel. Taps dismiss it; otherwise it goes on its own.
   */
  private showLevelNotice(art: string, sender: string, text: string): void {
    const D = 45;
    const cx = 535;
    const cy = 100;
    const w = 860;
    const h = 78;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const panel = this.add
      .rectangle(cx, cy, w, h, 0x15131f, 0.95)
      .setStrokeStyle(2, 0xffd23f)
      .setDepth(D)
      .setName('level-notice')
      .setInteractive({ useHandCursor: true });
    objs.push(panel);
    if (this.textures.exists(art)) {
      objs.push(
        this.add
          .image(cx - w / 2 + 44, cy, art)
          .setDisplaySize(56, 56)
          .setDepth(D + 1),
      );
    }
    const font = 'system-ui, sans-serif';
    const who = this.add
      .text(cx - w / 2 + 84, cy, `${sender}:`, {
        fontFamily: font,
        fontSize: '20px',
        fontStyle: 'bold',
        color: '#ffd23f',
      })
      .setOrigin(0, 0.5)
      .setDepth(D + 1);
    objs.push(who);
    objs.push(
      this.add
        .text(who.x + who.width + 8, cy, text, {
          fontFamily: font,
          fontSize: '20px',
          fontStyle: 'bold',
          color: '#f4efe6',
          wordWrap: { width: w - 84 - who.width - 30 },
        })
        .setOrigin(0, 0.5)
        .setDepth(D + 1)
        .setName('level-notice-text'),
    );
    let gone = false;
    const dismiss = () => {
      if (gone) return;
      gone = true;
      this.tweens.add({
        targets: objs,
        alpha: 0,
        duration: 300,
        onComplete: () => objs.forEach((o) => o.destroy()),
      });
    };
    panel.on(
      'pointerdown',
      (_p: unknown, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
        ev.stopPropagation();
        dismiss();
      },
    );
    this.time.delayedCall(NOTICE_MS, dismiss);
    this.dismissNotice = dismiss;
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      gone = true;
    });
  }

  /** Chiply's taunt over the loss screen — his leak is the whole run. */
  private showWhompNotice(depth: number): void {
    const cx = 640;
    // Under the RETRY / LEVELS buttons — the top of the screen belongs to the
    // achievement toasts a first loss raises.
    const cy = 585;
    const w = 980;
    const h = 70;
    this.add
      .rectangle(cx, cy, w, h, 0x15131f, 0.95)
      .setStrokeStyle(2, 0xff4444)
      .setDepth(depth)
      .setName('whomp-notice');
    if (this.textures.exists('enemy-chiply')) {
      this.add
        .image(cx - w / 2 + 40, cy, 'enemy-chiply')
        .setDisplaySize(52, 52)
        .setDepth(depth + 1);
    }
    this.add
      .text(cx + 20, cy, 'Whomp, Whomp, chiply got you, try harder next time.', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        fontStyle: 'bold',
        color: '#f4efe6',
      })
      .setOrigin(0.5)
      .setDepth(depth + 1);
  }

  /**
   * Crystal decor (`decor: 'crystals'`, Amethyst Core): procedural amethyst
   * piles hugging both shoulders of the track, plus a ring of crystals around
   * every tower plot. Deterministic — shapes derive from a position hash, so
   * every visit to the level looks identical. Pure Graphics, render-only.
   */
  private drawCrystalDecor(gfx: Phaser.GameObjects.Graphics): void {
    const SHOULDER = TRAIL_WIDTH / 2 + 12; // pile centres just off the trail edge
    const TRACK_SPACING = 72; // px between piles along a segment
    const PLOT_RING_R = 46; // ring radius around each plot (tile is 64px)
    const HUES = [0x7a3fa8, 0xa06ce0, 0xc9a0f0]; // deep / mid / pale amethyst

    // Tiny deterministic hash → [0, 1). Seeded purely by position and index.
    const rand = (a: number, b: number, c: number): number => {
      const s = Math.sin(a * 12.9898 + b * 78.233 + c * 37.719) * 43758.5453;
      return s - Math.floor(s);
    };

    // One pile: 2–3 faceted shards leaning out of a shared base.
    const pile = (x: number, y: number, seed: number): void => {
      const shards = 2 + Math.floor(rand(x, y, seed) * 2);
      for (let s = 0; s < shards; s++) {
        const h = 8 + rand(x, y, seed + s + 1) * 10; // shard height
        const w = 4 + rand(x, y, seed + s + 5) * 4; // shard half-width
        const ox = (rand(x, y, seed + s + 9) - 0.5) * 14;
        const lean = (rand(x, y, seed + s + 13) - 0.5) * 8;
        const hue = HUES[Math.floor(rand(x, y, seed + s + 17) * HUES.length)];
        gfx.fillStyle(hue, 1);
        gfx.fillTriangle(x + ox - w, y, x + ox + w, y, x + ox + lean, y - h);
        // A pale facet line up the shard's lit side.
        gfx.fillStyle(0xe6ccff, 0.55);
        gfx.fillTriangle(x + ox - w * 0.3, y, x + ox + w * 0.3, y, x + ox + lean, y - h);
      }
    };

    // Piles along both shoulders of every path segment, staggered so the two
    // sides don't mirror each other.
    const waypoints = this.level.path;
    for (let i = 1; i < waypoints.length; i++) {
      const a = waypoints[i - 1];
      const b = waypoints[i];
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      const ux = (b.x - a.x) / len;
      const uy = (b.y - a.y) / len;
      for (const side of [-1, 1]) {
        const start = TRACK_SPACING * (side === -1 ? 0.5 : 1);
        for (let t = start; t <= len - TRACK_SPACING / 3; t += TRACK_SPACING) {
          const x = a.x + ux * t + -uy * side * SHOULDER;
          const y = a.y + uy * t + ux * side * SHOULDER;
          // Keep piles on the visible playfield (clear of the HUD / header).
          if (x < 16 || x > 1056 || y < 56 || y > 704) continue;
          pile(Math.round(x), Math.round(y), i * 31 + side);
        }
      }
    }

    // A ring of crystals surrounding every tower plot.
    for (const plot of this.level.plots) {
      const n = 5 + Math.floor(rand(plot.pos.x, plot.pos.y, 3) * 2); // 5–6 piles
      const phase = rand(plot.pos.x, plot.pos.y, 7) * Math.PI * 2;
      for (let k = 0; k < n; k++) {
        const ang = phase + (k / n) * Math.PI * 2;
        const x = plot.pos.x + Math.cos(ang) * PLOT_RING_R;
        const y = plot.pos.y + Math.sin(ang) * PLOT_RING_R;
        if (x < 16 || x > 1056 || y < 56 || y > 704) continue;
        pile(Math.round(x), Math.round(y), plot.id * 13 + k);
      }
    }
  }

  /**
   * Procedural bomb explosion: an expanding, fading flash + shock ring — the
   * bomb's orange unless a palette is given (Demonic Hambi's dark purple).
   */
  private spawnExplosion(
    x: number,
    y: number,
    radius: number,
    palette: { flash: number; ring: number } = BOMB_BLAST,
  ): void {
    const flash = this.add.circle(x, y, radius * 0.4, palette.flash, 0.9).setDepth(3.6);
    const ring = this.add.circle(x, y, radius * 0.3, palette.ring, 0.55).setDepth(3.55);
    this.tweens.add({
      targets: flash,
      scale: 2.6,
      alpha: 0,
      duration: 320,
      onComplete: () => flash.destroy(),
    });
    this.tweens.add({
      targets: ring,
      scale: 3.4,
      alpha: 0,
      duration: 420,
      ease: 'Quad.easeOut',
      onComplete: () => ring.destroy(),
    });
  }

  /**
   * Fireball impact: a blue-white flash and an expanding azure shock ring where
   * King Ember's ball came down. Same shape as the bomb explosion, in the
   * boss's cold-flame palette so the two never read as the same event.
   */
  private spawnFireballBurst(x: number, y: number): void {
    const flash = this.add.circle(x, y, 18, 0xdff3ff, 0.95).setDepth(4.6);
    const ring = this.add.circle(x, y, 14, 0x2f8fe6, 0.6).setDepth(4.55);
    this.tweens.add({
      targets: flash,
      scale: 2.2,
      alpha: 0,
      duration: 280,
      onComplete: () => flash.destroy(),
    });
    this.tweens.add({
      targets: ring,
      scale: 3.2,
      alpha: 0,
      duration: 400,
      ease: 'Quad.easeOut',
      onComplete: () => ring.destroy(),
    });
  }

  /**
   * Victory confetti (`LevelDef.winConfetti` — the campaign finale only).
   * Multi-coloured paper rectangles fall from above the top edge with random
   * size, spin, drift and stagger, so the stream keeps going for the whole
   * celebration rather than dropping as one synchronised curtain.
   */
  private rainConfetti(depth: number): void {
    const COLORS = [0xff4d6d, 0xffd23f, 0x3ddc4a, 0x4fb8ff, 0xb066ff, 0xff9f1c, 0xffffff];
    const PIECES = 140;
    for (let i = 0; i < PIECES; i++) {
      const x = Math.random() * 1280;
      const w = 6 + Math.random() * 8;
      const h = 10 + Math.random() * 8;
      const piece = this.add
        .rectangle(x, -20 - Math.random() * 260, w, h, COLORS[i % COLORS.length])
        .setDepth(depth)
        .setAngle(Math.random() * 360);
      this.tweens.add({
        targets: piece,
        y: 780,
        x: x + (Math.random() * 200 - 100),
        angle: piece.angle + (Math.random() * 720 - 360),
        duration: 2600 + Math.random() * 2600,
        delay: Math.random() * 2200,
        ease: 'Sine.easeIn',
        onComplete: () => piece.destroy(),
      });
    }
  }

  /**
   * Pop bubble text over the enemy a shot just beat the baseline on. It floats
   * up and fades over 700ms, then destroys itself — a normal shot shows
   * nothing, because at one shot a second a label on every projectile would be
   * constant noise and the whole point of the cue is to mark the better rolls.
   *
   * The wording follows the TOWER, not the band: the Gunner's two bands read
   * CRITICAL and HEADSHOT, while the Fire Mage's single band is its blue
   * fireball and reads BLUE FLAME in the same blue as the projectile. Bow
   * Hambi's poison arrow reads POISON! in dark green.
   *
   * Depth 5 puts it above enemies (4). The heavy dark stroke is what keeps it
   * readable over a bright sprite or a busy background.
   */
  /**
   * "DODGE" over a dodging enemy's head (The Mushy). The hit labels' rise and
   * clamp, in white on brown so it reads apart from a crit.
   */
  private spawnDodgeLabel(
    x: number,
    y: number,
    text = 'DODGE',
    name = 'dodge-label',
    stroke = '#5a3418',
  ): void {
    const label = this.add
      .text(x, y, text, {
        fontFamily: 'monospace',
        fontSize: text.length > 6 ? '16px' : '22px',
        fontStyle: 'bold',
        color: '#ffffff',
        stroke,
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setDepth(5)
      .setName(name);
    const halfW = label.width / 2;
    label.x = Phaser.Math.Clamp(x, PLAYFIELD_X0 + halfW, PLAYFIELD_X1 - halfW);
    label.y = Math.max(label.y, PLAYFIELD_Y0 + label.height / 2 + HIT_LABEL_RISE);
    this.tweens.add({
      targets: label,
      y: label.y - HIT_LABEL_RISE,
      alpha: 0,
      duration: 700,
      ease: 'Quad.easeOut',
      onComplete: () => label.destroy(),
    });
  }

  /**
   * A shroomnitized enemy's strike (spec 2026-10-24): a snowball flies from it
   * to the enemy it hit. Theatre only — the sim already dealt the damage.
   */
  private flyStrike(from: { x: number; y: number }, to: { x: number; y: number }): void {
    const ball = this.add
      .image(from.x, from.y, 'proj-snowball')
      .setDisplaySize(26, 26)
      .setDepth(5)
      .setName('shroomnitize-strike');
    this.tweens.add({
      targets: ball,
      x: to.x,
      y: to.y,
      duration: 260,
      ease: 'Quad.easeIn',
      onComplete: () => ball.destroy(),
    });
  }

  private spawnHitLabel(
    x: number,
    y: number,
    kind: 'crit' | 'headshot' | 'poison',
    towerType: TowerType,
  ): void {
    const headshot = kind === 'headshot';
    const poison = kind === 'poison';
    const blueFlame = towerType === 'firemage';
    const text = poison ? 'POISON!' : blueFlame ? 'BLUE FLAME' : headshot ? 'HEADSHOT' : 'CRITICAL';
    const color = poison ? '#1f8a2e' : blueFlame ? '#4db8ff' : headshot ? '#b066ff' : '#ff3b30';
    const label = this.add
      .text(x, y - 18, text, {
        fontFamily: 'monospace',
        fontSize: headshot ? '22px' : '20px',
        fontStyle: 'bold',
        color,
        stroke: poison ? '#06140a' : '#1a0d24',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setDepth(5);
    // Keep the whole word on screen. Enemies enter at the very edge of the map,
    // so a centred label on a kill near the spawn would otherwise be sliced in
    // half by the canvas — and the float-up has to clear the HUD bar too.
    const halfW = label.width / 2;
    label.x = Phaser.Math.Clamp(x, PLAYFIELD_X0 + halfW, PLAYFIELD_X1 - halfW);
    label.y = Math.max(label.y, PLAYFIELD_Y0 + label.height / 2 + HIT_LABEL_RISE);
    this.tweens.add({
      targets: label,
      y: label.y - HIT_LABEL_RISE,
      alpha: 0,
      duration: 700,
      ease: 'Quad.easeOut',
      onComplete: () => label.destroy(),
    });
  }

  /**
   * Healer Hambi's pulse: a green plus rises off every tower that gained HP
   * and fades. Looked up by id on the live state; a tower sold the same tick
   * simply shows nothing.
   */
  private showTowerHeals(ids: number[]): void {
    if (!this.textures.exists('fx-heal')) return;
    for (const id of ids) {
      const t = this.sim.state.towers.find((o) => o.id === id);
      if (!t) continue;
      const plus = this.add
        .image(t.pos.x, t.pos.y - 18, 'fx-heal')
        .setDisplaySize(28, 28)
        .setDepth(5)
        .setName('tower-heal-fx');
      this.tweens.add({
        targets: plus,
        y: plus.y - 34,
        alpha: 0,
        duration: 900,
        ease: 'Quad.easeOut',
        onComplete: () => plus.destroy(),
      });
    }
  }

  /** Banking Burger's bonus: "+$200" in banknote green, floating off the kill. */
  private showBankerCash(pos: { x: number; y: number }, cash: number): void {
    const label = this.add
      .text(pos.x, pos.y - 26, `+$${cash}`, {
        fontFamily: 'monospace',
        fontSize: '18px',
        fontStyle: 'bold',
        color: '#7dff8a',
        stroke: '#0c2410',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setDepth(5)
      .setName('banker-cash');
    const halfW = label.width / 2;
    label.x = Phaser.Math.Clamp(pos.x, PLAYFIELD_X0 + halfW, PLAYFIELD_X1 - halfW);
    label.y = Math.max(label.y, PLAYFIELD_Y0 + label.height / 2 + HIT_LABEL_RISE);
    this.tweens.add({
      targets: label,
      y: label.y - HIT_LABEL_RISE,
      alpha: 0,
      duration: 800,
      ease: 'Quad.easeOut',
      onComplete: () => label.destroy(),
    });
  }

  /** Drop a transient ground decal that fades out over ~1s. */
  private spawnDecal(key: string, x: number, y: number): void {
    const decal = this.add.image(x, y, key).setDepth(1.5).setAlpha(0.8);
    this.tweens.add({
      targets: decal,
      alpha: 0,
      duration: 1000,
      onComplete: () => decal.destroy(),
    });
  }

  // ---------------------------------------------------------------------------
  // Game-over handling
  // ---------------------------------------------------------------------------

  /**
   * Show the result overlay once when `sim.isOver` is first detected.
   *
   * Responsibilities (Task 28):
   *  - Dimming full-screen rectangle + title + final score text.
   *  - Retry button (scene.restart same levelId) and Menu button (stop HudScene, start MenuScene).
   *  - Score submission via LocalScoreService (fire-and-forget, try/catch).
   *  - Level unlock persistence on win (key: 'hambi.unlocked').
   *
   * Guarded by `gameOverShown` so the entire block fires exactly once even
   * though `update()` continues calling this on every subsequent frame.
   */
  private handleGameOver(): void {
    if (this.gameOverShown) return;
    this.gameOverShown = true;

    // The run may have ended inside the step loop with its last events still
    // queued (the leak that took the final life among them) — read them now,
    // so a Chiply loss knows itself before the screen is drawn.
    for (const e of this.sim.drainEvents()) {
      if (e.type === 'enemyLeaked') this.lastLeak = e.enemyType;
    }

    // Hide the HUD so the dim overlay + result dialog are clean and on top.
    this.scene.stop('HudScene');
    this.dismissNotice?.();

    // An arena loss gets its own compact overlay: no stars/score theatre, a
    // defeated/spawned scoreboard, and a TRY AGAIN that keeps the towers.
    if (this.level.sandbox === true && this.sim.state.phase !== 'won') {
      this.showArenaLossOverlay();
      return;
    }

    const isDemo = this.demoOutcome !== null;
    // A sandbox run (the Test Arena) banks nothing either — no score, stars,
    // coins or tower grants. Same treatment the demo screens get.
    const noPersist = isDemo || this.level.sandbox === true;
    const won = this.sim.state.phase === 'won';
    const score = this.sim.state.score;
    const stars = starsFor(this.sim.state.lives, this.level.startLives, won);
    const isLastLevel = this.level.id === LEVELS[LEVELS.length - 1].id;

    // FAILURE: losing a campaign episode ("The Picnic Fell!"). The arena's
    // loss took the early return above, and demo/sandbox runs bank nothing.
    // Ten lifetime losses pay out "I Trusted you!".
    if (!won && !noPersist) {
      this.awardAch('failure');
      try {
        if (this.statsSvc.incLosses() >= 10) this.awardAch('itrustedyou');
      } catch (err) {
        console.warn('[GameScene] loss counter failed', err);
      }
      // Chiply's Anger Issues: he took the whole base in one bite.
      if (this.lastLeak === 'chiply') this.awardAch('angerissues');
    }

    // ── Score submission ─────────────────────────────────────────────────────
    // Fire-and-forget; LocalScoreService is synchronous under the hood but the
    // interface is async, so we void the promise and catch storage failures.
    // Skipped in demo/review and sandbox mode — nothing there is persisted.
    // A Peaceful run is not scored either: nothing can be lost, so BEST would
    // be a walk-through number on the Normal track's board.
    if (!noPersist && !this._peaceful) {
      try {
        const svc = new LocalScoreService(saveStorage());
        void svc
          .submit({ levelId: this.level.id, score, at: Date.now() })
          .catch((err: unknown) => console.warn('[GameScene] Score submit failed', err));
      } catch (err) {
        console.warn('[GameScene] Score submit failed', err);
      }
    }

    // ── Star rating + level unlock ───────────────────────────────────────────
    // Stars come from lives survived; clearing with ≥ REQUIRED_STARS_TO_ADVANCE
    // unlocks the next level. Records on win only (a loss earns 0 stars).
    let advanced = false;
    let coinsEarned = 0;
    let coinsTotal = 0;
    let grantedNow: TowerType[] = [];
    /**
     * True when THIS win claimed the Variety Orb — the cue for the claim
     * notification, and only that. The cutscene is gated on winning the finale,
     * not on the prize, so a replay still gets the ceremony.
     */
    let orbClaimed = false;
    /** True when THIS win claimed the Diamond (HTD Exhibition's first win). */
    let diamondClaimed = false;
    let moonstoneClaimed = false;
    let debrisClaimed = false;
    const hubLevel = isHubLevelId(this.level.id);
    const originLevel = isOriginLevelId(this.level.id);
    let darkCoinsEarned = 0;
    let darkCoinsTotal = 0;
    let darkShardClaimed = false;
    let evilOrbClaimed = false;
    let lightOrbClaimed = false;
    let tokenClaimed = false;
    let chestWon: ChestKind | undefined;
    // Above the Clouds pays Light Hambi Coins instead (spec 2026-10-08).
    const cloudsLevel = originChapterOf(this.level.id)?.id === 'clouds';
    let lightCoinsEarned = 0;
    let lightCoinsTotal = 0;
    let lightShardClaimed = false;
    const fungiItemsWon: FungiItemKind[] = [];
    if (won && noPersist) {
      // Review mode: show the same overlay/animation with preview values, but
      // persist nothing (no stars, unlocks, or coins written).
      coinsEarned = this.level.coinReward;
      coinsTotal = coinsEarned;
      advanced = stars >= REQUIRED_STARS_TO_ADVANCE && !isLastLevel;
      grantedNow = towersGrantedOnWin(this.level.id); // preview only — nothing persisted
      darkCoinsEarned = darkCoinRewardFor(this.level.id);
      darkCoinsTotal = darkCoinsEarned;
      lightCoinsEarned = lightCoinRewardFor(this.level.id);
      lightCoinsTotal = lightCoinsEarned;
    } else if (won && originLevel) {
      // An Origin Event level (spec 2026-10-06): its stars only — no cursor —
      // and Dark Hambi Coins on EVERY win. No coins and no rank EXP: the
      // event currency is the whole reward.
      try {
        const progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
        // Read BEFORE recording: the Evil Orb's quest pays 100 on its first
        // win and 60 after, and the orb itself only the first time.
        const firstWin = progress.stars(this.level.id) === 0;
        advanced = progress.recordOriginResult(this.level.id, stars).advanced;
        recordStat('clears');
        if (cloudsLevel) {
          // Heaven's Peak pays 100 on its first win and 60 after.
          lightCoinsEarned = lightCoinRewardFor(this.level.id, firstWin);
          lightCoinsTotal = progress.addLightCoins(lightCoinsEarned);
        } else {
          darkCoinsEarned = darkCoinRewardFor(this.level.id, firstWin);
          darkCoinsTotal = progress.addDarkCoins(darkCoinsEarned);
        }
        // The Sky Islands pays a Light Shard on EVERY win.
        if (this.level.id === LIGHT_SHARD_LEVEL_ID) {
          progress.awardLightShard();
          lightShardClaimed = true;
          recordStat('obtains');
        }
        // The Dark Forest pays a Dark Shard on EVERY win.
        if (this.level.id === DARK_SHARD_LEVEL_ID) {
          progress.awardDarkShard();
          darkShardClaimed = true;
          recordStat('obtains');
        }
        if (this.level.id === EVIL_ORB_LEVEL_ID && firstWin) {
          evilOrbClaimed = progress.claimEvilOrb();
          if (evilOrbClaimed) recordStat('obtains');
        }
        // The Light Orb (spec 2026-10-13): on ANY win of Heaven's Peak while it
        // has never been held — Heaven's Peak shipped before the orb did, so a
        // first-win gate would leave everyone who had already won it without.
        if (this.level.id === HEAVENS_PEAK_LEVEL_ID) {
          lightOrbClaimed = progress.claimLightOrb();
          if (lightOrbClaimed) recordStat('obtains');
        }
        // The chapter's chest: 25% on every win (render-side Math.random,
        // never the sim's seeded Rng).
        if (rollChestDrop(chestRandom())) {
          chestWon = cloudsLevel ? 'light' : 'dark';
          progress.awardChest(chestWon);
          recordStat('obtains');
        }
        sweepAndToastAchievements(saveStorage());
      } catch (err) {
        console.warn('[GameScene] Origin progress persist failed', err);
      }
    } else if (won && hubLevel) {
      // A hub level: its own cursor (`hambi.hub`), never the campaign's. Coins
      // and rank exp pay as a Chapter 3 episode does; no win-grant towers.
      try {
        const progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
        const peaceful = this._peaceful;
        const prevStars = progress.hubStars(this.level.id, peaceful);
        advanced = progress.recordHubResult(this.level.id, stars, peaceful).advanced;
        // Quests: a Moon clear (any win, replays included; Peaceful too).
        // Recorded BEFORE the coins so a fresh period baselines first.
        recordStat('clears');
        // `this.level.coinReward` is already the docked figure on a Peaceful run.
        const newTotal = progress.addCoins(this.level.coinReward);
        coinsEarned = this.level.coinReward;
        coinsTotal = newTotal;
        // Rank exp, the Diamond and the achievements are Normal-track rewards:
        // a Peaceful run cannot be lost, so it must not pay the prizes for
        // beating the level. Peaceful banks its coins and its own track only.
        if (!peaceful) {
          if (prevStars === 0) {
            const gain = expRewardFor(this.level);
            if (gain > 0) this.rankSvc.addExp(gain);
          }
          this.awardAch('stageness');
          // The Diamond: the exhibition's prize, once ever — same shape as the
          // orb, so a save that beat it before the diamond existed still gets it.
          if (this.level.id === DIAMOND_LEVEL_ID) diamondClaimed = progress.claimDiamond();
          if (diamondClaimed) recordStat('obtains');
          // The Moon Stone: the chapter finale's prize, once ever — the
          // Diamond's rule exactly, so a save that beat the level before the
          // stone existed still gets it on a replay.
          if (this.level.id === MOONSTONE_LEVEL_ID) moonstoneClaimed = progress.claimMoonstone();
          if (moonstoneClaimed) recordStat('obtains');
          // Space Debris: Rocket Rage pays one for EVERY win, not just the
          // first — the brief's words. Normal track only, like the Diamond.
          if (this.level.id === DEBRIS_LEVEL_ID) {
            progress.awardDebris();
            debrisClaimed = true;
            recordStat('obtains');
          }
          // The Chip Society: The Space Station is beaten (Normal track only —
          // a Peaceful run cannot lose, so it cannot earn the gold).
          if (this.level.id === 105) this.awardAch('chipsociety');
          // Win-grant towers (spec 2026-10-27): the Golden Shroom on Pathway
          // of the Fungi. Return-once, so only the first such win announces it.
          for (const t of towersGrantedOnWin(this.level.id)) {
            if (progress.grantTower(t)) {
              grantedNow.push(t);
              recordStat('obtains');
            }
          }
          // The Token of Shroom: The Shroombellion's FIRST win, like the Evil Orb.
          if (this.level.id === SHROOMBELLION_LEVEL_ID && prevStars === 0) {
            tokenClaimed = progress.claimTokenOfShroom();
            if (tokenClaimed) recordStat('obtains');
          }
          // The Fungi items (spec 2026-10-24): 25% each, one roll per item,
          // from Mushy Mayhem on (the Snowshroom from Icy Photosynthesis only).
          for (const kind of fungiItemDrops(this.level.id, chestRandom(), chestRandom())) {
            progress.awardFungiItem(kind);
            fungiItemsWon.push(kind);
            recordStat('obtains');
          }
        }
        sweepAndToastAchievements(saveStorage());
      } catch (err) {
        console.warn('[GameScene] Hub progress persist failed', err);
      }
    } else if (won) {
      try {
        const progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
        // Exp pays on the FIRST win only: stars absent/0 → ≥1. Read before
        // recordResult moves it. Replaying for a better rating pays nothing.
        const prevStars = progress.stars(this.level.id);
        advanced = progress.recordResult(this.level.id, stars).advanced;
        // Persist first; only mark coins as "earned" once addCoins succeeds, so a
        // storage failure leaves both at 0 and the pill block is skipped (never a
        // negative startTotal).
        // Quests: a campaign clear (any win, replays included), recorded
        // BEFORE the coins so a fresh period baselines first.
        recordStat('clears');
        const newTotal = progress.addCoins(this.level.coinReward);
        coinsEarned = this.level.coinReward;
        coinsTotal = newTotal;
        for (const t of towersGrantedOnWin(this.level.id)) {
          if (progress.grantTower(t)) {
            grantedNow.push(t);
            recordStat('obtains');
          }
        }
        // Rank exp for a first-time completion (ch2 = 100, ch3 = 150, ch1 = 0).
        if (prevStars === 0) {
          const gain = expRewardFor(this.level);
          if (gain > 0) this.rankSvc.addExp(gain);
        }
        // Stageness on any campaign win (idempotent — only the first pays);
        // Very Bossy for felling the Golden Nugget at the Chapter 1 finale.
        // Batch 2: the four boss-level completions, then a state sweep so
        // coin-threshold awards (Richness/Mansion) land with the win reward.
        this.awardAch('stageness');
        if (this.level.id === 5) this.awardAch('verybossy');
        if (this.level.id === 10) this.awardAch('null');
        if (this.level.id === 15) this.awardAch('theveg');
        if (this.level.id === 20) this.awardAch('spicy');
        if (this.level.id === 25) this.awardAch('kingdown');
        // The Variety Orb: the campaign finale's prize, once ever. Gated on the
        // orb never having been claimed rather than on `prevStars === 0`, so
        // players who finished the campaign before the orb existed still get it
        // — on their next win of 25 — instead of never. `claimOrb` returns
        // false on a replay, so this is safe to run on every win of the level.
        if (isLastLevel) orbClaimed = progress.claimOrb();
        if (orbClaimed) recordStat('obtains');
        sweepAndToastAchievements(saveStorage());
      } catch (err) {
        console.warn('[GameScene] Progress persist failed', err);
      }
    }

    const overlayArgs: ResultOverlayArgs = {
      isDemo,
      won,
      score,
      stars,
      isLastLevel,
      advanced,
      coinsEarned,
      coinsTotal,
      grantedNow,
      orbClaimed,
      diamondClaimed,
      moonstoneClaimed,
      debrisClaimed,
      darkCoinsEarned,
      darkCoinsTotal,
      darkShardClaimed,
      evilOrbClaimed,
      lightOrbClaimed,
      tokenClaimed,
      chestWon,
      lightCoinsEarned,
      lightCoinsTotal,
      lightShardClaimed,
      fungiItemsWon,
    };

    // The Variety Orb cutscene plays BEFORE the result screen and builds it at
    // the white peak. Everything above has already been persisted, so a player
    // who closes the tab mid-cutscene keeps whatever the win banked.
    //
    // It runs on EVERY real win of the finale, not only the one that claims the
    // orb: the ten seconds in the meadow are the level's send-off, and beating
    // *The Ember and Flame* again should replay it. `orbClaimed` stays false on
    // those replays, so the ceremony repeats while the prize does not — the
    // cutscene ends in the ordinary Level Complete screen with no claim modal.
    //
    // `noPersist` keeps it out of the ?debug result previews and the sandbox
    // arena, which bank nothing and are not the player beating the level; the
    // debug page plays the cutscene from its own button instead.
    if (won && isLastLevel && !noPersist) {
      this.cancelOrbCutscene = playVarietyOrbCutscene(this, () =>
        this.showResultOverlay(overlayArgs),
      );
    } else if (won && this.level.id === MOONSTONE_LEVEL_ID && !noPersist) {
      // The Moon's send-off. Like the orb's it runs on EVERY real win of the
      // finale, not just the one that claims the stone: the ten seconds are
      // the level's send-off. It uses the same cancel slot, so a player who
      // leaves mid-cutscene leaves no orphan tweens behind.
      this.cancelOrbCutscene = playPrinceNachoCutscene(this, () =>
        this.showResultOverlay(overlayArgs),
      );
    } else if (won && this.level.id === EVIL_ORB_LEVEL_ID && !noPersist) {
      // The Evil Orb's send-off, on EVERY real win (the user's call): the orb
      // floats out of the dead tree and bursts into white. Same cancel slot.
      const orb = this.evilOrbDecor;
      const glow = orb?.getData('glow') as Phaser.GameObjects.Arc | undefined;
      // It plays ON the board, not behind a cover: clear any enemy sprite the
      // last frame left up (the Reaper falls on the tick the level ends) and
      // his bar.
      for (const v of this.enemyViews.values()) v.destroy();
      this.enemyViews.clear();
      this.syncBossBar({ ...this.sim.state, enemies: [] });
      this.cancelOrbCutscene = playEvilOrbCutscene(
        this,
        { ...EVIL_ORB_DECOR.orb },
        () => {
          orb?.setVisible(false);
          glow?.setVisible(false);
        },
        () => this.showResultOverlay(overlayArgs),
      );
    } else if (won && this.level.id === SHROOMBELLION_LEVEL_ID && !noPersist) {
      // The Fungi Forest's send-off (spec 2026-10-27), on EVERY real win:
      // Ninja Hambi's chase and his leap at King Shroometron. It covers the
      // board, but clear the King's sprite and bar first all the same.
      for (const v of this.enemyViews.values()) v.destroy();
      this.enemyViews.clear();
      this.syncBossBar({ ...this.sim.state, enemies: [] });
      this.cancelOrbCutscene = playShroometronCutscene(this, () =>
        this.showResultOverlay(overlayArgs),
      );
    } else if (won && this.level.id === HEAVENS_PEAK_LEVEL_ID && !noPersist) {
      // The Light Orb's send-off (spec 2026-10-13), on EVERY real win like the
      // Evil Orb's: the orb in the sakura ring floats up into the sky and
      // bursts into white. Lord Turtleshell falls on the last tick, so clear
      // his sprite and bar first.
      const orb = this.heavenOrbDecor;
      for (const v of this.enemyViews.values()) v.destroy();
      this.enemyViews.clear();
      this.syncBossBar({ ...this.sim.state, enemies: [] });
      const { centre } = HEAVENS_PEAK_RING;
      this.cancelOrbCutscene = playOrbCutscene(
        this,
        LIGHT_ORB_LOOK,
        { x: centre.x, y: centre.y - 6, size: 84 },
        () => {
          if (orb) {
            this.tweens.killTweensOf(orb);
            orb.setVisible(false);
          }
        },
        () => this.showResultOverlay(overlayArgs),
      );
    } else {
      this.showResultOverlay(overlayArgs);
    }
  }

  /**
   * Draw the Level Complete / The Picnic Fell result screen.
   *
   * Split out of `handleGameOver` so the Variety Orb cutscene can run first
   * and call this at its white peak: the ten seconds of theatre happen
   * between the persistence above and the screen below, with nothing else
   * reordered. Every value it needs was computed there and is passed in,
   * because it must show what was actually banked.
   */
  private showResultOverlay(a: ResultOverlayArgs): void {
    const { isDemo, won, score, stars, isLastLevel, advanced } = a;
    const { coinsEarned, coinsTotal, grantedNow, orbClaimed } = a;
    // ── Canvas dimensions ────────────────────────────────────────────────────
    const W = 1280;
    const H = 720;
    const OVERLAY_DEPTH = 50; // above all GameScene layers (deepest was 10)

    // ── Dimming rectangle ────────────────────────────────────────────────────
    this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.65).setDepth(OVERLAY_DEPTH);

    // ── Confetti (the campaign finale, on a win) ─────────────────────────────
    // Above the dim layer but below the dialog text, so it falls behind the
    // result copy rather than over the top of it.
    if (won && this.level.winConfetti) this.rainConfetti(OVERLAY_DEPTH + 0.5);

    // ── Title ────────────────────────────────────────────────────────────────
    // A Chiply loss has its own title; every other loss is The Picnic Fell.
    const titleMsg = won
      ? 'Level Complete!'
      : this.lastLeak === 'chiply'
        ? 'Chiply was very mad!'
        : 'The Picnic Fell!';
    this.add
      .text(W / 2, H / 2 - 130, titleMsg, {
        fontSize: '56px',
        color: won ? '#f5c518' : '#ff4444',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(OVERLAY_DEPTH + 1)
      // Named so a test can say "the result screen is up" — the finale's replay
      // path has no other marker, the orb having already been claimed.
      .setName('result-title');

    // A Chiply leak: he took every life at a stroke, and he says so.
    if (!won && this.lastLeak === 'chiply') this.showWhompNotice(OVERLAY_DEPTH + 1);

    // ── Star rating ───────────────────────────────────────────────────────────
    // Filled gold ★ for earned stars, hollow ☆ for the rest (of 3).
    this.add
      .text(W / 2, H / 2 - 58, '★'.repeat(stars) + '☆'.repeat(3 - stars), {
        fontSize: '46px',
        color: '#f5c518',
      })
      .setOrigin(0.5)
      .setDepth(OVERLAY_DEPTH + 1);

    // ── Final score ───────────────────────────────────────────────────────────
    this.add
      .text(W / 2, H / 2 - 2, `Score: ${score}`, {
        fontSize: '34px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setDepth(OVERLAY_DEPTH + 1);

    // ── Gate message ──────────────────────────────────────────────────────────
    // Tells the player whether they cleared the advancement bar (any win). Boss
    // finales get their own two-line send-off instead (and the buttons drop a
    // few px to make room for it).
    const bossWin = won && hasBoss(this.level);
    const btnY = H / 2 + (bossWin ? 108 : 90);
    if (bossWin) {
      // Each boss finale gets its own send-off line.
      const bossSendOffs: Record<string, string> = {
        nullnugget: 'The Null Nugget is null and void. The lanterns burn on.',
        kingnuggetron: 'King Nugget-tron is dethroned. The crown rolls into the fryer.',
        emperorpepro: 'Emperor Pepro is deposed. His sandy cloud blows away on the wind.',
        kingember: 'King Ember is snuffed out. The last blue flame gutters and dies.',
        chiplysdad: "Chiply's Dad is crushed to crumbs. The Moon is yours.",
        themushy: 'The Mushy is mulched. The forest breathes again.',
      };
      const bossMsg =
        bossSendOffs[bossTypeOf(this.level) ?? ''] ??
        'The Golden Nugget is toast. Dipped, dunked, done.';
      this.add
        .text(W / 2, H / 2 + 32, bossMsg, {
          fontSize: '19px',
          color: '#f5c518',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(OVERLAY_DEPTH + 1);
      if (WIN_GATE_LEVEL_IDS.includes(this.level.id)) {
        // Chapter-aware: beating a chapter's finale unlocks the NEXT chapter.
        const nextChapter = chapterOf(this.level) + 1;
        const unlockMsg =
          nextChapter === 2
            ? 'CHAPTER 2 UNLOCKED — the Frozen Peaks are calling.'
            : nextChapter === 3
              ? 'CHAPTER 3 UNLOCKED — Hambi Castle awaits.'
              : `CHAPTER ${nextChapter} UNLOCKED!`;
        this.add
          .text(W / 2, H / 2 + 60, unlockMsg, {
            fontSize: '17px',
            color: '#9fd8ff',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(OVERLAY_DEPTH + 1);
      } else if (isLastLevel) {
        this.add
          .text(W / 2, H / 2 + 60, 'CAMPAIGN COMPLETE — the picnic is safe. For now…', {
            fontSize: '17px',
            color: '#9fd8ff',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(OVERLAY_DEPTH + 1);
      } else if (grantedNow.length > 0) {
        // NOTE: this is the last arm of an else-if chain and only shows
        // grantedNow[0]. King Hambi IS a win-grant on the last level, so the
        // `isLastLevel` arm above shadows this line for him — which is why he
        // gets his own modal instead (`showKingHambiNotification`). A future
        // grant on a chapter finale needs the same, and a level granting two
        // towers would still show only the first here.
        this.add
          .text(W / 2, H / 2 + 60, `NEW TOWER UNLOCKED — ${TOWER_NAMES[grantedNow[0]]}!`, {
            fontSize: '17px',
            color: '#c77dff',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(OVERLAY_DEPTH + 1);
      }
    } else {
      let gateMsg = '';
      let gateColor = '#9aa6ad';
      if (won && advanced) {
        gateMsg = 'Next level unlocked!';
        gateColor = '#5bd35b';
      } else if (won && isLastLevel) {
        // Every win is 3★-eligible but only ≥80% lives earns it — on the last
        // level there is nothing left to unlock, so ★★★ becomes the target.
        gateMsg = stars >= 3 ? 'Campaign complete!' : 'Cleared — now chase ★★★!';
        gateColor = stars >= 3 ? '#5bd35b' : '#f5c518';
      }
      // No "you fell short of the gate" arm: any win (≥1★) now advances, so a
      // win that doesn't unlock the next level can only be the last level.
      if (gateMsg) {
        this.add
          .text(W / 2, H / 2 + 38, gateMsg, {
            fontSize: '20px',
            color: gateColor,
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(OVERLAY_DEPTH + 1);
      }
    }

    // ── Retry button (≥44 px tall) ────────────────────────────────────────────
    const retryBg = this.add
      .rectangle(W / 2 - 130, btnY, 210, 56, 0x1a6b1a)
      .setStrokeStyle(2, 0x4caf50)
      .setInteractive({ useHandCursor: true })
      .setDepth(OVERLAY_DEPTH + 1);

    this.add
      .text(W / 2 - 130, btnY, 'RETRY', {
        fontSize: '22px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(OVERLAY_DEPTH + 2);

    retryBg.on('pointerup', () => {
      if (isDemo) {
        this.scene.start('DemoScene');
        return;
      }
      // The arena must come back up in arena mode, or the HUD would relaunch as
      // a campaign level on a sandbox map (priced shop, no wave dialog).
      const arena = this.level.sandbox === true;
      const peaceful = this._peaceful;
      this.scene.launch('HudScene', { levelId: this.level.id, arena, peaceful });
      this.scene.restart({ levelId: this.level.id, peaceful });
    });

    // ── Menu button (≥44 px tall) ─────────────────────────────────────────────
    const menuBg = this.add
      .rectangle(W / 2 + 130, btnY, 210, 56, 0x2a2a7a)
      .setStrokeStyle(2, 0x4466cc)
      .setInteractive({ useHandCursor: true })
      .setDepth(OVERLAY_DEPTH + 1);

    const hubRun = isHubLevelId(this.level.id) || isOriginLevelId(this.level.id);
    this.add
      .text(W / 2 + 130, btnY, isDemo ? 'MENU' : hubRun ? 'LEVELS' : 'EPISODES', {
        fontSize: '22px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(OVERLAY_DEPTH + 2);

    // Leaving a finished level returns to the episode picker (not the home
    // title) so the player can jump straight to the next episode — matching the
    // in-game ABANDON button. Demo/review mode returns to its own gallery.
    menuBg.on('pointerup', () => {
      this.scene.stop('HudScene');
      if (isDemo) {
        this.scene.start('DemoScene');
      } else {
        const picker = levelPickerFor(this.level);
        this.scene.start(picker.key, picker.data);
      }
    });

    if (won && coinsEarned > 0) {
      const pillY = 70;
      const startTotal = coinsTotal - coinsEarned;

      // Balance pill (top-centre of the overlay).
      this.add
        .rectangle(W / 2, pillY, 220, 48, 0x1a1726, 0.9)
        .setStrokeStyle(2, 0xf5c518)
        .setDepth(OVERLAY_DEPTH + 1);
      const pillCoin = this.add
        .image(W / 2 - 78, pillY, 'coin')
        .setDisplaySize(34, 34)
        .setDepth(OVERLAY_DEPTH + 2);
      spinCoin(this, pillCoin);
      const pillText = this.add
        .text(W / 2 - 50, pillY, `${startTotal}`, {
          fontSize: '26px',
          color: '#f5c518',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setDepth(OVERLAY_DEPTH + 2);

      // "+N" flourish — grouped just under the pill (clear of the score/gate stack).
      this.add
        .text(W / 2, pillY + 44, `+${coinsEarned} coins`, {
          fontSize: '20px',
          color: '#f5c518',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(OVERLAY_DEPTH + 2);

      // Float a handful of spinning coins from mid-screen up into the pill,
      // bumping the total. Spawn point is below the stars, clear of all text.
      const flyers = Math.min(coinsEarned, 8);
      let landed = 0;
      for (let i = 0; i < flyers; i++) {
        const coin = this.add
          .image(W / 2, H / 2 - 30, 'coin')
          .setDisplaySize(28, 28)
          .setDepth(OVERLAY_DEPTH + 3);
        spinCoin(this, coin);
        this.tweens.add({
          targets: coin,
          x: W / 2 - 78,
          y: pillY,
          delay: i * 90,
          duration: 600,
          ease: 'Cubic.easeIn',
          onComplete: () => {
            coin.destroy();
            landed++;
            // Distribute the full reward across the flying coins so the final
            // value always lands exactly on coinsTotal.
            const shown = startTotal + Math.round((coinsEarned * landed) / flyers);
            pillText.setText(`${shown}`);
          },
        });
      }
    }

    // Dark / Light Hambi Coins (an Origin win): the same pill and flight, in
    // the chapter's own coin. A level pays one or the other, never both.
    const eventCoinPill = (
      earned: number,
      total: number,
      art: 'darkcoin' | 'lightcoin',
      label: string,
      name: string,
    ): void => {
      const frames = art === 'darkcoin' ? DARKCOIN_SPIN_FRAMES : LIGHTCOIN_SPIN_FRAMES;
      const pillY = 70;
      const startTotal = total - earned;
      this.add
        .rectangle(W / 2, pillY, 220, 48, 0x1a1726, 0.9)
        .setStrokeStyle(2, 0x8a8f99)
        .setDepth(OVERLAY_DEPTH + 1);
      const pillCoin = this.add
        .image(W / 2 - 78, pillY, art)
        .setDisplaySize(34, 34)
        .setDepth(OVERLAY_DEPTH + 2);
      spinCoin(this, pillCoin, frames);
      const pillText = this.add
        .text(W / 2 - 50, pillY, `${startTotal}`, {
          fontSize: '26px',
          color: '#d7dbe2',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setDepth(OVERLAY_DEPTH + 2)
        .setName(`${name}-total`);
      this.add
        .text(W / 2, pillY + 44, `+${earned} ${label}`, {
          fontSize: '20px',
          color: '#d7dbe2',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(OVERLAY_DEPTH + 2)
        .setName(name);
      const flyers = Math.min(earned, 8);
      let landed = 0;
      for (let i = 0; i < flyers; i++) {
        const coin = this.add
          .image(W / 2, H / 2 - 30, art)
          .setDisplaySize(28, 28)
          .setDepth(OVERLAY_DEPTH + 3);
        spinCoin(this, coin, frames);
        this.tweens.add({
          targets: coin,
          x: W / 2 - 78,
          y: pillY,
          delay: i * 90,
          duration: 600,
          ease: 'Cubic.easeIn',
          onComplete: () => {
            coin.destroy();
            landed++;
            pillText.setText(`${startTotal + Math.round((earned * landed) / flyers)}`);
          },
        });
      }
    };
    const darkEarned = a.darkCoinsEarned ?? 0;
    if (won && darkEarned > 0) {
      eventCoinPill(
        darkEarned,
        a.darkCoinsTotal ?? darkEarned,
        'darkcoin',
        'Dark Hambi Coins',
        'result-darkcoins',
      );
    }
    const lightEarned = a.lightCoinsEarned ?? 0;
    if (won && lightEarned > 0) {
      eventCoinPill(
        lightEarned,
        a.lightCoinsTotal ?? lightEarned,
        'lightcoin',
        'Light Hambi Coins',
        'result-lightcoins',
      );
    }

    if (orbClaimed) this.showOrbClaimNotification();
    if (a.diamondClaimed) this.showDiamondClaimNotification();
    if (a.moonstoneClaimed) this.showMoonstoneClaimNotification();
    if (a.debrisClaimed) this.showDebrisClaimNotification();
    if (a.darkShardClaimed) this.showDarkShardClaimNotification();
    if (a.lightShardClaimed) this.showLightShardClaimNotification();
    if (a.evilOrbClaimed) this.showEvilOrbClaimNotification();
    if (a.chestWon) this.showChestClaimNotification(a.chestWon);
    for (const kind of a.fungiItemsWon ?? []) this.showFungiItemClaimNotification(kind);
    // Over the chest's modal on the win that pays both.
    if (a.lightOrbClaimed) this.showLightOrbClaimNotification();
    if (a.tokenClaimed) this.showTokenOfShroomClaimNotification();
    // Over the Fungi item modals on the win that pays both.
    if (won && grantedNow.includes('goldenshroom')) this.showGoldenShroomNotification();
    // Last, so it sits on top of the orb's modal on the win that claims both.
    if (won && grantedNow.includes('kinghambi')) this.showKingHambiNotification();
  }

  /**
   * The Dark Shadows' prize modal (spec 2026-10-07): the item bobbing on a
   * card, a title, a line, and buttons. `rarity` draws the band name over it
   * (the Evil Orb is Ketchuptastic). Returns `dismiss` and `say` for the
   * callers' buttons.
   */
  private showPrizeModal(opts: {
    art: string;
    accent: number;
    title: string;
    titleColor: string;
    line: string;
    name: string;
    rarity?: string;
    buttons: { label: string; fill: number; stroke: number; name: string; onUp: () => void }[];
    /**
     * Stacking depth (default 120). A prize that can land on the same win as
     * another modal sits ABOVE it, the way King Hambi's notice sits at 130 over
     * the orb's — two modals at one depth draw their text through each other.
     */
    depth?: number;
  }): { dismiss: () => void; say: (msg: string, color: string) => void } {
    const W = 1280;
    const H = 720;
    const D = opts.depth ?? 120;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      return o;
    };
    add(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x05030c, 0.82)
        .setDepth(D)
        .setInteractive(),
    );
    add(
      this.add
        .rectangle(W / 2, H / 2, 720, 460, 0x1a1726, 0.98)
        .setStrokeStyle(3, opts.accent)
        .setDepth(D + 1),
    );
    if (opts.rarity) {
      add(
        this.add
          .text(W / 2, H / 2 - 196, opts.rarity, {
            fontSize: '30px',
            color: `#${RARITY_COLORS.Ketchuptastic.toString(16).padStart(6, '0')}`,
            fontStyle: 'bold',
            stroke: '#fff4d6',
            strokeThickness: 5,
          })
          .setOrigin(0.5)
          .setDepth(D + 2)
          .setName(`${opts.name}-rarity`),
      );
    }
    let bob: Phaser.Tweens.Tween | null = null;
    if (this.textures.exists(opts.art)) {
      const img = add(
        this.add
          .image(W / 2, H / 2 - 92, opts.art)
          .setDisplaySize(150, 150)
          .setDepth(D + 2)
          .setName(`${opts.name}-art`),
      );
      bob = this.tweens.add({
        targets: img,
        y: H / 2 - 104,
        duration: 1400,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    }
    add(
      this.add
        .text(W / 2, H / 2 + 22, opts.title, {
          fontSize: '32px',
          color: opts.titleColor,
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    add(
      this.add
        .text(W / 2, H / 2 + 64, opts.line, { fontSize: '19px', color: '#c8c2d8' })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    const dismiss = () => {
      bob?.stop();
      objs.forEach((o) => o.destroy());
      objs.length = 0;
    };
    const say = (msg: string, color: string) => {
      this.add
        .text(640, 150, msg, { fontSize: '22px', color, fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(60);
    };
    const btnY = H / 2 + 148;
    const step = 260;
    const x0 = W / 2 - ((opts.buttons.length - 1) * step) / 2;
    opts.buttons.forEach((b, i) => {
      const bg = add(
        this.add
          .rectangle(x0 + i * step, btnY, 230, 60, b.fill)
          .setStrokeStyle(2, b.stroke)
          .setInteractive({ useHandCursor: true })
          .setDepth(D + 2)
          .setName(b.name),
      );
      add(
        this.add
          .text(x0 + i * step, btnY, b.label, {
            fontSize: '24px',
            color: '#ffffff',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(D + 3),
      );
      bg.on('pointerup', b.onUp);
    });
    return { dismiss, say };
  }

  /** "You found a Dark Shard!" — every Dark Forest win; STORE or USE. */
  private showDarkShardClaimNotification(): void {
    const RED = '#ff7a86';
    const m = this.showPrizeModal({
      art: 'item-darkshard',
      accent: 0xd8263a,
      title: 'You found a Dark Shard!',
      titleColor: RED,
      line: 'Every tower hits 10% harder for 2 hours of play.',
      name: 'darkshard-claim',
      buttons: [
        {
          label: 'STORE',
          fill: 0x2a2a7a,
          stroke: 0x4466cc,
          name: 'darkshard-claim-store',
          onUp: () => {
            m.dismiss();
            m.say('The Dark Shard is waiting in STORAGE.', RED);
          },
        },
        {
          label: 'USE',
          fill: 0x1a6b1a,
          stroke: 0x4caf50,
          name: 'darkshard-claim-use',
          onUp: () => {
            const progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
            const now = Date.now();
            // Ask BEFORE taking, as for Space Debris: a refused duplicate must
            // not cost the shard.
            const check = canApply(progress.effects(), 'darkshard', undefined, now);
            m.dismiss();
            if (!check.ok) {
              m.say(`Dark Shard kept — ${check.reason.toLowerCase()}.`, '#c8c2d8');
              return;
            }
            if (progress.takeDarkShard()) progress.startEffect('darkshard', undefined, now);
            m.say('The Dark Shard glows — +10% for 2:00:00 of play.', RED);
          },
        },
      ],
    });
  }

  /**
   * "You found the Shroom!" (spec 2026-10-24) — a lucky Fungi Forest win. It
   * is already on the shelf: it goes on a tower from Storage, where the
   * picker is, so the modal only says where it went.
   */
  private showFungiItemClaimNotification(kind: FungiItemKind): void {
    const def = FUNGI_ITEMS[kind];
    const color = kind === 'shroom' ? '#ff9a8a' : '#d6f0ff';
    const m = this.showPrizeModal({
      art: def.art,
      accent: kind === 'shroom' ? 0xe0483a : 0x9fd6f0,
      title: `You found ${kind === 'shroom' ? 'the Shroom' : 'a Snowshroom'}!`,
      titleColor: color,
      line: `${def.effect} for 3:00. It is waiting in STORAGE.`,
      name: `${kind}-claim`,
      buttons: [
        {
          label: 'OK',
          fill: 0x1a6b1a,
          stroke: 0x4caf50,
          name: `${kind}-claim-ok`,
          onUp: () => m.dismiss(),
        },
      ],
    });
  }

  /** "You found a Light Shard!" — every Sky Islands win; STORE or USE. */
  private showLightShardClaimNotification(): void {
    const WHITE = '#eaf6ff';
    const m = this.showPrizeModal({
      art: 'item-lightshard',
      accent: 0xbfe3ff,
      title: 'You found a Light Shard!',
      titleColor: WHITE,
      line: 'Every tower hits 10% harder for 2 hours of play.',
      name: 'lightshard-claim',
      buttons: [
        {
          label: 'STORE',
          fill: 0x2a2a7a,
          stroke: 0x4466cc,
          name: 'lightshard-claim-store',
          onUp: () => {
            m.dismiss();
            m.say('The Light Shard is waiting in STORAGE.', WHITE);
          },
        },
        {
          label: 'USE',
          fill: 0x1a6b1a,
          stroke: 0x4caf50,
          name: 'lightshard-claim-use',
          onUp: () => {
            const progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
            const now = Date.now();
            // Ask BEFORE taking, as for the Dark Shard.
            const check = canApply(progress.effects(), 'lightshard', undefined, now);
            m.dismiss();
            if (!check.ok) {
              m.say(`Light Shard kept — ${check.reason.toLowerCase()}.`, '#c8c2d8');
              return;
            }
            if (progress.takeLightShard()) progress.startEffect('lightshard', undefined, now);
            m.say('The Light Shard shines — +10% for 2:00:00 of play.', WHITE);
          },
        },
      ],
    });
  }

  /** "You have claimed the Evil Orb!" — its quest's first win. Ketchuptastic. */
  /** "You have claimed the Light Orb!" — the Evil Orb's twin (spec 2026-10-13). */
  private showLightOrbClaimNotification(): void {
    const m = this.showPrizeModal({
      art: 'decor-heavenorb',
      accent: 0x9fd8ff,
      rarity: 'KETCHUPTASTIC',
      title: 'You have claimed the Light Orb!',
      titleColor: '#e6f6ff',
      line: 'It is waiting in STORAGE. What it does... no one knows yet.',
      name: 'lightorb-claim',
      buttons: [
        {
          label: 'OK',
          fill: 0x1a6b1a,
          stroke: 0x4caf50,
          name: 'lightorb-claim-ok',
          onUp: () => m.dismiss(),
        },
      ],
    });
  }

  /** A lucky win dropped the chapter's chest (spec 2026-10-13): it is opened from Storage. */
  private showChestClaimNotification(kind: ChestKind): void {
    const dark = kind === 'dark';
    const m = this.showPrizeModal({
      art: CHEST_ART[kind],
      accent: dark ? 0x8a6ad0 : 0xfff1c8,
      title: `You found a ${CHEST_NAMES[kind]}!`,
      titleColor: dark ? '#d8c8ff' : '#fff6dc',
      line: 'It is waiting in STORAGE. Open it there to see what is inside!',
      name: 'chest-claim',
      buttons: [
        {
          label: 'OK',
          fill: 0x1a6b1a,
          stroke: 0x4caf50,
          name: 'chest-claim-ok',
          onUp: () => m.dismiss(),
        },
      ],
    });
  }

  /**
   * "You have unlocked The Golden Shroom!" — Pathway of the Fungi's prize.
   * Always the GOLD tier-3 art (MENU_TIER), so everyone sees it golden.
   */
  private showGoldenShroomNotification(): void {
    const m = this.showPrizeModal({
      art: menuTowerTexture('goldenshroom'),
      accent: 0xffc93a,
      rarity: 'KETCHUPTASTIC',
      title: 'You have unlocked The Golden Shroom!',
      titleColor: '#ffd84a',
      line: 'Golden shrooms, and a SHROOM FRENZY every 25 seconds.',
      name: 'goldenshroom-claim',
      // Over a Shroom drop's modal on the same win.
      depth: 128,
      buttons: [
        {
          label: 'OK',
          fill: 0x1a6b1a,
          stroke: 0x4caf50,
          name: 'goldenshroom-claim-ok',
          onUp: () => m.dismiss(),
        },
      ],
    });
  }

  /** The Shroombellion's first-win prize: a mossy gold coin that does nothing yet. */
  private showTokenOfShroomClaimNotification(): void {
    const m = this.showPrizeModal({
      art: 'item-tokenofshroom',
      accent: 0x8fbf3a,
      rarity: 'KETCHUPTASTIC',
      title: 'You have claimed the Token of Shroom!',
      titleColor: '#d8f08a',
      line: 'It is waiting in STORAGE. What it does... no one knows yet.',
      name: 'tokenofshroom-claim',
      // Over a Shroom drop's modal on the same win.
      depth: 125,
      buttons: [
        {
          label: 'OK',
          fill: 0x1a6b1a,
          stroke: 0x4caf50,
          name: 'tokenofshroom-claim-ok',
          onUp: () => m.dismiss(),
        },
      ],
    });
  }

  private showEvilOrbClaimNotification(): void {
    const m = this.showPrizeModal({
      art: 'item-evilorb',
      accent: 0xff5a1f,
      rarity: 'KETCHUPTASTIC',
      title: 'You have claimed the Evil Orb!',
      titleColor: '#ffb48a',
      line: 'It is waiting in STORAGE. What it does... no one knows yet.',
      name: 'evilorb-claim',
      buttons: [
        {
          label: 'OK',
          fill: 0x1a6b1a,
          stroke: 0x4caf50,
          name: 'evilorb-claim-ok',
          onUp: () => m.dismiss(),
        },
      ],
    });
  }

  /**
   * "You have found the Diamond!" — HTD Exhibition's first-win modal, the
   * orb's twin with USE and STORE. Claiming already put it on the shelf, so
   * STORE only dismisses; USE spends it and starts the minute of shielding
   * from right now, wherever the player goes next.
   */
  private showDiamondClaimNotification(): void {
    const W = 1280;
    const H = 720;
    const D = 120;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      return o;
    };
    add(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x05030c, 0.82)
        .setDepth(D)
        .setInteractive(),
    );
    add(
      this.add
        .rectangle(W / 2, H / 2, 720, 460, 0x1a1726, 0.98)
        .setStrokeStyle(3, 0xffe94a)
        .setDepth(D + 1),
    );
    const gem = add(
      this.add
        .image(W / 2, H / 2 - 110, 'diamond')
        .setDisplaySize(150, 150)
        .setDepth(D + 2)
        .setName('diamond-claim-art'),
    );
    const bob = this.tweens.add({
      targets: gem,
      y: H / 2 - 122,
      duration: 1400,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
    add(
      this.add
        .text(W / 2, H / 2 + 12, 'You have found the Diamond!', {
          fontSize: '32px',
          color: '#ffe94a',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    add(
      this.add
        .text(W / 2, H / 2 + 54, 'Legendary — your towers take no damage for one minute.', {
          fontSize: '19px',
          color: '#9fd8ff',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    const btnY = H / 2 + 138;
    const button = (dx: number, label: string, fill: number, stroke: number, name: string) => {
      const bg = add(
        this.add
          .rectangle(W / 2 + dx, btnY, 230, 60, fill)
          .setStrokeStyle(2, stroke)
          .setInteractive({ useHandCursor: true })
          .setDepth(D + 2)
          .setName(name),
      );
      add(
        this.add
          .text(W / 2 + dx, btnY, label, { fontSize: '24px', color: '#ffffff', fontStyle: 'bold' })
          .setOrigin(0.5)
          .setDepth(D + 3),
      );
      return bg;
    };
    const dismiss = () => {
      bob.stop();
      objs.forEach((o) => o.destroy());
      objs.length = 0;
    };
    button(-130, 'STORE', 0x2a2a7a, 0x4466cc, 'diamond-claim-store').on('pointerup', () => {
      dismiss();
      this.add
        .text(640, 150, 'The Diamond is waiting in STORAGE.', {
          fontSize: '22px',
          color: '#ffe94a',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(60);
    });
    button(130, 'USE', 0x1a6b1a, 0x4caf50, 'diamond-claim-use').on('pointerup', () => {
      const progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
      // Off the shelf FIRST, so a double-click cannot start two shields.
      if (progress.useDiamond()) progress.startEffect('diamond', undefined, Date.now());
      dismiss();
      this.add
        .text(640, 150, 'The Diamond shines — your towers are shielded for 1:00.', {
          fontSize: '22px',
          color: '#9fd8ff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(60);
    });
  }

  /**
   * "You found Space Debris" — Rocket Rage's every-win modal, the Diamond's
   * twin with USE and STORE. The piece is already on the shelf by the time
   * this shows, so STORE only dismisses; USE spends one and starts the two
   * minutes of jammed nachos from right now, wherever the player goes next.
   */
  private showDebrisClaimNotification(): void {
    const W = 1280;
    const H = 720;
    const D = 120;
    const STEEL = 0x8fa3b8;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      return o;
    };
    add(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x05030c, 0.82)
        .setDepth(D)
        .setInteractive(),
    );
    add(
      this.add
        .rectangle(W / 2, H / 2, 720, 460, 0x1a1726, 0.98)
        .setStrokeStyle(3, STEEL)
        .setDepth(D + 1),
    );
    let bob: Phaser.Tweens.Tween | null = null;
    if (this.textures.exists('item-debris')) {
      const slab = add(
        this.add
          .image(W / 2, H / 2 - 110, 'item-debris')
          .setDisplaySize(150, 150)
          .setDepth(D + 2)
          .setName('debris-claim-art'),
      );
      bob = this.tweens.add({
        targets: slab,
        y: H / 2 - 122,
        duration: 1400,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    }
    add(
      this.add
        .text(W / 2, H / 2 + 12, 'You found Space Debris', {
          fontSize: '32px',
          color: '#cfe4ff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    add(
      this.add
        .text(W / 2, H / 2 + 54, 'Use it wisely to fend off the nachos.', {
          fontSize: '19px',
          color: '#9fb4c8',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    const btnY = H / 2 + 138;
    const button = (dx: number, label: string, fill: number, stroke: number, name: string) => {
      const bg = add(
        this.add
          .rectangle(W / 2 + dx, btnY, 230, 60, fill)
          .setStrokeStyle(2, stroke)
          .setInteractive({ useHandCursor: true })
          .setDepth(D + 2)
          .setName(name),
      );
      add(
        this.add
          .text(W / 2 + dx, btnY, label, { fontSize: '24px', color: '#ffffff', fontStyle: 'bold' })
          .setOrigin(0.5)
          .setDepth(D + 3),
      );
      return bg;
    };
    const dismiss = () => {
      bob?.stop();
      objs.forEach((o) => o.destroy());
      objs.length = 0;
    };
    const say = (msg: string, color: string) => {
      this.add
        .text(640, 150, msg, { fontSize: '22px', color, fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(60);
    };
    button(-130, 'STORE', 0x2a2a7a, 0x4466cc, 'debris-claim-store').on('pointerup', () => {
      dismiss();
      say('The Space Debris is waiting in STORAGE.', '#cfe4ff');
    });
    button(130, 'USE', 0x1a6b1a, 0x4caf50, 'debris-claim-use').on('pointerup', () => {
      const progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
      const now = Date.now();
      // Ask BEFORE taking: debris stacks, so a piece can already be running,
      // and taking one off the shelf for a refused effect would destroy it.
      const check = canApply(progress.effects(), 'debris', undefined, now);
      dismiss();
      if (!check.ok) {
        say(`Space Debris kept — ${check.reason.toLowerCase()}.`, '#9fb4c8');
        return;
      }
      if (progress.takeDebris()) progress.startEffect('debris', undefined, now);
      say('The nachos are jammed for 2:00.', '#cfe4ff');
    });
  }

  /**
   * "You found the Moon Stone" — the chapter finale's first-win modal, the
   * Diamond's twin with USE and STORE. Claiming already put it on the shelf,
   * so STORE only dismisses; USE spends it and starts the half hour from
   * right now, wherever the player goes next.
   */
  private showMoonstoneClaimNotification(): void {
    const W = 1280;
    const H = 720;
    const D = 120;
    const VIOLET = 0x9d7bff;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      return o;
    };
    add(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x05030c, 0.82)
        .setDepth(D)
        .setInteractive(),
    );
    add(
      this.add
        .rectangle(W / 2, H / 2, 720, 460, 0x1a1726, 0.98)
        .setStrokeStyle(3, VIOLET)
        .setDepth(D + 1),
    );
    let bob: Phaser.Tweens.Tween | null = null;
    if (this.textures.exists('item-moonstone')) {
      const stone = add(
        this.add
          .image(W / 2, H / 2 - 110, 'item-moonstone')
          .setDisplaySize(150, 150)
          .setDepth(D + 2)
          .setName('moonstone-claim-art'),
      );
      bob = this.tweens.add({
        targets: stone,
        y: H / 2 - 122,
        duration: 1400,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    }
    add(
      this.add
        .text(W / 2, H / 2 + 12, 'You found the Moon Stone', {
          fontSize: '32px',
          color: '#c3aaff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    add(
      this.add
        .text(W / 2, H / 2 + 54, 'Thirty minutes of moonlight. Every tower hits harder.', {
          fontSize: '19px',
          color: '#a893d8',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    const btnY = H / 2 + 138;
    const button = (dx: number, label: string, fill: number, stroke: number, name: string) => {
      const bg = add(
        this.add
          .rectangle(W / 2 + dx, btnY, 230, 60, fill)
          .setStrokeStyle(2, stroke)
          .setInteractive({ useHandCursor: true })
          .setDepth(D + 2)
          .setName(name),
      );
      add(
        this.add
          .text(W / 2 + dx, btnY, label, { fontSize: '24px', color: '#ffffff', fontStyle: 'bold' })
          .setOrigin(0.5)
          .setDepth(D + 3),
      );
      return bg;
    };
    const dismiss = () => {
      bob?.stop();
      objs.forEach((o) => o.destroy());
      objs.length = 0;
    };
    const say = (msg: string, color: string) => {
      this.add
        .text(640, 150, msg, { fontSize: '22px', color, fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(60);
    };
    button(-130, 'STORE', 0x2a2a7a, 0x4466cc, 'moonstone-claim-store').on('pointerup', () => {
      dismiss();
      say('The Moon Stone is waiting in STORAGE.', '#c3aaff');
    });
    button(130, 'USE', 0x1a6b1a, 0x4caf50, 'moonstone-claim-use').on('pointerup', () => {
      const progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
      dismiss();
      // Off the shelf first: one exists ever, so there is no stacking case to
      // guard and a double-tap must not start two moons.
      if (progress.useMoonstone()) {
        progress.startEffect('moonstone', undefined, Date.now());
        say('Every tower hits 50% harder for 30:00.', '#c3aaff');
      }
    });
  }

  /** Re-read the effect records on a slow clock and push the sim's modifiers. */
  private refreshEffects(): void {
    const now = Date.now();
    if (now - this.effectsReadAt < EFFECTS_REFRESH_MS) return;
    this.effectsReadAt = now;
    try {
      this.effects = activeEffects(
        new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS).effects(),
        now,
      );
    } catch (err) {
      console.warn('[GameScene] effects read failed', err);
      this.effects = [];
    }
    const mods = effectsToModifiers(this.effects, now);
    this.shieldOn = mods.invulnerable;
    this.sim.setModifiers(mods);
  }

  /**
   * "You have claimed the Variety Orb!" — the modal that lands on the finished
   * Level Complete screen, with STORE and OPEN beneath it.
   *
   * Drawn OVER the result screen rather than woven into it. The result screen
   * is a fixed vertical stack (title, stars, score, send-off, unlock line,
   * buttons) with no room left, and — more to the point — the file already
   * warns that the granted-tower callout is the last arm of an else-if chain
   * that `isLastLevel` shadows. Keeping the orb out of that chain is what lets
   * Level 25 keep its CAMPAIGN COMPLETE line and still announce the orb.
   *
   * STORE is the verb the Restaurant already uses for "shelve it for later",
   * not the shop; the orb is already on the shelf by this point, so STORE
   * simply dismisses and OPEN runs the ceremony.
   */
  private showOrbClaimNotification(): void {
    const W = 1280;
    const H = 720;
    const D = 120; // above the result screen (50), below the orb reveal (600)
    const objs: Phaser.GameObjects.GameObject[] = [];
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      return o;
    };

    // Full-screen scrim, interactive so a stray click cannot reach the RETRY /
    // EPISODES buttons sitting live underneath the modal.
    add(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x05030c, 0.82)
        .setDepth(D)
        .setInteractive(),
    );
    add(
      this.add
        .rectangle(W / 2, H / 2, 720, 460, 0x1a1726, 0.98)
        .setStrokeStyle(3, 0xc77dff)
        .setDepth(D + 1),
    );

    // The Variety Orb is Ketchuptastic (since 2026-10-07), like the Evil Orb.
    add(
      this.add
        .text(W / 2, H / 2 - 196, 'KETCHUPTASTIC', {
          fontSize: '30px',
          color: `#${RARITY_COLORS.Ketchuptastic.toString(16).padStart(6, '0')}`,
          fontStyle: 'bold',
          stroke: '#fff4d6',
          strokeThickness: 5,
        })
        .setOrigin(0.5)
        .setDepth(D + 2)
        .setName('orb-claim-rarity'),
    );
    const orb = add(
      this.add
        .image(W / 2, H / 2 - 110, 'orb-variety')
        .setDisplaySize(170, 170)
        .setDepth(D + 2)
        .setName('orb-claim-art'),
    );
    // A slow bob, so the prize reads as floating rather than pasted on. Held so
    // `dismiss` can STOP it: an endlessly repeating tween whose target has been
    // destroyed keeps being stepped by the scene's tween manager, and the orb
    // reveal that follows is a chain of tweens and delayed calls on that same
    // scene — leaving this one running is how the ceremony silently never
    // starts.
    const bob = this.tweens.add({
      targets: orb,
      y: H / 2 - 122,
      duration: 1400,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    add(
      this.add
        .text(W / 2, H / 2 + 12, 'You have claimed the Variety Orb!', {
          fontSize: '32px',
          color: '#f5c518',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    add(
      this.add
        .text(W / 2, H / 2 + 54, 'It holds one tower — any but the ones you started with.', {
          fontSize: '19px',
          color: '#9fd8ff',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );

    const btnY = H / 2 + 138;
    const button = (dx: number, label: string, fill: number, stroke: number, name: string) => {
      const bg = add(
        this.add
          .rectangle(W / 2 + dx, btnY, 230, 60, fill)
          .setStrokeStyle(2, stroke)
          .setInteractive({ useHandCursor: true })
          .setDepth(D + 2)
          .setName(name),
      );
      add(
        this.add
          .text(W / 2 + dx, btnY, label, {
            fontSize: '24px',
            color: '#ffffff',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(D + 3),
      );
      return bg;
    };

    const dismiss = () => {
      bob.stop();
      objs.forEach((o) => o.destroy());
      objs.length = 0;
    };

    // STORE: the orb is already 'stored' — claiming put it on the shelf — so
    // this only has to get out of the way and point at where it went.
    button(-130, 'STORE', 0x2a2a7a, 0x4466cc, 'orb-claim-store').on('pointerup', () => {
      dismiss();
      this.add
        .text(640, 150, 'The Variety Orb is waiting in STORAGE.', {
          fontSize: '22px',
          color: '#9fd8ff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(60);
    });

    button(130, 'OPEN', 0x1a6b1a, 0x4caf50, 'orb-claim-open').on('pointerup', () => {
      // One service for both the take and the grant. They share the same
      // underlying storage either way, but a single instance says plainly that
      // this is one transaction.
      const progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
      // Take it off the shelf FIRST, exactly as Storage does with a bag, so a
      // double-click cannot roll two towers.
      if (!progress.openOrb()) {
        dismiss();
        return;
      }
      dismiss();
      // Next tick, not this one: `dismiss` has just destroyed the very button
      // whose pointerup is still being dispatched, and the ceremony below
      // builds a fresh overlay on the same scene. Letting the current input
      // dispatch finish first keeps those two things from overlapping.
      this.time.delayedCall(0, () => openVarietyOrb(this, { progress, onDone: () => {} }));
    });
  }

  /**
   * Arm the arena's Shrekbook challenge from the wave HudScene is about to
   * send. Called for EVERY arena wave — a wave that does not qualify disarms
   * the tracker rather than leaving a stale attempt running.
   */
  public armArenaChallenge(entries: readonly ArenaWaveEntry[]): void {
    this.arenaChallenge.armFor(entries);
  }

  /** Abandon a challenge attempt (Clear Wave wipes the pitch mid-wave). */
  public resetArenaChallenge(): void {
    this.arenaChallenge.reset();
  }

  /**
   * The challenge was met: persist the reward and celebrate.
   *
   * This is the ONE thing the sandbox arena writes to storage. It deliberately
   * does not go through `handleGameOver` — the arena run is not over, the
   * player keeps their towers, and the `noPersist` rule there (a sandbox banks
   * no score, stars or coins) still holds for everything else.
   *
   * `grantChallengeTower` returns false when the tower is already held, so
   * re-running the challenge simply re-shows nothing.
   */
  private awardChallengeTower(): void {
    // Belt and braces on top of the idempotent grant below: one arena visit
    // shows the celebration at most once, whatever the sim does. Without it a
    // second `waveCleared` in the same session — or a storage write that
    // silently fails, leaving `grantChallengeTower` willing to "grant" again —
    // could put the panel up twice.
    if (this.challengeOverlayShown) return;
    let granted = false;
    try {
      const progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
      granted = progress.grantChallengeTower(SHREK_CHALLENGE.reward);
    } catch (err) {
      console.warn('[GameScene] Challenge grant failed', err);
      return;
    }
    if (granted) {
      this.challengeOverlayShown = true;
      this.showChallengeUnlockOverlay();
    }
  }

  /**
   * The Shrekbook unlock celebration: a panel over the pitch with the tier-1
   * sprite and a CONTINUE that dismisses it. The sandbox keeps running
   * underneath — the sim is paused only while the panel is up, so a stray
   * wave cannot walk out behind it.
   */
  private showChallengeUnlockOverlay(): void {
    const W = 1280;
    const H = 720;
    const OVERLAY_DEPTH = 50;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const wasPaused = this.paused;
    this.paused = true;

    objs.push(this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.7).setDepth(OVERLAY_DEPTH));
    objs.push(
      this.add
        .rectangle(W / 2, H / 2, 620, 380, 0x10240f)
        .setStrokeStyle(3, 0x6ecb3c)
        .setDepth(OVERLAY_DEPTH + 1),
    );
    objs.push(
      this.add
        .text(W / 2, H / 2 - 140, 'CHALLENGE COMPLETE!', {
          fontSize: '40px',
          color: '#8be04a',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(OVERLAY_DEPTH + 2),
    );
    objs.push(
      this.add
        .image(W / 2, H / 2 - 45, `tower-${SHREK_CHALLENGE.reward}-1`)
        .setDisplaySize(96, 96)
        .setDepth(OVERLAY_DEPTH + 2),
    );
    objs.push(
      this.add
        .text(W / 2, H / 2 + 30, `${TOWER_NAMES[SHREK_CHALLENGE.reward]} unlocked!`, {
          fontSize: '30px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(OVERLAY_DEPTH + 2),
    );
    objs.push(
      this.add
        // The Equip screen is reached from the Shop, not the episode picker —
        // naming the wrong screen sends the player hunting.
        .text(
          W / 2,
          H / 2 + 70,
          'He is yours for good — field him via EQUIP LOADOUT in the Shop.',
          {
            fontSize: '17px',
            color: '#9fd8ff',
          },
        )
        .setOrigin(0.5)
        .setDepth(OVERLAY_DEPTH + 2),
    );

    const btnY = H / 2 + 130;
    const btnBg = this.add
      .rectangle(W / 2, btnY, 220, 56, 0x1a6b1a)
      .setStrokeStyle(2, 0x4caf50)
      .setInteractive({ useHandCursor: true })
      .setDepth(OVERLAY_DEPTH + 2)
      .setName('challenge-continue');
    objs.push(btnBg);
    objs.push(
      this.add
        .text(W / 2, btnY, 'CONTINUE', { fontSize: '22px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(OVERLAY_DEPTH + 3),
    );
    btnBg.on('pointerup', () => {
      objs.forEach((o) => o.destroy());
      this.paused = wasPaused;
    });
  }

  /**
   * The Test Arena's loss overlay. A sandbox loss is a data point, not a
   * defeat: no stars, no score, no persistence — just "Too Risky!", the
   * attempt's scoreboard (enemies defeated / enemies sent), and a TRY AGAIN
   * that clears the pitch but KEEPS the towers (the clearArena command), so
   * the player can tweak the build instead of rebuilding from scratch.
   * In demo/review mode both buttons return to the demo gallery instead.
   */
  private showArenaLossOverlay(): void {
    const W = 1280;
    const H = 720;
    const OVERLAY_DEPTH = 50;
    const isDemo = this.demoOutcome !== null;
    // Collected for teardown: TRY AGAIN destroys the overlay and resumes the
    // very same scene, unlike the campaign overlay which always restarts it.
    const objs: Phaser.GameObjects.GameObject[] = [];

    objs.push(this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.65).setDepth(OVERLAY_DEPTH));
    objs.push(
      this.add
        .text(W / 2, H / 2 - 110, 'Too Risky!', {
          fontSize: '56px',
          color: '#ff4444',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(OVERLAY_DEPTH + 1),
    );

    const { arenaKills, arenaSpawnTotal } = this.sim.state;
    objs.push(
      this.add
        .text(W / 2, H / 2 - 30, `Defeated: ${arenaKills}/${arenaSpawnTotal}`, {
          fontSize: '34px',
          color: '#ffffff',
        })
        .setOrigin(0.5)
        .setDepth(OVERLAY_DEPTH + 1),
    );
    objs.push(
      this.add
        .text(W / 2, H / 2 + 16, 'Your towers are still standing — adjust and go again.', {
          fontSize: '17px',
          color: '#9fd8ff',
        })
        .setOrigin(0.5)
        .setDepth(OVERLAY_DEPTH + 1),
    );

    const btnY = H / 2 + 90;
    const retryBg = this.add
      .rectangle(W / 2 - 130, btnY, 210, 56, 0x1a6b1a)
      .setStrokeStyle(2, 0x4caf50)
      .setInteractive({ useHandCursor: true })
      .setDepth(OVERLAY_DEPTH + 1);
    objs.push(retryBg);
    objs.push(
      this.add
        .text(W / 2 - 130, btnY, 'TRY AGAIN', {
          fontSize: '22px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(OVERLAY_DEPTH + 2),
    );
    retryBg.on('pointerup', () => {
      if (isDemo) {
        this.scene.start('DemoScene');
        return;
      }
      // clearArena wipes enemies/effects and restores lives but keeps towers;
      // the run resumes in place, so the overlay must clean itself up.
      this.sim.dispatch({ type: 'clearArena' });
      objs.forEach((o) => o.destroy());
      this.gameOverShown = false;
      this.scene.launch('HudScene', { levelId: this.level.id, arena: true });
    });

    const menuBg = this.add
      .rectangle(W / 2 + 130, btnY, 210, 56, 0x2a2a7a)
      .setStrokeStyle(2, 0x4466cc)
      .setInteractive({ useHandCursor: true })
      .setDepth(OVERLAY_DEPTH + 1);
    objs.push(menuBg);
    objs.push(
      this.add
        .text(W / 2 + 130, btnY, isDemo ? 'MENU' : 'EPISODES', {
          fontSize: '22px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(OVERLAY_DEPTH + 2),
    );
    menuBg.on('pointerup', () => {
      if (isDemo) {
        this.scene.start('DemoScene');
      } else {
        const picker = levelPickerFor(this.level);
        this.scene.start(picker.key, picker.data);
      }
    });
  }
}
